/** Agentic tab — paper trading agent (isolated from main ledger). */

import {
  createPaperAgent,
  createPromptTemplate,
  deletePaperAgent,
  deletePromptTemplate,
  fetchOutgoingPaperAgentShares,
  fetchPaperAdviseLog,
  fetchPaperAgentDefaults,
  fetchPaperAgentState,
  fetchPromptTemplates,
  fetchSharedPaperAgentState,
  fetchSharedPaperAgents,
  grantPaperAgentView,
  paperAgentObserverLink,
  PAPER_AGENT_COLOR_OPTIONS,
  PAPER_CLAUDE_MODEL_OPTIONS,
  renamePaperAgent,
  recyclePaperAgent,
  resetPromptTemplate,
  revokePaperAgentView,
  runAgentAdvice,
  runPaperAgentNow,
  savePaperAgentSettings,
  setActivePaperAgent,
  updateAdviceStatus,
  updatePromptTemplate,
  type PaperAgentColor,
  type PaperAgentIncomingShare,
  type PaperAgentMeta,
  type PaperAgentSettings,
  type PaperAgentSnapshot,
  type PaperAgentStateResponse,
  type PaperClaudeModel,
  type PaperFill,
  type PaperPosition,
  type PaperRunLogEntry,
  type PaperWebQuery,
  type PaperShareHolding,
  type PromptTemplate,
  type AdvisorRecommendation,
} from "../api/agentsPaperClient";
import { formatAgentJustificationHtml } from "../shared/formatDataTip";
import { openAdviceDetailModal } from "./agentAdviceOverlay";
import { capturePanelView, isUserActiveInPanel, restorePanelView, withPreservedPanelView } from "./preservePanelView";
import {
  guardTickerFilterInputs,
  sanitizeTickerFilterValue,
  TICKER_FILTER_INPUT_ATTRS,
} from "../shared/tickerFilterInput";
import { notifyAgentsStatusChanged } from "./agentsStatus";
import { fetchAuthMe } from "./sessionAuth";
import {
  confirmDialog,
  contentDialog,
  instructionsDialog,
  promptDialog,
  textAreaDialog,
} from "./confirmDialog";
import {
  bindDetailsCollapsiblePersistence,
  collapsibleOpenAttr,
  isCollapsibleOpen,
  setCollapsibleOpen,
} from "./collapsibleSections";
import {
  BUILTIN_PROMPT_TEMPLATE_ID,
  getDefaultBootstrapPromptBody,
  getDefaultSlotPromptTemplateBody,
  getPlaceholderLegend,
} from "../shared/cursorAgentPromptTemplate";
import { paperAgentStatsHtml } from "./paperAgentStats";
import {
  accountShortCellHtml,
  accountShortInnerHtml,
  brokerMarkCellHtml,
  brokerMarkInnerHtml,
  ensureLedgerMarkIndex,
  markForAccountId,
  recommendationLedgerMark,
  subscribeLedgerMarks,
  type LedgerDisplayMark,
} from "./ledgerMarks";
import { withPreservedInputFocus } from "./preserveInputFocus";
import {
  displayTableColumns,
  displayTableColumnsForData,
  getTableColumnDefaults,
  openMultiColumnSettingsModal,
  renderTableColgroup,
  renderTableHeadRow,
  renderTablePinnedAttr,
  type ColumnState,
} from "./tableColumns";
import { renderScreenerExpiryInnerHtml, screenerExpiryDateLabel } from "./screenerExpiryCell";
import {
  compactSubline,
  effectiveWebTableId,
  isWebCompactMode,
  resolveWebTableColumns,
  subscribeWebCompactMode,
} from "./webCompactMode";
import { loadSavedTickers, normalizeTicker, plClass } from "./watchlistData";
import { renderRowFilterMenuItem } from "./rowFilterMenuItem";

const AGENTS_COLLAPSIBLE_SCOPE = "agents";
let agentsAdviceActionsEnabled = false;
let agentsAdviceAgentId = "";
/** Call-assignment P/L by fill id for the current transaction table render. */
let paperTxnAssignIncome = new Map<string, number>();

function isAdviceFill(fill: PaperFill): boolean {
  return String(fill.reason || "").toLowerCase() === "advice";
}

function fillToAdviceRec(fill: PaperFill): AdvisorRecommendation {
  return {
    id: fill.id,
    ts: fill.ts,
    action: fill.action,
    reason: fill.reason,
    justification: fill.justification,
    underlying: fill.underlying,
    side: fill.side,
    strike: fill.strike,
    expirationDate: fill.expirationDate,
    optionSymbol: fill.optionSymbol,
    quantity: fill.quantity,
    premium: fill.premium,
    fee: fill.fee,
    pnl: fill.pnl,
    agentId: agentsAdviceAgentId,
    userStatus: fill.userStatus,
    userStatusAt: fill.userStatusAt,
    userNote: fill.userNote,
  };
}
const AGENTS_OPTIONS_TABLE_ID = "agents-paper-options" as const;
const AGENTS_TXN_TABLE_ID = "agents-paper-transactions" as const;
const AGENTS_OPTIONS_TICKER_FILTER_PREFIX = "sfe-agents-options-ticker-filter-v1";
const AGENTS_TXN_TICKER_FILTER_PREFIX = "sfe-agents-txn-ticker-filter-v1";

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escAttr(value: string): string {
  return esc(value);
}

function agentsTickerFilterStorageKey(
  agentId: string | null,
  scope: "options" | "transactions",
): string {
  const prefix =
    scope === "options" ? AGENTS_OPTIONS_TICKER_FILTER_PREFIX : AGENTS_TXN_TICKER_FILTER_PREFIX;
  return `${prefix}:${agentId || "default"}`;
}

function loadAgentsTickerFilter(agentId: string | null, scope: "options" | "transactions"): string {
  try {
    const raw = localStorage.getItem(agentsTickerFilterStorageKey(agentId, scope));
    return sanitizeTickerFilterValue(raw ?? "").toUpperCase();
  } catch {
    return "";
  }
}

function saveAgentsTickerFilter(
  agentId: string | null,
  scope: "options" | "transactions",
  ticker: string,
): void {
  localStorage.setItem(
    agentsTickerFilterStorageKey(agentId, scope),
    sanitizeTickerFilterValue(ticker).toUpperCase(),
  );
}

function matchesAgentsTickerFilter(underlying: string, query: string): boolean {
  const q = query.trim().toUpperCase();
  if (!q) return true;
  return normalizeTicker(underlying).toUpperCase().startsWith(q);
}

function agentsTickerFilterBarHtml(
  ticker: string,
  inputId: string,
  resetAction: string,
): string {
  return `
    <div class="filter-bar recs-filter-bar sfe-agents-filter-bar sfe-agents-ticker-filter">
      <div class="filter-field">
        <label for="${escAttr(inputId)}">Ticker</label>
        <input type="text" id="${escAttr(inputId)}" class="filter-input-compact" placeholder="All"${TICKER_FILTER_INPUT_ATTRS} value="${escAttr(ticker)}" />
      </div>
      <div class="filter-field">
        <div class="sfe-filter-reset-actions">
          <button type="button" class="small sfe-reset-filters" data-paper-action="${escAttr(resetAction)}">Reset filters</button>
        </div>
      </div>
    </div>`;
}

type AgentsTableScope = "options" | "transactions";

const AGENTS_ROW_MENU_ID = "sfe-agents-row-menu-portal";
let agentsRowMenuSuppressCloseUntil = 0;

function agentsTableScopeInputId(scope: AgentsTableScope): string {
  return scope === "options" ? "sfeAgentsOptionsTicker" : "sfeAgentsTxnTicker";
}

function agentsTableScopeResetAction(scope: AgentsTableScope): string {
  return scope === "options" ? "reset-options-ticker-filter" : "reset-txn-ticker-filter";
}

function showAgentsTickerFilterPanel(ticker: string, panelOpen: boolean): boolean {
  return panelOpen || Boolean(ticker.trim());
}

function renderAgentsRowMenuHtml(scope: AgentsTableScope, ticker: string): string {
  const normalized = normalizeTicker(ticker).toUpperCase();
  if (!normalized) return "";
  return `
    <div
      class="sfe-agents-row-menu"
      data-agents-table-scope="${escAttr(scope)}"
      data-agents-ticker="${escAttr(normalized)}">
      <button
        type="button"
        class="sfe-agents-row-menu-btn"
        data-paper-action="agents-row-menu"
        aria-label="Actions for ${escAttr(normalized)}"
        aria-haspopup="menu"
        aria-expanded="false"
        title="Actions">&#8942;</button>
    </div>`;
}

function agentsTickerFilterPanelHtml(
  scope: AgentsTableScope,
  ticker: string,
  visible: boolean,
): string {
  if (!visible) return "";
  return `
    <div class="sfe-agents-ticker-filter-panel" data-agents-filter-panel="${escAttr(scope)}">
      ${agentsTickerFilterBarHtml(ticker, agentsTableScopeInputId(scope), agentsTableScopeResetAction(scope))}
    </div>`;
}

function closeAgentsRowMenu(): void {
  document.getElementById(AGENTS_ROW_MENU_ID)?.remove();
  document
    .querySelectorAll<HTMLElement>('[data-paper-action="agents-row-menu"][aria-expanded="true"]')
    .forEach((btn) => btn.setAttribute("aria-expanded", "false"));
}

function positionAgentsRowMenu(menu: HTMLElement, anchor: HTMLElement): void {
  menu.style.visibility = "hidden";
  menu.style.left = "0";
  menu.style.top = "0";
  document.body.appendChild(menu);
  const pad = 8;
  const viewW = document.documentElement.clientWidth || window.innerWidth;
  const viewH = document.documentElement.clientHeight || window.innerHeight;
  menu.style.maxWidth = `${Math.max(120, viewW - pad * 2)}px`;
  const width = menu.offsetWidth;
  const height = menu.offsetHeight;
  const rect = anchor.getBoundingClientRect();
  let left = Math.round(rect.right - width);
  let top = Math.round(rect.bottom + 4);
  left = Math.min(Math.max(left, pad), Math.max(pad, viewW - width - pad));
  if (top + height > viewH - pad && rect.top > height + pad) {
    top = Math.round(rect.top - height - 4);
  }
  top = Math.min(Math.max(top, pad), Math.max(pad, viewH - height - pad));
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;
  menu.style.visibility = "";
}

function openAgentsRowMenu(
  anchor: HTMLElement,
  scope: AgentsTableScope,
  ticker: string,
  activeFilter: string,
): void {
  closeAgentsRowMenu();
  const normalized = sanitizeTickerFilterValue(ticker).toUpperCase();
  const active =
    Boolean(activeFilter.trim()) &&
    sanitizeTickerFilterValue(activeFilter).toUpperCase() === normalized;
  const menu = document.createElement("div");
  menu.id = AGENTS_ROW_MENU_ID;
  menu.className = "sfe-focus-menu-pop sfe-agents-row-menu-pop";
  menu.setAttribute("role", "menu");
  menu.setAttribute("data-sfe-injected", "true");
  menu.innerHTML = renderRowFilterMenuItem({
    active,
    actionAttr: "data-paper-action",
    actionValue: "agents-row-filter",
    attrs: {
      "data-agents-table-scope": scope,
      "data-agents-ticker": normalized,
    },
  });
  agentsRowMenuSuppressCloseUntil = Date.now() + 250;
  anchor.setAttribute("aria-expanded", "true");
  positionAgentsRowMenu(menu, anchor);
}

function fmtUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  return `${sign}$${Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  })}`;
}

/** Strike / non-currency fin amounts with thousand separators. */
function fmtFin(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  });
}

/** Form display for money fields (commas, no $). */
function fmtMoneyInput(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return "";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  });
}

function parseMoneyInput(raw: string, fallback = 0): number {
  const cleaned = String(raw || "")
    .replace(/[$,\s]/g, "")
    .trim();
  if (!cleaned) return fallback;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : fallback;
}

function fmtPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  })}%`;
}

function fmtInt(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return Math.round(value).toLocaleString("en-US", { useGrouping: true });
}

const PACIFIC_TZ = "America/Los_Angeles";

function fmtTs(value: string | null | undefined): string {
  if (!value) return "—";
  const d = Date.parse(value);
  if (!Number.isFinite(d)) return value;
  return new Date(d).toLocaleString("en-US", {
    timeZone: PACIFIC_TZ,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
}

/** Preset run times in Pacific (market session ~06:30–13:00 PT). */
const SLOT_PRESETS = ["06:45", "07:00", "08:30", "10:00", "11:30", "12:30"] as const;

function numInput(host: HTMLElement, sel: string, fallback = 0): number {
  const el = host.querySelector<HTMLInputElement>(sel);
  return parseMoneyInput(el?.value ?? "", fallback);
}

function activeChipValues(host: HTMLElement, group: string): string[] {
  return [...host.querySelectorAll<HTMLElement>(`[data-paper-chip-group="${group}"] .year-chip.active`)]
    .map((btn) => btn.dataset.paperChip ?? "")
    .filter(Boolean);
}

function normalizeTimeInput(raw: string): string | null {
  const m = String(raw || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function readAgentColorFromForm(host: HTMLElement, fallback: string): string {
  const active = host.querySelector<HTMLElement>(
    "[data-paper-color-group] .sfe-agents-color-swatch.active",
  );
  const value = active?.dataset.paperColor || fallback || "slate";
  return PAPER_AGENT_COLOR_OPTIONS.some((c) => c.id === value) ? value : "slate";
}

function agentAccentStyle(colorHex: string | null | undefined): string {
  const hex = colorHex && /^#[0-9a-fA-F]{6}$/.test(colorHex) ? colorHex : "#64748b";
  return `--sfe-agent-accent:${hex}`;
}

function isAdvisorAgent(value: { kind?: string } | null | undefined): boolean {
  return (value?.kind || "paper") === "advisor";
}

function colorSwatchesHtml(selected: string): string {
  const current = PAPER_AGENT_COLOR_OPTIONS.some((c) => c.id === selected) ? selected : "slate";
  return `
    <div class="sfe-agents-color-swatches" data-paper-color-group role="radiogroup" aria-label="Agent color">
      ${PAPER_AGENT_COLOR_OPTIONS.map(
        (c) => `
        <button
          type="button"
          class="sfe-agents-color-swatch${c.id === current ? " active" : ""}"
          data-paper-color="${esc(c.id)}"
          data-paper-action="pick-color"
          style="--sfe-swatch:${esc(c.hex)}"
          title="${esc(c.label)}"
          aria-label="${esc(c.label)}"
          aria-pressed="${c.id === current ? "true" : "false"}"
        ></button>`,
      ).join("")}
    </div>`;
}

function readSettingsFromForm(
  host: HTMLElement,
  prev: PaperAgentSettings,
): PaperAgentSettings & { name?: string; color?: string } {
  const scheduleChip = activeChipValues(host, "schedule-mode")[0];
  const scheduleMode: PaperAgentSettings["scheduleMode"] =
    scheduleChip === "interval" || scheduleChip === "manual" || scheduleChip === "slots"
      ? scheduleChip
      : prev.scheduleMode === "interval" || prev.scheduleMode === "manual"
        ? prev.scheduleMode
        : "slots";

  const slotChips = activeChipValues(host, "slots");
  const slots = [...new Set(slotChips.map((s) => normalizeTimeInput(s)).filter(Boolean))] as string[];

  const intervalMinutes = Math.round(
    numInput(host, "[data-paper-interval-minutes]", prev.intervalMinutes ?? 30),
  );
  const intervalStart =
    normalizeTimeInput(
      host.querySelector<HTMLInputElement>("[data-paper-interval-start]")?.value ?? "",
    ) ??
    prev.intervalStart ??
    "06:45";

  const sourceChips = activeChipValues(host, "sources");
  const sources: Array<"puts" | "calls"> = [];
  if (sourceChips.includes("puts")) sources.push("puts");
  if (sourceChips.includes("calls")) sources.push("calls");

  const rankRaw = activeChipValues(host, "rank")[0] ?? prev.rankBy;
  const rankBy =
    rankRaw === "profit_day"
      ? "profit_day"
      : rankRaw === "both" || rankRaw === "either"
        ? "both"
        : "score";

  const enabled = activeChipValues(host, "enabled").includes("on");
  const preferFocusTickers = activeChipValues(host, "focus").includes("prefer");
  const tradableOnly = activeChipValues(host, "tradable").includes("on");
  const allowRiskyCalls = activeChipValues(host, "risky").includes("on");
  const closeAtExpiry = activeChipValues(host, "expiry").includes("close");

  return {
    enabled,
    initialCapital: host.querySelector("[data-paper-initial-capital]")
      ? numInput(host, "[data-paper-initial-capital]", prev.initialCapital)
      : prev.initialCapital,
    expectedMonthlyRocPct: numInput(host, "[data-paper-expected-roc]", prev.expectedMonthlyRocPct),
    tryOverperform: activeChipValues(host, "overperform").includes("on"),
    perTradeFee: host.querySelector("[data-paper-fee]")
      ? numInput(host, "[data-paper-fee]", prev.perTradeFee)
      : prev.perTradeFee,
    scheduleMode,
    slots: slots.length ? slots : [...(prev.slots.length ? prev.slots : SLOT_PRESETS)],
    intervalMinutes: Number.isFinite(intervalMinutes) ? intervalMinutes : 30,
    intervalStart,
    strategy: activeChipValues(host, "strategy")[0] === "wheel" ? "wheel" : "discretion",
    sources: sources.length ? sources : ["puts", "calls"],
    rankBy,
    topNPerSlotDiscretion: activeChipValues(host, "top-n-discretion").includes("on"),
    topNPerSlot: Math.max(
      1,
      Math.round(numInput(host, "[data-paper-top-n]", prev.topNPerSlot || 1)),
    ),
    maxOpenPositions: Math.round(numInput(host, "[data-paper-max-open]", prev.maxOpenPositions)),
    maxContractsPerTrade: Math.round(
      numInput(host, "[data-paper-max-contracts]", prev.maxContractsPerTrade),
    ),
    minPop: numInput(host, "[data-paper-min-pop]", prev.minPop),
    minAnnRoc: numInput(host, "[data-paper-min-ann-roc]", prev.minAnnRoc),
    maxOpenDtePuts: Math.max(
      0,
      Math.round(numInput(host, "[data-paper-max-open-dte-puts]", prev.maxOpenDtePuts ?? 45)),
    ),
    maxOpenDteCalls: Math.max(
      0,
      Math.round(numInput(host, "[data-paper-max-open-dte-calls]", prev.maxOpenDteCalls ?? 45)),
    ),
    tradableOnly,
    allowRiskyCalls,
    riskyCallsMinPop: Math.max(
      0,
      Math.min(
        100,
        numInput(host, "[data-paper-risky-min-pop]", prev.riskyCallsMinPop ?? 70),
      ),
    ),
    preferFocusTickers,
    focusTickers: loadSavedTickers(),
    takeProfitDiscretion: activeChipValues(host, "tp-discretion").includes("on"),
    takeProfitPctOfCredit: Math.max(
      0,
      Math.min(100, numInput(host, "[data-paper-tp]", prev.takeProfitPctOfCredit)),
    ),
    stopLossMode: (() => {
      const mode = activeChipValues(host, "stop-mode")[0] ?? prev.stopLossMode ?? "stop";
      if (mode === "keep") return "keep";
      if (mode === "either") return "either";
      return "stop";
    })(),
    stopLossPctOfCredit: numInput(host, "[data-paper-sl]", prev.stopLossPctOfCredit),
    maxHoldDte: Math.round(numInput(host, "[data-paper-max-hold-dte]", prev.maxHoldDte)),
    closeAtExpiry,
    claudeModel: (() => {
      const raw = (activeChipValues(host, "claude-model")[0] ||
        prev.claudeModel ||
        "auto") as string;
      const allowed = new Set(PAPER_CLAUDE_MODEL_OPTIONS.map((o) => o.id));
      return allowed.has(raw as PaperClaudeModel) ? (raw as PaperClaudeModel) : "auto";
    })(),
    keepCursorAgentBetweenSessions: (() => {
      const chip = activeChipValues(host, "keep-cursor-agent")[0];
      if (chip === "on") return true;
      if (chip === "off") return false;
      return prev.keepCursorAgentBetweenSessions === true;
    })(),
    allowWeb: (() => {
      const chip = activeChipValues(host, "allow-web")[0];
      if (chip === "on") return true;
      if (chip === "off") return false;
      return prev.allowWeb === true;
    })(),
    autoRecycleEveryRuns: Math.max(
      0,
      Math.min(500, Math.round(numInput(host, "[data-paper-auto-recycle-runs]", prev.autoRecycleEveryRuns ?? 30))),
    ),
    autoRecycleMinRunTokens: Math.max(
      0,
      Math.min(
        50_000_000,
        Math.round(numInput(host, "[data-paper-auto-recycle-tokens]", prev.autoRecycleMinRunTokens ?? 1_500_000)),
      ),
    ),
    instructions: typeof prev.instructions === "string" ? prev.instructions : "",
    promptTemplateId:
      typeof prev.promptTemplateId === "string" && prev.promptTemplateId.trim()
        ? prev.promptTemplateId.trim()
        : BUILTIN_PROMPT_TEMPLATE_ID,
    color: readAgentColorFromForm(host, "slate"),
  };
}

function chipBtn(group: string, value: string, label: string, active: boolean, readOnly = false): string {
  return `<button type="button" class="year-chip${active ? " active" : ""}" data-paper-chip="${esc(value)}" aria-pressed="${active ? "true" : "false"}"${readOnly ? " disabled" : ""}>${esc(label)}</button>`;
}

function tip(text: string): string {
  return `<span class="hint" tabindex="0" role="img" data-tip="${esc(text)}">i</span>`;
}

function slotChipBtn(slot: string, active: boolean, readOnly = false): string {
  if (readOnly) {
    if (!active) return "";
    return `<span class="year-chip active sfe-agents-slot-chip sfe-agents-slot-chip-readonly">${esc(slot)}</span>`;
  }
  return `<button type="button" class="year-chip sfe-agents-slot-chip${active ? " active" : ""}" data-paper-chip="${esc(slot)}" aria-pressed="${active ? "true" : "false"}"><span class="sfe-agents-slot-label">${esc(slot)}</span><span class="sfe-agents-slot-remove" data-paper-action="remove-slot" data-slot="${esc(slot)}" role="button" tabindex="0" aria-label="Remove ${esc(slot)}" title="Remove">×</span></button>`;
}

function tokenCardsHtml(
  state: Pick<
    PaperAgentStateResponse,
    "marketdata" | "claude" | "cursor" | "tokenTotals"
  >,
): string {
  const md = state.marketdata;
  const cursor = state.cursor || state.claude;
  const totals = state.tokenTotals;
  const mdUsed = totals?.mdUsedToday ?? md?.creditsUsedToday;
  const mdLabel =
    md?.configured === false
      ? "not configured"
      : mdUsed != null && md?.dailyBudget != null && Number.isFinite(mdUsed) && Number.isFinite(md.dailyBudget)
        ? `${fmtInt(mdUsed)} / ${fmtInt(md.dailyBudget)}`
        : md?.configured
          ? "0"
          : "—";
  const cursorUsed = totals?.cursorUsedThisMonth ?? cursor?.tokensUsedThisMonth ?? cursor?.tokensUsedToday;
  const cursorLabel =
    cursor?.configured === false
      ? "not configured"
      : cursorUsed != null && Number.isFinite(cursorUsed)
        ? fmtInt(cursorUsed)
        : cursor?.configured
          ? "0"
          : "—";
  return `
    <div class="sfe-agents-token-cards" aria-label="Token usage">
      <div class="sfe-agents-token-card" title="MarketData tokens used today by all paper agents vs the shared daily budget.">
        <span class="muted small">MarketData tokens${tip("Total MarketData tokens used today across all paper agents. Resets at midnight UTC.")}</span>
        <strong>${esc(mdLabel)}</strong>
        <span class="muted small sfe-agents-token-renews">daily limit</span>
      </div>
      <div class="sfe-agents-token-card" title="Cursor tokens used this month by all paper agents.">
        <span class="muted small">Cursor tokens${tip("Total Cursor tokens used this month across all paper agents (Pacific calendar). Resets on the 1st.")}</span>
        <strong>${esc(cursorLabel)}</strong>
        <span class="muted small sfe-agents-token-renews">Monthly refresh</span>
      </div>
    </div>`;
}

function agentTabsHtml(
  agents: PaperAgentMeta[],
  activeId: string | null,
  agentName: string,
  enabled: boolean,
  agentColor: string,
  hasInstructions = false,
  accentHex?: string,
  nextSlot: { slot: string; label: string } | null = null,
): string {
  const tabs = (
    agents.length
      ? agents
      : [
          {
            id: "default",
            name: agentName || "Default",
            color: "slate",
            colorHex: "#64748b",
            enabled: false,
            hasSnapshot: false,
            equity: 0,
            openPositions: 0,
          },
        ]
  )
    .map((a) => {
      const active = a.id === activeId;
      const hex = a.colorHex || "#64748b";
      const advisor = isAdvisorAgent(a);
      return `
        <button
          type="button"
          class="sfe-agents-tab${advisor ? " sfe-agents-tab--advisor" : ""}${active ? " active" : ""}${a.enabled ? " is-on" : ""}"
          data-paper-action="select-agent"
          data-agent-id="${esc(a.id)}"
          style="${agentAccentStyle(hex)}"
          title="${esc(a.name)}${advisor ? " · advisor" : ""}${a.enabled ? " · on" : " · off"}"
        >
          <span class="sfe-agents-tab-dot" aria-hidden="true"></span>
          <span class="sfe-agents-tab-name">${esc(a.name)}</span>
          ${advisor ? `<span class="sfe-agents-tab-badge">Advisor</span>` : ""}
          ${a.runInFlight ? `<span class="sfe-agents-tab-count" title="Run in progress">…</span>` : a.openPositions ? `<span class="sfe-agents-tab-count">${a.openPositions}</span>` : ""}
        </button>`;
    })
    .join("");
  const selectedHex =
    accentHex ||
    agents.find((a) => a.id === activeId)?.colorHex ||
    PAPER_AGENT_COLOR_OPTIONS.find((c) => c.id === agentColor)?.hex ||
    "#64748b";
  const selectedAdvisor = isAdvisorAgent(agents.find((a) => a.id === activeId));
  return `
    <div class="sfe-agents-tabs-block sfe-agents-tinted" style="${agentAccentStyle(selectedHex)}">
      <div class="sfe-agents-tabs-row">
        <div class="sfe-agents-tabs" role="tablist" aria-label="Paper agents">${tabs}</div>
        <div class="sfe-agents-tabs-end">
          <div class="sfe-agents-header-toggle" title="${selectedAdvisor ? "Per-advisor switch. Off stops advice runs and removes the Cursor cloud agent until you turn it back on." : "Per-agent switch. Off skips scheduled opens and manage runs."}">
            <div class="year-chips" data-paper-chip-group="enabled" data-paper-chip-mode="single">
              ${chipBtn("enabled", "on", "On", enabled)}
              ${chipBtn("enabled", "off", "Off", !enabled)}
            </div>
          </div>
          <div class="sfe-agents-header-color" title="Accent color for this agent’s tab and related sections.">
            ${colorSwatchesHtml(agentColor)}
          </div>
        </div>
      </div>
      <div class="sfe-agents-selected-actions" aria-label="Selected agent actions">
        <div class="sfe-agents-selected-actions-main">
          <button type="button" class="small ghost" data-paper-action="rename-agent" title="Rename the selected agent">Rename</button>
          <button
            type="button"
            class="small ghost${hasInstructions ? " sfe-agents-instructions-set" : ""}"
            data-paper-action="edit-instructions"
            title="Edit cloud prompt templates and additional instructions"
          >Instructions</button>
          <button
            type="button"
            class="small ghost"
            data-paper-action="view-advise-log"
            title="View Cursor cloud advise interactions for this agent (prompts, raw results, parsed decisions, tokens)"
          >Log</button>
          <span class="sfe-agents-selected-actions-ops" aria-label="Run controls">
            <span class="sfe-agents-next-run sfe-agents-next-run--bar">
              <span class="muted small">Next run</span>
              <strong class="sfe-agents-next-run-val">${esc(nextSlot?.label ?? "—")}</strong>
            </span>
            <button type="button" class="small sfe-agents-recycle-btn" data-paper-action="recycle-agent" title="${selectedAdvisor ? "Restart the Cursor cloud agent and sync it to your live portfolio — recommendations and history unchanged. Advisor must be On." : "Restart the Cursor cloud agent and sync it to the current paper book — ledger and performance unchanged"}"${selectedAdvisor && !enabled ? " disabled" : ""}>Recycle</button>
            <button
              type="button"
              class="small sfe-agents-run-btn"
              data-paper-action="run-now"
              title="${
                selectedAdvisor
                  ? "Run this advisor now against your live portfolio. Recommendations are not executed. Advisor must be On."
                  : "Run this agent now (Cursor advise + manage). Agent must be On. Bypasses the schedule; also runs when the market is closed."
              }"
              ${enabled ? "" : " disabled"}
            >${selectedAdvisor ? "Run advice" : "Run"}</button>
          </span>
        </div>
        <div class="sfe-agents-selected-actions-share">
          <button type="button" class="small ghost" data-paper-action="share-agent" title="Grant view-only access to another StockForge+ user">Share</button>
          <button type="button" class="small ghost" data-paper-action="copy-observer-link" title="Copy a link for signed-in observers">Copy link</button>
          <button type="button" class="small ghost" data-paper-action="revoke-share" title="Revoke view-only access by observer email, or ALL for every observer">Revoke</button>
        </div>
      </div>
    </div>`;
}

function sharedWithMeHtml(grants: PaperAgentIncomingShare[], activeOwnerId: string | null, activeAgentId: string | null): string {
  if (!grants.length) return "";
  const chips = grants
    .map((g) => {
      const active = g.ownerUserId === activeOwnerId && g.agentId === activeAgentId;
      const ownerLabel = g.ownerEmail || g.ownerUserId;
      return `
        <button
          type="button"
          class="sfe-agents-shared-chip year-chip${active ? " active" : ""}"
          data-paper-action="open-shared-agent"
          data-owner-id="${esc(g.ownerUserId)}"
          data-agent-id="${esc(g.agentId)}"
          style="${agentAccentStyle(g.agentColorHex)}"
          title="View-only · shared by ${esc(ownerLabel)}"
          aria-pressed="${active ? "true" : "false"}"
        >
          <span class="sfe-agents-tab-dot" aria-hidden="true"></span>
          <span>${esc(g.agentName)}</span>
          <span class="muted small sfe-agents-shared-by">· ${esc(ownerLabel)}</span>
        </button>`;
    })
    .join("");
  return `
    <div class="sfe-agents-shared-block">
      <span class="muted small sfe-agents-shared-label">Shared with me</span>
      <div class="sfe-agents-shared-chips" role="list">${chips}</div>
    </div>`;
}

function viewerBannerHtml(state: PaperAgentStateResponse, standalone = false): string {
  const ownerLabel = state.sharedBy?.email || state.sharedBy?.userId || "owner";
  const backControl = standalone
    ? ""
    : `<button type="button" class="small ghost" data-paper-action="exit-viewer">My agents</button>`;
  return `
    <div class="sfe-agents-viewer-banner" role="status">
      <div class="sfe-agents-viewer-banner-main">
        <strong>View only</strong>
        <span class="muted small">Observing <strong>${esc(state.name)}</strong> · shared by ${esc(ownerLabel)}. You cannot change settings or run trades.</span>
      </div>
      ${backControl}
    </div>`;
}

function settingsFormHtml(s: PaperAgentSettings, readOnly = false, advisor = false): string {
  const sources = new Set(s.sources);
  const slotSet = new Set(s.slots);
  const customSlots = s.slots.filter((slot) => !(SLOT_PRESETS as readonly string[]).includes(slot));
  const allSlotChips = [...SLOT_PRESETS, ...customSlots];
  const rankValue = s.rankBy === "both" ? "either" : s.rankBy;
  const scheduleMode =
    s.scheduleMode === "interval"
      ? "interval"
      : s.scheduleMode === "manual"
        ? "manual"
        : "slots";
  const intervalMinutes = s.intervalMinutes ?? 30;
  const intervalStart = s.intervalStart || "06:45";
  const ro = readOnly ? " disabled" : "";

  return `
    <details class="collapsible sfe-agents-settings${readOnly ? " sfe-agents-settings-readonly" : ""}" data-sfe-collapsible="settings"${isCollapsibleOpen(AGENTS_COLLAPSIBLE_SCOPE, "settings", true) ? " open" : ""}>
      <summary class="sfe-agents-section-summary">Agent settings${readOnly ? " <span class=\"muted small\">(view only)</span>" : ""}</summary>
      <div class="sfe-agents-settings-body">
        <p class="muted small sfe-agents-settings-lead">
          ${
            readOnly
              ? "Read-only view of this agent's configuration. Schedule times are Pacific (PT)."
              : advisor
                ? "Advisor — reads your live portfolio and cash. Same gates as paper agents; recommendations are recorded, not executed. Live trading is not enabled yet. Use Manual unless you want a schedule."
                : "Paper only — never writes the Stock Forge ledger. Each tab is a separate agent with its own settings and book. Schedule times are Pacific (PT); runs only while the NYSE session is open."
          }
        </p>

        <div class="sfe-agents-settings-cards">
          <section class="sfe-agents-settings-card">
            <header class="sfe-agents-settings-card-head">
              <h3 class="sfe-agents-settings-card-title">Schedule</h3>
              <p class="muted small">${advisor ? "When this advisor may run (Pacific). Manual = Run advice only." : "When this agent may open new trades (Pacific)."}</p>
            </header>
            <div class="filter-bar recs-filter-bar sfe-agents-filter-bar">
              <div class="filter-field">
                <label>Sources${tip("Which screener lists the agent may open from.")}</label>
                <div class="year-chips" data-paper-chip-group="sources" data-paper-chip-mode="multi">
                  ${chipBtn("sources", "puts", "Puts", sources.has("puts"), readOnly)}
                  ${chipBtn("sources", "calls", "Calls", sources.has("calls"), readOnly)}
                </div>
              </div>
              <div class="filter-field">
                <label>Run mode${tip(advisor ? "Manual = only when you click Run advice. Timeslots = fixed HH:MM Pacific. Interval = every N minutes from a Pacific start time." : "Timeslots = fixed HH:MM Pacific each day. Interval = every N minutes from a Pacific start time.")}</label>
                <div class="year-chips" data-paper-chip-group="schedule-mode" data-paper-chip-mode="single" data-paper-action-local="schedule-mode">
                  ${advisor ? chipBtn("schedule-mode", "manual", "Manual", scheduleMode === "manual", readOnly) : ""}
                  ${chipBtn("schedule-mode", "slots", "Timeslots", scheduleMode === "slots", readOnly)}
                  ${chipBtn("schedule-mode", "interval", "Every N min", scheduleMode === "interval", readOnly)}
                </div>
              </div>
            </div>
            <div class="filter-bar recs-filter-bar sfe-agents-filter-bar" data-paper-schedule-panel="manual"${scheduleMode === "manual" ? "" : " hidden"}>
              <p class="muted small">No scheduled runs. Click <strong>Run advice</strong> here or on Puts / Calls / Options.</p>
            </div>
            <div class="filter-bar recs-filter-bar sfe-agents-filter-bar" data-paper-schedule-panel="slots"${scheduleMode === "slots" ? "" : " hidden"}>
              <div class="filter-field sfe-agents-slots-field">
                <label>Daily timeslots (PT)${tip("Pacific times. Session ≈ 06:30–13:00 PT. Toggle to include/exclude; × removes from the list.")}</label>
                <div class="year-chips" data-paper-chip-group="slots" data-paper-chip-mode="multi" data-paper-slots-host>
                  ${
                    readOnly
                      ? s.slots.map((slot) => slotChipBtn(slot, true, true)).join("")
                      : allSlotChips.map((slot) => slotChipBtn(slot, slotSet.has(slot))).join("")
                  }
                </div>
              </div>
              ${
                readOnly
                  ? ""
                  : `<div class="filter-field">
                <label for="sfe-paper-slot-add">Add time (PT)${tip("Add a custom Pacific open time to the list above.")}</label>
                <div class="sfe-agents-add-slot">
                  <input type="time" id="sfe-paper-slot-add" class="filter-input-compact" data-paper-slot-add value="10:00" />
                  <button type="button" class="small" data-paper-action="add-slot">Add</button>
                </div>
              </div>`
              }
            </div>
            <div class="filter-bar recs-filter-bar sfe-agents-filter-bar" data-paper-schedule-panel="interval"${scheduleMode === "interval" ? "" : " hidden"}>
              <div class="filter-field">
                <label>Every${tip("Open cycle every N minutes during the regular session, starting at the Pacific time below.")}</label>
                <div class="filter-affix">
                  <input type="number" min="5" max="240" step="5" class="filter-input-compact sfe-agents-num-sm" data-paper-interval-minutes value="${esc(String(intervalMinutes))}"${ro} />
                  <span class="filter-affix__suffix">min</span>
                </div>
              </div>
              <div class="filter-field">
                <label for="sfe-paper-interval-start">Starting (PT)${tip("First interval run of the day in Pacific time. Later runs follow every N minutes while the market is open.")}</label>
                <input type="time" id="sfe-paper-interval-start" class="filter-input-compact" data-paper-interval-start value="${esc(intervalStart)}"${ro} />
              </div>
              <div class="filter-field">
                <label>&nbsp;</label>
                <p class="muted small sfe-agents-interval-hint">e.g. every ${esc(String(intervalMinutes))} min from ${esc(intervalStart)} PT while market open</p>
              </div>
            </div>
          </section>

          <section class="sfe-agents-settings-card">
            <header class="sfe-agents-settings-card-head">
              <h3 class="sfe-agents-settings-card-title">${advisor ? "Target" : "Capital &amp; target"}</h3>
              <p class="muted small">${advisor ? "Performance goal for recommendations against the live book." : "Paper book size and performance goal."}</p>
            </header>
            <div class="filter-bar recs-filter-bar sfe-agents-filter-bar">
              ${
                advisor
                  ? ""
                  : `<div class="filter-field">
                <label>Capital${tip("Starting paper cash. Resetting the agent rebases cash to this amount when the book is empty.")}</label>
                <div class="filter-affix">
                  <span class="filter-affix__prefix">$</span>
                  <input type="text" inputmode="decimal" class="filter-input-compact has-prefix" data-paper-initial-capital value="${esc(fmtMoneyInput(s.initialCapital, 2))}" autocomplete="off"${ro} />
                </div>
              </div>`
              }
              <div class="filter-field">
                <label>Month ROC${tip(advisor ? "Target monthly return used as a benchmark and by Try outperform. Live cash comes from your portfolio, not a paper book." : "Target monthly return on capital for the paper book. Used as a benchmark and by Try outperform.")}</label>
                <div class="filter-affix">
                  <input type="number" min="0" step="0.1" class="filter-input-compact" data-paper-expected-roc value="${esc(String(s.expectedMonthlyRocPct))}"${ro} />
                  <span class="filter-affix__suffix">%</span>
                </div>
              </div>
              <div class="filter-field">
                <label>Target${tip("Try outperform biases picks and sizing toward beating the monthly ROC target.")}</label>
                <div class="year-chips" data-paper-chip-group="overperform" data-paper-chip-mode="toggle">
                  ${chipBtn("overperform", "on", "Try outperform", Boolean(s.tryOverperform), readOnly)}
                </div>
              </div>
              ${
                advisor
                  ? ""
                  : `<div class="filter-field">
                <label>Fee / trade${tip("Per-contract paper fee charged on each open and close fill.")}</label>
                <div class="filter-affix">
                  <span class="filter-affix__prefix">$</span>
                  <input type="text" inputmode="decimal" class="filter-input-compact has-prefix" data-paper-fee value="${esc(fmtMoneyInput(s.perTradeFee, 2))}" autocomplete="off"${ro} />
                </div>
              </div>`
              }
            </div>
          </section>

          <section class="sfe-agents-settings-card">
            <header class="sfe-agents-settings-card-head">
              <h3 class="sfe-agents-settings-card-title">Cursor</h3>
              <p class="muted small">Model and session behavior for Cursor cloud advise.</p>
            </header>
            <div class="filter-bar recs-filter-bar sfe-agents-filter-bar">
              <div class="filter-field">
                <label>Strategy${tip("Wheel: cash-secured puts when you have no owed/long paper shares; covered calls only on tickers with free owed shares from this agent's private paper ledger (not live SF+/broker holdings). Agent discretion: open from Sources without those phase gates.")}</label>
                <div class="year-chips" data-paper-chip-group="strategy" data-paper-chip-mode="single">
                  ${chipBtn("strategy", "wheel", "Wheel", (s.strategy || "discretion") === "wheel", readOnly)}
                  ${chipBtn("strategy", "discretion", "Agent discretion", (s.strategy || "discretion") !== "wheel", readOnly)}
                </div>
              </div>
              <div class="filter-field">
                <label>Cursor model${tip("Auto = slot-aware: Sonnet when opens/CCs/near-expiry possible, Haiku on quiet manage-only slots. Pin Haiku/Sonnet/Opus to force that model every slot. Hard rules still gate fills.")}</label>
                <div class="year-chips" data-paper-chip-group="claude-model" data-paper-chip-mode="single">
                  ${PAPER_CLAUDE_MODEL_OPTIONS.map((opt) =>
                    chipBtn(
                      "claude-model",
                      opt.id,
                      opt.label,
                      (s.claudeModel || "auto") === opt.id,
                      readOnly,
                    ),
                  ).join("")}
                </div>
              </div>
              <div class="filter-field">
                <label>Keep agent between sessions${tip("When on, persist the Cursor agent id and resume the same conversation on later slots (Agent.resume). When off (default), create a fresh agent each advise for predictable cost/context. Turning off clears the stored id.")}</label>
                <div class="year-chips" data-paper-chip-group="keep-cursor-agent" data-paper-chip-mode="single">
                  ${chipBtn("keep-cursor-agent", "on", "On", Boolean(s.keepCursorAgentBetweenSessions), readOnly)}
                  ${chipBtn("keep-cursor-agent", "off", "Off", !s.keepCursorAgentBetweenSessions, readOnly)}
                </div>
              </div>
              <div class="filter-field">
                <label>Allow Web${tip("When on, Cursor cloud advise may browse/search for earnings, news, or catalysts before deciding (uses VM network / computer-use). When off (default), JSON-only from packed SF+ book and candidates — usually fewer tokens. Same final JSON schema either way. Compare runs in Advise log / run log (allowWeb flag).")}</label>
                <div class="year-chips" data-paper-chip-group="allow-web" data-paper-chip-mode="single">
                  ${chipBtn("allow-web", "on", "On", Boolean(s.allowWeb), readOnly)}
                  ${chipBtn("allow-web", "off", "Off", !s.allowWeb, readOnly)}
                </div>
              </div>
              <div class="filter-field">
                <label>Auto-recycle every N runs${tip("0 = off. After this many scheduled or manual Cursor runs since the last recycle (manual or auto), bootstrap a fresh cloud agent synced to the current book. Recommended: 30.")}</label>
                <input type="number" min="0" max="500" step="1" class="filter-input-compact sfe-agents-num-sm" data-paper-auto-recycle-runs value="${esc(String(s.autoRecycleEveryRuns ?? 30))}" placeholder="30"${ro} />
              </div>
              <div class="filter-field">
                <label>Auto-recycle when run tokens ≥${tip("0 = off. Recycle after a run whose Cursor tokensTotal reaches this threshold, but only after ≥3 runs since last recycle. Recommended: 1500000.")}</label>
                <input type="number" min="0" max="50000000" step="1000" class="filter-input-compact sfe-agents-num-sm" data-paper-auto-recycle-tokens value="${esc(String(s.autoRecycleMinRunTokens ?? 1_500_000))}" placeholder="1500000"${ro} />
              </div>
            </div>
          </section>

          <section class="sfe-agents-settings-card">
            <header class="sfe-agents-settings-card-head">
              <h3 class="sfe-agents-settings-card-title">Selection</h3>
              <p class="muted small">How candidates are ranked and filtered.</p>
            </header>
            <div class="filter-bar recs-filter-bar sfe-agents-filter-bar">
              <div class="filter-field">
                <label>Rank by${tip("Score = screener score. Profit/day = estimated daily credit. Agent discretion = agent picks score or profit/day per name.")}</label>
                <div class="year-chips" data-paper-chip-group="rank" data-paper-chip-mode="single">
                  ${chipBtn("rank", "score", "Score", rankValue === "score", readOnly)}
                  ${chipBtn("rank", "profit_day", "Profit/day", rankValue === "profit_day", readOnly)}
                  ${chipBtn("rank", "either", "Agent discretion", rankValue === "either", readOnly)}
                </div>
              </div>
              <div class="filter-field">
                <label>Focus${tip("Prefer your Focus watchlist tickers when ranking candidates (does not exclude others).")}</label>
                <div class="year-chips" data-paper-chip-group="focus" data-paper-chip-mode="toggle">
                  ${chipBtn("focus", "prefer", "Prefer Focus", s.preferFocusTickers, readOnly)}
                </div>
              </div>
              <div class="filter-field">
                <label>Quote${tip("Same as Puts/Calls Tradable: keep quotes where mid and last are close, and reject wide bid/ask spreads.")}</label>
                <div class="year-chips" data-paper-chip-group="tradable" data-paper-chip-mode="toggle">
                  ${chipBtn("tradable", "on", "Tradable", s.tradableOnly !== false, readOnly)}
                </div>
              </div>
              <div class="filter-field sfe-agents-risky-field">
                <label>Risky calls${tip("Like Calls+: when on, the agent expands beyond the Stock Forge call screener (which only shows strike ≥ avg) using Market Data chains for underwater paper holdings with free shares. Below-avg opens still require Min PoP for risky.")}</label>
                <div class="sfe-agents-field-with-check">
                  <div class="year-chips" data-paper-chip-group="risky" data-paper-chip-mode="toggle">
                    ${chipBtn("risky", "on", "Allow risky", Boolean(s.allowRiskyCalls), readOnly)}
                  </div>
                  <div class="filter-affix">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      class="filter-input-compact sfe-agents-num-sm"
                      data-paper-risky-min-pop
                      data-paper-risky-min-pop-input
                      value="${esc(String(s.riskyCallsMinPop ?? 70))}"
                      ${readOnly || !s.allowRiskyCalls ? " disabled" : ""}
                    />
                    <span class="filter-affix__suffix">% PoP</span>
                  </div>
                </div>
              </div>
              <div class="filter-field">
                <label>Min PoP${tip("Skip candidates below this probability of profit. 0 = no PoP filter.")}</label>
                <div class="filter-affix">
                  <input type="number" min="0" max="100" step="1" class="filter-input-compact sfe-agents-num-sm" data-paper-min-pop value="${esc(String(s.minPop))}"${ro} />
                  <span class="filter-affix__suffix">%</span>
                </div>
              </div>
              <div class="filter-field">
                <label>Min Ann. ROC${tip("Skip candidates below this annualized ROC. 0 = no Ann. ROC filter.")}</label>
                <div class="filter-affix">
                  <input type="number" min="0" step="1" class="filter-input-compact sfe-agents-num-sm" data-paper-min-ann-roc value="${esc(String(s.minAnnRoc))}"${ro} />
                  <span class="filter-affix__suffix">%</span>
                </div>
              </div>
              <div class="filter-field">
                <label>Max DTE puts${tip("Do not open puts with more days to expiry than this. 0 = no max.")}</label>
                <input type="number" min="0" max="365" step="1" class="filter-input-compact sfe-agents-num-sm" data-paper-max-open-dte-puts value="${esc(String(s.maxOpenDtePuts ?? 45))}"${ro} />
              </div>
              <div class="filter-field">
                <label>Max DTE calls${tip("Do not open calls with more days to expiry than this. 0 = no max.")}</label>
                <input type="number" min="0" max="365" step="1" class="filter-input-compact sfe-agents-num-sm" data-paper-max-open-dte-calls value="${esc(String(s.maxOpenDteCalls ?? 45))}"${ro} />
              </div>
            </div>
          </section>

          <section class="sfe-agents-settings-card">
            <header class="sfe-agents-settings-card-head">
              <h3 class="sfe-agents-settings-card-title">Sizing</h3>
              <p class="muted small">How many positions and contracts to open.</p>
            </header>
            <div class="filter-bar recs-filter-bar sfe-agents-filter-bar">
              <div class="filter-field sfe-agents-top-n-field">
                <label>Max positions / timeslot${tip("Max new paper opens on each scheduled run. Turn on Agent discretion to let the bot pick 1–3.")}</label>
                <div class="sfe-agents-field-with-check">
                  <input
                    type="number"
                    min="1"
                    max="20"
                    step="1"
                    class="filter-input-compact sfe-agents-num-sm"
                    data-paper-top-n
                    data-paper-top-n-input
                    value="${esc(String(Math.max(1, s.topNPerSlot || 1)))}"
                    ${readOnly || s.topNPerSlotDiscretion ? " disabled" : ""}
                  />
                  <div class="year-chips" data-paper-chip-group="top-n-discretion" data-paper-chip-mode="toggle" title="Let the agent choose how many new opens this timeslot (1–3 within room).">
                    ${chipBtn("top-n-discretion", "on", "Agent discretion", Boolean(s.topNPerSlotDiscretion), readOnly)}
                  </div>
                </div>
              </div>
              <div class="filter-field">
                <label>Max open${tip("Hard cap on open paper positions. 0 = agent discretion (about 3–12 from cash/capital).")}</label>
                <input type="number" min="0" max="50" step="1" class="filter-input-compact sfe-agents-num-sm" data-paper-max-open value="${esc(String(s.maxOpenPositions))}"${ro} />
              </div>
              <div class="filter-field">
                <label>Contracts${tip("Max contracts per open. 0 = agent discretion (sizes 1–5 from rank strength and available cash).")}</label>
                <input type="number" min="0" max="20" step="1" class="filter-input-compact sfe-agents-num-sm" data-paper-max-contracts value="${esc(String(s.maxContractsPerTrade))}"${ro} />
              </div>
            </div>
          </section>

          <section class="sfe-agents-settings-card">
            <header class="sfe-agents-settings-card-head">
              <h3 class="sfe-agents-settings-card-title">Exits</h3>
              <p class="muted small">Take-profit, stop policy, and hold limits.</p>
            </header>
            <div class="filter-bar recs-filter-bar sfe-agents-filter-bar">
              <div class="filter-field sfe-agents-tp-field">
                <label>Take profit${tip("Guidance for Cursor closes (target % of credit captured). Not force-closed by the engine — Cursor decides on slots. Agent discretion ≈25–75% from DTE/PoP/outperform.")}</label>
                <div class="sfe-agents-field-with-check">
                  <div class="filter-affix">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      class="filter-input-compact sfe-agents-num-sm"
                      data-paper-tp
                      data-paper-tp-input
                      value="${esc(String(s.takeProfitPctOfCredit))}"
                      ${readOnly || s.takeProfitDiscretion ? " disabled" : ""}
                    />
                    <span class="filter-affix__suffix">%</span>
                  </div>
                  <div class="year-chips" data-paper-chip-group="tp-discretion" data-paper-chip-mode="toggle" title="Let the agent choose take-profit % from DTE, PoP, and outperform bias.">
                    ${chipBtn("tp-discretion", "on", "Agent discretion", Boolean(s.takeProfitDiscretion), readOnly)}
                  </div>
                </div>
              </div>
              <div class="filter-field">
                <label>Stop loss${tip("Guidance for Cursor: Stop = prefer cutting near Stop @ %; Keep = prefer hold/assignment; Agent discretion = Cursor decides when underwater. Engine does not auto-stop — only expiry is automatic.")}</label>
                <div class="year-chips" data-paper-chip-group="stop-mode" data-paper-chip-mode="single" data-paper-action-local="stop-mode">
                  ${chipBtn("stop-mode", "stop", "Stop", (s.stopLossMode || "stop") === "stop", readOnly)}
                  ${chipBtn("stop-mode", "keep", "Keep", s.stopLossMode === "keep", readOnly)}
                  ${chipBtn("stop-mode", "either", "Agent discretion", s.stopLossMode === "either", readOnly)}
                </div>
              </div>
              <div class="filter-field" data-paper-sl-pct${s.stopLossMode === "keep" ? " hidden" : ""}>
                <label>Stop @${tip("Close (or evaluate) when mid rises to this % of entry credit — e.g. 100% means mid ≈ 2× entry premium.")}</label>
                <div class="filter-affix">
                  <input type="number" min="0" step="1" class="filter-input-compact sfe-agents-num-sm" data-paper-sl value="${esc(String(s.stopLossPctOfCredit))}"${ro} />
                  <span class="filter-affix__suffix">%</span>
                </div>
              </div>
              <div class="filter-field">
                <label>Max hold DTE${tip("Guidance for Cursor when deciding closes. 0 = no DTE hold preference. Engine only auto-closes at expiry; Cursor decides other exits on slots.")}</label>
                <input type="number" min="0" max="365" step="1" class="filter-input-compact sfe-agents-num-sm" data-paper-max-hold-dte value="${esc(String(s.maxHoldDte))}"${ro} />
              </div>
              <div class="filter-field">
                <label>Expiry${tip("When on, close paper shorts at or after expiration if still open.")}</label>
                <div class="year-chips" data-paper-chip-group="expiry" data-paper-chip-mode="toggle">
                  ${chipBtn("expiry", "close", "Close at expiry", s.closeAtExpiry, readOnly)}
                </div>
              </div>
            </div>
          </section>
        </div>

        ${
          readOnly
            ? ""
            : `<div class="sfe-agents-settings-footer">
          <div class="sfe-agents-settings-actions">
            <button type="button" class="small" data-paper-action="save">Save</button>
            <button type="button" class="small ghost" data-paper-action="defaults" title="Reload default settings into the form. Click Save to persist.">Reset</button>
          </div>
          <p class="muted small sfe-agents-status" data-paper-status></p>
        </div>`
        }
      </div>
    </details>
  `;
}

function fmtSignedMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const abs = fmtUsd(Math.abs(value));
  if (value === 0) return abs;
  return value < 0 ? `-${abs}` : abs;
}

function fmtPctSigned(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    useGrouping: true,
  })}%`;
}

function rocClass(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "";
  return value >= 12 ? "roi-strong" : value >= 0 ? "" : "roi-loss";
}

type PaperOptionRow = {
  position: PaperPosition;
  credit: number;
  pnl: number;
  pnlPct: number | null;
  openRoc: number | null;
  heldRoc: number | null;
  isCall: boolean;
};

function enrichPaperOptionRows(positions: PaperPosition[]): PaperOptionRow[] {
  return positions.map((position) => {
    const credit = position.entryPremium * 100 * position.quantity;
    const pnl =
      position.unrealizedPnl ?? (position.entryPremium - position.mark) * 100 * position.quantity;
    const pnlPct = credit > 0 ? (pnl / credit) * 100 : null;
    const openRoc =
      position.annRoc != null && Number.isFinite(position.annRoc)
        ? position.annRoc
        : position.collateral > 0
          ? (credit / position.collateral) * 100
          : null;
    const heldRoc =
      position.collateral > 0 ? ((credit + pnl) / position.collateral) * 100 : openRoc;
    return {
      position,
      credit,
      pnl,
      pnlPct,
      openRoc,
      heldRoc,
      isCall: String(position.side || "").toLowerCase().startsWith("c"),
    };
  });
}

function paperAgentLedgerMark(color: string | undefined): LedgerDisplayMark {
  const key = String(color || "slate").trim().toLowerCase() || "slate";
  const accountId = `paper_agent_${key}`;
  const live = markForAccountId(accountId);
  if (live?.brokerCode || live?.accountShort) return live;
  return {
    brokerCode: "PA",
    brokerColor: "#8b5cf6",
    brokerTitle: "Paper",
    accountShort: key.slice(0, 6).toUpperCase(),
    accountTitle: accountId,
  };
}

/** Paper books show their own account. Advisor rows still use the live holding. */
let agentsPaperMark: LedgerDisplayMark | null = null;

function agentsRowMark(ticker: string): LedgerDisplayMark | null {
  return agentsPaperMark ?? recommendationLedgerMark(ticker);
}

function paperOptionColumnValue(row: PaperOptionRow, key: string): unknown {
  const p = row.position;
  switch (key) {
    case "ticker":
      return p.underlying;
    case "broker_mark":
      return agentsRowMark(p.underlying)?.brokerCode || "";
    case "account_short":
      return agentsRowMark(p.underlying)?.accountShort || "";
    case "sector":
      return null;
    case "sfe_holding_avg_cost":
      return null;
    case "strike":
      return p.strike;
    case "sfe_risk_opty":
      return null;
    case "price":
      return null;
    case "target":
      return null;
    case "side":
      return row.isCall ? "call" : "put";
    case "expiration":
      return p.expirationDate;
    case "dte":
      return p.dte;
    case "qty":
      return p.quantity;
    case "entry":
      return p.entryPremium;
    case "credit":
      return row.credit;
    case "mark":
      return p.mark;
    case "pnl":
      return row.pnl;
    case "held_roc":
      return row.heldRoc;
    case "open_roc":
      return row.openRoc;
    case "collateral":
      return row.isCall ? null : p.collateral;
    case "actions":
      return true;
    default:
      return null;
  }
}

function paperOptionVisibleColumns(columns: ColumnState[], rows: PaperOptionRow[]): ColumnState[] {
  return displayTableColumnsForData(
    columns,
    isWebCompactMode(),
    rows,
    paperOptionColumnValue,
  ).filter((column) => column.key !== "dte");
}

type PaperWhyEntry = { title: string; body: string; adviceRec?: AdvisorRecommendation };
type PaperWhyRegistry = Map<string, PaperWhyEntry>;

function paperPositionWhyId(p: PaperPosition): string {
  const sym = p.optionSymbol?.trim();
  if (sym) return `pos:${sym}`;
  return `pos:${p.underlying}:${p.strike}:${p.expirationDate}`;
}

function paperFillWhyId(f: PaperFill): string {
  if (f.id) return `txn:${f.id}`;
  return `txn:${f.ts}:${f.underlying}:${f.strike}:${f.action}`;
}

function paperWhyButton(
  registry: PaperWhyRegistry,
  id: string,
  title: string,
  body: string,
  adviceRec?: AdvisorRecommendation,
): string {
  const text = body.trim();
  if (!text || text === "—") return "";
  registry.set(id, { title, body: text, adviceRec });
  return `<button type="button" class="sfe-agents-why-btn" data-paper-action="show-why" data-why-id="${esc(id)}" aria-label="Why" title="Why">i</button>`;
}

function paperOptionSideBadgeHtml(row: PaperOptionRow): string {
  const sideCls = row.isCall ? "side-call" : "side-put";
  const sideLabel = row.isCall ? "Call" : "Put";
  return `<span class="side-badge ${sideCls}">${sideLabel}</span>`;
}

function paperAgentsTickerCellHtml(ticker: string, whyBtn: string, compact: boolean): string {
  const cellCls = compact ? "tk-cell sfe-web-compact-cell" : "tk-cell";
  return `<td class="${cellCls}" data-col="ticker"><div class="sfe-agents-ticker-cell"><span class="recs-ticker">${esc(ticker)}</span>${whyBtn}</div></td>`;
}

function renderPaperOptionCell(
  colId: string,
  row: PaperOptionRow,
  registry: PaperWhyRegistry,
): string {
  const p = row.position;
  const compact = isWebCompactMode();

  switch (colId) {
    case "ticker": {
      const whyBtn = paperWhyButton(
        registry,
        paperPositionWhyId(p),
        `${p.underlying} — Why`,
        p.justification?.trim() || "",
      );
      return paperAgentsTickerCellHtml(p.underlying, whyBtn, compact);
    }
    case "broker_mark":
      return brokerMarkCellHtml(agentsRowMark(p.underlying));
    case "account_short":
      return accountShortCellHtml(agentsRowMark(p.underlying));
    case "sector":
      return `<td class="sector-cell" data-col="sector">—</td>`;
    case "sfe_holding_avg_cost":
      return `<td class="num sfe-avg-cost-cell" data-col="sfe_holding_avg_cost">—</td>`;
    case "sfe_risk_opty":
      return `<td class="num sfe-risk-opty-cell muted" data-col="sfe_risk_opty">—</td>`;
    case "strike": {
      const popText =
        p.pop != null && Number.isFinite(p.pop) ? `PoP ${p.pop.toFixed(0)}%` : "";
      const popSpan = popText ? `<span class="pl-pct">${popText}</span>` : "";
      return `<td class="num pos-pl-cell sfe-strike-stack-cell" data-col="strike"><span class="pl-amt">$${fmtFin(p.strike)}</span>${popSpan}</td>`;
    }
    case "price":
      return compact
        ? `<td class="num sfe-web-compact-cell" data-col="price">—${compactSubline("—", "sfe-web-compact-sub")}</td>`
        : `<td class="num" data-col="price">—</td>`;
    case "target":
      return `<td class="num" data-col="target">—</td>`;
    case "side":
      return `<td data-col="side">${paperOptionSideBadgeHtml(row)}</td>`;
    case "expiration": {
      const inner = renderScreenerExpiryInnerHtml(
        { expiration_date: p.expirationDate, dte: p.dte != null ? Number(p.dte) : null },
        esc,
      );
      const cls = compact ? "sfe-web-compact-cell" : "sfe-screener-expiry-cell";
      const expDate = screenerExpiryDateLabel(p.expirationDate);
      const dateAttr = expDate !== "—" ? ` data-expiration-date="${esc(expDate)}"` : "";
      return `<td class="${cls}" data-col="expiration"${dateAttr}>${inner}</td>`;
    }
    case "dte":
      return `<td class="num" data-col="dte">${p.dte != null ? esc(String(p.dte)) : "—"}</td>`;
    case "qty":
      return `<td class="num" data-col="qty">${esc(String(p.quantity))}</td>`;
    case "entry":
      return compact
        ? `<td class="num sfe-web-compact-cell" data-col="entry">${fmtUsd(p.entryPremium)}${compactSubline(`mid ${fmtUsd(p.mark)}`, "sfe-web-compact-sub")}</td>`
        : `<td class="num" data-col="entry">${fmtUsd(p.entryPremium)}</td>`;
    case "credit":
      return compact
        ? `<td class="num sfe-web-compact-cell ${plClass(row.pnl)}" data-col="credit">${fmtUsd(row.credit)}${compactSubline(row.isCall ? "n/a" : fmtUsd(p.collateral), "sfe-web-compact-sub")}</td>`
        : `<td class="num" data-col="credit">${fmtUsd(row.credit)}</td>`;
    case "mark":
      return `<td class="num" data-col="mark">${fmtUsd(p.mark)}</td>`;
    case "pnl":
      return compact
        ? `<td class="num pos-pl-cell sfe-web-compact-cell ${plClass(row.pnl)}" data-col="pnl"><span class="pl-amt">${fmtSignedMoney(row.pnl)}</span>${compactSubline(fmtPctSigned(row.pnlPct), "sfe-web-compact-sub")}</td>`
        : `<td class="num pos-pl-cell ${plClass(row.pnl)}" data-col="pnl"><span class="pl-amt">${fmtSignedMoney(row.pnl)}</span><span class="pl-pct">${fmtPctSigned(row.pnlPct)}</span></td>`;
    case "held_roc":
      return `<td class="num ${rocClass(row.heldRoc)}" data-col="held_roc">${row.heldRoc != null ? fmtPct(row.heldRoc) : "—"}</td>`;
    case "open_roc":
      return `<td class="num ${rocClass(row.openRoc)}" data-col="open_roc">${row.openRoc != null ? fmtPct(row.openRoc) : "—"}</td>`;
    case "collateral":
      return `<td class="num${row.isCall ? " sfe-call-collateral-na" : ""}" data-col="collateral">${row.isCall ? "n/a" : fmtUsd(p.collateral)}</td>`;
    case "actions":
      return `<td class="pos-actions" data-col="actions">${renderAgentsRowMenuHtml("options", p.underlying)}</td>`;
    default:
      return `<td data-col="${esc(colId)}">—</td>`;
  }
}

const AGENTS_MOBILE_OPTION_COLUMNS: { key: string; label: string; width: number }[] = [
  { key: "broker_mark", label: "Br", width: 36 },
  { key: "account_short", label: "Ac", width: 44 },
  { key: "ticker", label: "Ticker", width: 88 },
  { key: "strike", label: "Strike", width: 72 },
  { key: "expiry", label: "Expiry", width: 76 },
  { key: "qty", label: "Qty", width: 28 },
  { key: "entry", label: "Entry / mid", width: 76 },
  { key: "credit", label: "Credit / collat", width: 92 },
  { key: "pnl", label: "P/L", width: 58 },
  { key: "held_roc", label: "Held ROC", width: 58 },
  { key: "open_roc", label: "Open ROC", width: 50 },
  { key: "actions", label: "", width: 32 },
];

function agentsMobileHost(): boolean {
  return document.body.classList.contains("sfe-mobile-host");
}

function paperOptionsMobileCell(columnKey: string, row: PaperOptionRow, registry: PaperWhyRegistry): string {
  const p = row.position;
  const sideClass = row.isCall ? "sfe-mobile-option-call" : "sfe-mobile-option-put";
  const sideLabel = row.isCall ? "Call" : "Put";
  if (columnKey === "ticker") {
    const whyBtn = paperWhyButton(
      registry,
      paperPositionWhyId(p),
      `${p.underlying} — Why`,
      p.justification?.trim() || "",
    );
    return `<td data-col="ticker"><span class="sfe-mobile-agents-ticker"><span class="sfe-mobile-options3-ticker-main">${esc(p.underlying)}<span class="sfe-mobile-table-side ${sideClass}">${sideLabel}</span></span>${whyBtn}</span></td>`;
  }
  if (columnKey === "broker_mark" || columnKey === "account_short") {
    const mark = agentsRowMark(p.underlying);
    const inner = columnKey === "broker_mark" ? brokerMarkInnerHtml(mark) : accountShortInnerHtml(mark);
    return `<td class="sfe-mark-col" data-col="${columnKey}">${inner}</td>`;
  }
  if (columnKey === "strike") {
    const pop =
      p.pop != null && Number.isFinite(p.pop)
        ? `<small class="sfe-mobile-strike-pop">POP: ${esc(p.pop.toFixed(0))}%</small>`
        : "";
    return `<td data-col="strike">${fmtUsd(p.strike)}${pop}</td>`;
  }
  if (columnKey === "expiry") {
    const expiryClass = p.dte === 0 ? "loss" : p.dte != null && p.dte <= 2 ? "sfe-mobile-dte-warning" : "";
    const date = screenerExpiryDateLabel(p.expirationDate);
    return `<td class="${expiryClass}" data-col="expiration">${esc(date)}<small class="${expiryClass}" data-agents-dte>${p.dte == null ? "—" : `${esc(String(p.dte))} DTE`}</small></td>`;
  }
  if (columnKey === "qty") return `<td data-col="qty">${esc(String(p.quantity))}</td>`;
  if (columnKey === "entry") {
    return `<td data-col="entry">${fmtUsd(p.entryPremium)}<small data-agents-mid>mid ${fmtUsd(p.mark)}</small></td>`;
  }
  if (columnKey === "credit") {
    const collat = row.isCall ? "—" : fmtUsd(p.collateral);
    return `<td class="${plClass(row.pnl)}" data-col="credit">${fmtUsd(row.credit)}<small>collat ${collat}</small></td>`;
  }
  if (columnKey === "pnl") {
    return `<td class="${plClass(row.pnl)}" data-col="pnl">${fmtSignedMoney(row.pnl)}<small data-agents-pnl-pct>${fmtPctSigned(row.pnlPct)}</small></td>`;
  }
  if (columnKey === "held_roc") {
    return `<td class="${rocClass(row.heldRoc)}" data-col="held_roc">${row.heldRoc != null ? fmtPct(row.heldRoc) : "—"}</td>`;
  }
  if (columnKey === "open_roc") {
    return `<td class="${rocClass(row.openRoc)}" data-col="open_roc">${row.openRoc != null ? fmtPct(row.openRoc) : "—"}</td>`;
  }
  return `<td class="pos-actions" data-col="actions">${renderAgentsRowMenuHtml("options", p.underlying)}</td>`;
}

function paperOptionsMobileTableHtml(
  positions: PaperPosition[],
  registry: PaperWhyRegistry,
  tickerFilter = "",
): string {
  const filtered = positions.filter((p) => matchesAgentsTickerFilter(p.underlying, tickerFilter));
  const rows = enrichPaperOptionRows(filtered);
  const columns = AGENTS_MOBILE_OPTION_COLUMNS;
  const tableWidth = columns.reduce((total, column) => total + column.width, 0);
  const columnTemplate = columns.map((column) => `${column.width}px`).join(" ");
  const emptyLabel = tickerFilter.trim()
    ? "No open paper option positions match this ticker filter."
    : "No open paper option positions.";
  const body = rows.length
    ? rows
        .map((row) => {
          const cells = columns.map((column) => paperOptionsMobileCell(column.key, row, registry)).join("");
          return `<tr data-symbol="${esc(row.position.optionSymbol || "")}">${cells}</tr>`;
        })
        .join("")
    : `<tr><td colspan="${columns.length}" class="muted">${emptyLabel}</td></tr>`;
  const header = columns
    .map((column) => `<span>${esc(column.label)}</span>`)
    .join("");
  return `
    <div class="sfe-mobile-agents-options-table">
      <div class="sfe-mobile-options-table-sticky-head sfe-mobile-agents-options-sticky-head">
        <div class="sfe-mobile-options-table-sticky-inner" style="width:${tableWidth}px;grid-template-columns:${columnTemplate}">${header}</div>
      </div>
      <div class="sfe-mobile-options-table-wrap sfe-mobile-agents-options-table-wrap" data-agents-options-table="1">
        <table class="sfe-mobile-options-table" style="width:${tableWidth}px;min-width:${tableWidth}px">
          <colgroup>${columns.map((column) => `<col style="width:${column.width}px" />`).join("")}</colgroup>
          <tbody>${body}</tbody>
        </table>
      </div>
    </div>`;
}

function paperOptionsTableHtml(
  positions: PaperPosition[],
  registry: PaperWhyRegistry,
  tickerFilter = "",
): string {
  if (agentsMobileHost()) return paperOptionsMobileTableHtml(positions, registry, tickerFilter);
  const filtered = positions.filter((p) => matchesAgentsTickerFilter(p.underlying, tickerFilter));
  const rows = enrichPaperOptionRows(filtered);
  const columns = resolveWebTableColumns(AGENTS_OPTIONS_TABLE_ID);
  const visible = paperOptionVisibleColumns(columns, rows);
  const colCount = Math.max(visible.length, 1);
  const emptyLabel = tickerFilter.trim()
    ? "No open paper option positions match this ticker filter."
    : "No open paper option positions.";
  const body = rows.length
    ? rows
        .map((row) => {
          const cells = visible
            .map((column) => renderPaperOptionCell(column.key, row, registry))
            .join("");
          return `<tr data-symbol="${esc(row.position.optionSymbol || "")}">${cells}</tr>`;
        })
        .join("")
    : `<tr><td colspan="${colCount}" class="muted">${emptyLabel}</td></tr>`;

  return `
    <div class="recs-wrap sfe-agents-recs-wrap">
      <div class="recs-hscroll">
        <table class="recs recs-compact recs-sticky-col sfe-agents-positions-table"${renderTablePinnedAttr(visible)}>
          ${renderTableColgroup(visible)}
          <thead><tr>${renderTableHeadRow(visible)}</tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    </div>`;
}

function agentsStructureSignature(
  state: Pick<PaperAgentStateResponse, "agentId" | "runInFlight" | "portfolio">,
): string {
  const p = state.portfolio;
  const positions = (p.positions || [])
    .map((row) => `${row.optionSymbol || ""}:${row.quantity}`)
    .sort()
    .join("|");
  const fills = (p.fills || [])
    .map((row) => String(row.id || ""))
    .sort()
    .join("|");
  return [
    state.agentId || "",
    state.runInFlight ? "1" : "0",
    positions,
    fills,
    String((p.runLog || []).length),
  ].join("::");
}

function agentsLiveNumbersSignature(
  state: Pick<PaperAgentStateResponse, "portfolio" | "runInFlight">,
): string {
  const p = state.portfolio;
  const marks = (p.positions || [])
    .map((row) => `${row.optionSymbol || ""}:${Number(row.mark).toFixed(4)}:${row.dte ?? ""}`)
    .sort()
    .join("|");
  return [
    state.runInFlight ? "1" : "0",
    Number(p.equity).toFixed(2),
    Number(p.cash).toFixed(2),
    Number(p.unrealized).toFixed(2),
    Number(p.reserved).toFixed(2),
    marks,
  ].join("::");
}

function patchAgentsKpiValue(host: HTMLElement, label: string, value: string, tone?: "pos" | "neg"): void {
  for (const div of host.querySelectorAll<HTMLElement>(".sfe-agents-kpi-row > div")) {
    const span = div.querySelector("span.muted.small");
    if (span?.textContent?.trim() !== label) continue;
    const strong = div.querySelector("strong");
    if (!strong) continue;
    strong.textContent = value;
    if (tone === "pos") strong.className = "sfe-pos";
    else if (tone === "neg") strong.className = "sfe-neg";
    else strong.removeAttribute("class");
    return;
  }
}

function patchAgentsLiveOverview(host: HTMLElement, portfolio: PaperAgentStateResponse["portfolio"], advisor: boolean): void {
  const wrap = host.querySelector<HTMLElement>(".sfe-agents-live .sfe-agents-kpi-row");
  if (!wrap) return;
  patchAgentsKpiValue(wrap, "Cash", fmtUsd(portfolio.cash));
  if (advisor) patchAgentsKpiValue(wrap, "Buying power", fmtUsd(portfolio.buyingPower ?? portfolio.cash));
  patchAgentsKpiValue(wrap, "Equity", fmtUsd(portfolio.equity));
  patchAgentsKpiValue(wrap, "Reserved", fmtUsd(portfolio.reserved));
  if (!advisor) {
    patchAgentsKpiValue(
      wrap,
      "Unrealized",
      fmtUsd(portfolio.unrealized),
      portfolio.unrealized >= 0 ? "pos" : "neg",
    );
  }
}

function patchAgentsOptionRow(host: HTMLElement, row: PaperOptionRow): void {
  const sym = row.position.optionSymbol || "";
  const tr = host.querySelector<HTMLElement>(`tr[data-symbol="${CSS.escape(sym)}"]`);
  if (!tr) return;
  const compact = isWebCompactMode();
  const markCell = tr.querySelector<HTMLElement>('td[data-col="mark"]');
  if (markCell) markCell.textContent = fmtUsd(row.position.mark);
  const dteCell = tr.querySelector<HTMLElement>('td[data-col="dte"]');
  if (dteCell) dteCell.textContent = row.position.dte != null ? String(row.position.dte) : "—";
  const mobileDte = tr.querySelector<HTMLElement>("[data-agents-dte]");
  if (mobileDte) {
    const dte = row.position.dte;
    mobileDte.textContent = dte == null ? "—" : `${dte} DTE`;
    mobileDte.className = dte === 0 ? "loss" : dte != null && dte <= 2 ? "sfe-mobile-dte-warning" : "";
  }
  const mobileMid = tr.querySelector<HTMLElement>("[data-agents-mid]");
  if (mobileMid) mobileMid.textContent = `mid ${fmtUsd(row.position.mark)}`;
  const mobilePnlPct = tr.querySelector<HTMLElement>("[data-agents-pnl-pct]");
  if (mobilePnlPct) mobilePnlPct.textContent = fmtPctSigned(row.pnlPct);
  const pnlCell = tr.querySelector<HTMLElement>('td[data-col="pnl"]');
  if (pnlCell) {
    pnlCell.className = `num pos-pl-cell${compact ? " sfe-web-compact-cell" : ""} ${plClass(row.pnl)}`.trim();
    const amt = pnlCell.querySelector(".pl-amt");
    if (amt) amt.textContent = fmtSignedMoney(row.pnl);
    const pct = pnlCell.querySelector(".pl-pct");
    if (pct) pct.textContent = fmtPctSigned(row.pnlPct);
  }
  const heldRocCell = tr.querySelector<HTMLElement>('td[data-col="held_roc"]');
  if (heldRocCell) {
    heldRocCell.className = `num ${rocClass(row.heldRoc)}`.trim();
    heldRocCell.textContent = row.heldRoc != null ? fmtPct(row.heldRoc) : "—";
  }
  const openRocCell = tr.querySelector<HTMLElement>('td[data-col="open_roc"]');
  if (openRocCell) {
    openRocCell.className = `num ${rocClass(row.openRoc)}`.trim();
    openRocCell.textContent = row.openRoc != null ? fmtPct(row.openRoc) : "—";
  }
  if (compact) {
    const entryCell = tr.querySelector<HTMLElement>('td[data-col="entry"]');
    const sub = entryCell?.querySelector(".sfe-web-compact-sub");
    if (sub) sub.textContent = `mid ${fmtUsd(row.position.mark)}`;
    const creditCell = tr.querySelector<HTMLElement>('td[data-col="credit"]');
    if (creditCell) creditCell.className = `num sfe-web-compact-cell ${plClass(row.pnl)}`.trim();
  }
}

function patchAgentsLivePositions(host: HTMLElement, positions: PaperPosition[]): void {
  const rows = enrichPaperOptionRows(positions);
  for (const row of rows) patchAgentsOptionRow(host, row);
}

function patchAgentsLiveQuiet(
  host: HTMLElement,
  state: PaperAgentStateResponse,
): boolean {
  if (!host.querySelector(".sfe-agents-live")) return false;
  const advisor = isAdvisorAgent(state);
  patchAgentsLiveOverview(host, state.portfolio, advisor);
  patchAgentsLivePositions(host, state.portfolio.positions || []);
  return true;
}

function paperStocksTableHtml(shares: PaperShareHolding[]): string {
  const rows = (shares || [])
    .filter((row) => (Number(row.quantity) || 0) > 0)
    .slice()
    .sort((a, b) => a.ticker.localeCompare(b.ticker));
  const body = rows.length
    ? rows
        .map((row) => {
          const qty = Number(row.quantity) || 0;
          const available = Number(row.available);
          const availLabel = Number.isFinite(available) ? fmtInt(available) : "—";
          const availCls =
            Number.isFinite(available) && available > 0 ? "roi-strong" : "sfe-web-compact-sub";
          const mark = agentsRowMark(row.ticker);
          return `<tr data-ticker="${esc(row.ticker)}">
            ${brokerMarkCellHtml(mark)}
            ${accountShortCellHtml(mark)}
            <td class="tk-cell" data-col="ticker">${esc(row.ticker)}</td>
            <td class="num sfe-shares-cell" data-col="quantity">${fmtInt(qty)}${compactSubline(`avail ${availLabel}`, availCls)}</td>
            <td class="num" data-col="avg_price">${row.avgCost != null ? fmtUsd(row.avgCost) : "—"}</td>
            <td class="num" data-col="cost_basis">${row.costBasis != null ? fmtUsd(row.costBasis) : "—"}</td>
          </tr>`;
        })
        .join("")
    : `<tr><td colspan="6" class="muted">No stock positions — assigned puts will show shares here.</td></tr>`;
  return `
    <div class="recs-wrap sfe-agents-recs-wrap">
      <div class="recs-hscroll">
        <table class="recs recs-compact recs-sticky-col sfe-agents-positions-table">
          <colgroup>
            <col class="sfe-stocks-ticker-col" />
            <col class="sfe-mark-col" style="width:22.4px" />
            <col class="sfe-mark-col sfe-acct-col" style="width:29.4px" />
            <col />
            <col />
            <col />
          </colgroup>
          <thead><tr>
            <th class="tk-cell" data-col="ticker">Ticker</th>
            <th class="sfe-mark-col" data-col="broker_mark" title="Broker">Br</th>
            <th class="sfe-mark-col sfe-acct-col" data-col="account_short" title="Account">Ac</th>
            <th class="num" data-col="quantity">Shares</th>
            <th class="num" data-col="avg_price" title="Strike-based avg from assigned puts">Avg cost</th>
            <th class="num" data-col="cost_basis">Cost basis</th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    </div>`;
}

function positionsBookHtml(
  positions: PaperPosition[],
  shares: PaperShareHolding[],
  open: { options: boolean; stocks: boolean },
  persist = true,
  registry: PaperWhyRegistry,
  optionsTickerFilter = "",
  optionsFilterPanelOpen = false,
): string {
  const filtered = positions.filter((p) => matchesAgentsTickerFilter(p.underlying, optionsTickerFilter));
  const putCollateral = filtered
    .filter((p) => !String(p.side || "").toLowerCase().startsWith("c"))
    .reduce((sum, p) => sum + (Number(p.collateral) || 0), 0);
  const contracts = filtered.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);
  const shareRows = (shares || []).filter((row) => (Number(row.quantity) || 0) > 0);
  const shareQty = shareRows.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
  const legSummary =
    optionsTickerFilter.trim() && filtered.length !== positions.length
      ? `${fmtInt(filtered.length)} of ${fmtInt(positions.length)} legs`
      : `${fmtInt(filtered.length)} legs`;
  const showOptionsFilter = showAgentsTickerFilterPanel(optionsTickerFilter, optionsFilterPanelOpen);
  return `
    <div class="sfe-agents-book">
      <details class="collapsible sfe-agents-book-section" ${liveSectionAttrs("options", open.options, persist)}>
        <summary class="sfe-agents-section-summary sfe-agents-book-summary">
          Options · open positions
          <span class="muted small">${legSummary} · ${fmtInt(contracts)} contracts · put coll. ${fmtUsd(putCollateral)}</span>
        </summary>
        <div class="sfe-agents-book-body">
          ${agentsTickerFilterPanelHtml("options", optionsTickerFilter, showOptionsFilter)}
          ${paperOptionsTableHtml(positions, registry, optionsTickerFilter)}
        </div>
      </details>
      <details class="collapsible sfe-agents-book-section" ${liveSectionAttrs("stocks", open.stocks, persist)}>
        <summary class="sfe-agents-section-summary sfe-agents-book-summary">
          Stocks
          <span class="muted small">${fmtInt(shareRows.length)} tickers · ${fmtInt(shareQty)} shares</span>
        </summary>
        <div class="sfe-agents-book-body">
          ${paperStocksTableHtml(shareRows)}
        </div>
      </details>
    </div>`;
}

function strategyLabel(strategy: string | null | undefined): string {
  const value = String(strategy || "").toLowerCase();
  if (value === "wheel") return "Wheel";
  if (value === "discretion" || value === "agent_discretion") return "Agent discretion";
  return "—";
}

function syncAgentsColumnPickers(host: HTMLElement, onApply: () => void): void {
  const pickerHost = host.querySelector<HTMLElement>("[data-agents-column-picker]");
  if (!pickerHost) return;
  pickerHost.innerHTML = "";
  const button = document.createElement("button");
  button.type = "button";
  button.className = "small ghost sfe-column-settings-btn";
  button.setAttribute("aria-label", "Configure Agentic table columns");
  button.title = "Configure table columns";
  button.textContent = "☷";
  button.addEventListener("click", () => {
    openMultiColumnSettingsModal({
      title: "Configure Agentic columns",
      sections: [
        {
          tableId: AGENTS_OPTIONS_TABLE_ID,
          sectionTitle: "Options · open positions",
          defaults: getTableColumnDefaults(effectiveWebTableId(AGENTS_OPTIONS_TABLE_ID)),
        },
        {
          tableId: AGENTS_TXN_TABLE_ID,
          sectionTitle: "Transactions",
          defaults: getTableColumnDefaults(AGENTS_TXN_TABLE_ID),
        },
      ],
      onApply,
    });
  });
  pickerHost.appendChild(button);
}

/** Open credit in dollars (for close-row P&L % vs entry). */
function paperFillOpenCreditTotal(f: PaperFill): number {
  const qty = Math.max(1, Math.round(f.quantity || 1));
  const action = String(f.action || "").toLowerCase();
  const perShare =
    action === "close" && f.entryPremium != null && Number.isFinite(f.entryPremium)
      ? f.entryPremium
      : f.premium;
  return (Number(perShare) || 0) * 100 * qty;
}

function paperFillPnlPct(f: PaperFill): number | null {
  const credit = paperFillOpenCreditTotal(f);
  if (!(credit > 0) || !Number.isFinite(f.pnl)) return null;
  return (f.pnl / credit) * 100;
}

/** Transaction premium in total contract dollars (premium × 100 × qty). */
function paperFillPremiumTotal(f: PaperFill): number {
  const qty = Math.max(1, Math.round(f.quantity || 1));
  return (Number(f.premium) || 0) * 100 * qty;
}

/** Ledger-style action code for paper fills (STO, BTC, EXP, ASG, …). */
function paperFillTxnKind(fill: PaperFill): { kind: string; label: string } {
  const action = String(fill.action || "").toLowerCase();
  const reason = String(fill.reason || "").toLowerCase();
  const side = String(fill.side || "").toLowerCase();

  if (action === "assign" || action === "assignment") {
    return { kind: "asgn", label: "ASG" };
  }
  if (reason === "assignment" || reason === "assign" || reason === "asgn") {
    return { kind: "asgn", label: "ASG" };
  }
  if (action === "open") {
    return { kind: "sto", label: "STO" };
  }
  if (action === "hold") {
    return { kind: "hold", label: "HOLD" };
  }
  if (action === "skip") {
    return { kind: "skip", label: "SKIP" };
  }
  if (action === "close") {
    if (reason === "assignment" || reason === "assign" || reason === "asgn") {
      return { kind: "asgn", label: "ASG" };
    }
    if (reason === "expiry" || reason === "exp" || reason === "expired") {
      return { kind: "exp", label: "EXP" };
    }
    return { kind: "btc", label: "BTC" };
  }
  const fallback = action ? action.toUpperCase() : "—";
  return { kind: action || "sto", label: fallback };
}

/**
 * Call-assignment P/L per fill, using share avg at that moment.
 * Same replay as History `assign_pl` and month own capital (`paperAgentHistory.mjs`).
 */
function paperAssignIncomeByFillId(fills: PaperFill[]): Map<string, number> {
  const lots = new Map<string, { quantity: number; costBasis: number }>();
  const byId = new Map<string, number>();
  const chrono = [...fills].sort((a, b) => String(a.ts || "").localeCompare(String(b.ts || "")));
  for (const fill of chrono) {
    if (paperFillTxnKind(fill).kind !== "asgn") continue;
    const ticker = String(fill.underlying || "").trim().toUpperCase();
    const strike = Number(fill.strike) || 0;
    const shares = Math.max(1, Math.round(Number(fill.quantity) || 1)) * 100;
    const side = String(fill.side || "").toLowerCase();
    if (!fill.id) continue;
    if (!ticker || !(strike > 0)) {
      byId.set(fill.id, 0);
      continue;
    }
    if (side.startsWith("p")) {
      const lot = lots.get(ticker) || { quantity: 0, costBasis: 0 };
      lot.quantity += shares;
      lot.costBasis += strike * shares;
      lots.set(ticker, lot);
      byId.set(fill.id, 0);
      continue;
    }
    if (!side.startsWith("c")) {
      byId.set(fill.id, 0);
      continue;
    }
    const lot = lots.get(ticker);
    const deliver = Math.min(lot?.quantity || 0, shares);
    let pnl = 0;
    if (lot && deliver > 0) {
      const avgCost = lot.costBasis / lot.quantity;
      pnl = (strike - avgCost) * deliver;
      lot.quantity -= deliver;
      lot.costBasis = avgCost * lot.quantity;
      if (lot.quantity <= 0) lots.delete(ticker);
      else lots.set(ticker, lot);
    }
    byId.set(fill.id, pnl);
  }
  return byId;
}

function paperFillAssignPnl(fill: PaperFill): number {
  if (fill.assignIncome != null && Number.isFinite(fill.assignIncome)) return fill.assignIncome;
  return paperTxnAssignIncome.get(fill.id) ?? 0;
}

function paperFillSideBadgeHtml(fill: PaperFill): string {
  const side = String(fill.side || "").toLowerCase();
  const isCall = side.startsWith("c");
  if (!isCall && !side.startsWith("p")) return "—";
  const sideCls = isCall ? "side-call" : "side-put";
  const sideLabel = isCall ? "Call" : "Put";
  return `<span class="side-badge ${sideCls}">${sideLabel}</span>`;
}

function renderPaperTransactionCell(
  colId: string,
  fill: PaperFill,
  registry: PaperWhyRegistry,
): string {
  const pnlPct = paperFillPnlPct(fill);
  const action = String(fill.action || "").toLowerCase();
  switch (colId) {
    case "when":
      return `<td data-col="when">${esc(fmtTs(fill.ts))}</td>`;
    case "action": {
      const txn = paperFillTxnKind(fill);
      return `<td data-col="action"><span class="sfe-txn-kind sfe-txn-kind-${esc(txn.kind)}">${esc(txn.label)}</span></td>`;
    }
    case "strategy":
      return `<td data-col="strategy">${esc(strategyLabel(fill.strategy))}</td>`;
    case "reason":
      return `<td data-col="reason">${esc(fill.reason)}</td>`;
    case "ticker": {
      const adviceRec =
        agentsAdviceActionsEnabled && isAdviceFill(fill) ? fillToAdviceRec(fill) : undefined;
      const whyBtn = paperWhyButton(
        registry,
        paperFillWhyId(fill),
        `${fill.underlying} ${fill.action} — Advice`,
        fill.justification?.trim() || fill.reason || "",
        adviceRec,
      );
      return `<td class="tk-cell" data-col="ticker"><div class="sfe-agents-ticker-cell"><span class="recs-ticker">${esc(fill.underlying)}</span>${whyBtn}</div></td>`;
    }
    case "broker_mark":
      return brokerMarkCellHtml(agentsRowMark(fill.underlying));
    case "account_short":
      return accountShortCellHtml(agentsRowMark(fill.underlying));
    case "side":
      return `<td data-col="side">${paperFillSideBadgeHtml(fill)}</td>`;
    case "contract":
      return `<td data-col="contract">$${fmtFin(fill.strike)}</td>`;
    case "qty":
      return `<td class="num" data-col="qty">${esc(String(fill.quantity))}</td>`;
    case "premium": {
      const totalPremium = paperFillPremiumTotal(fill);
      const isClose = action === "close";
      if (isClose) {
        const txn = paperFillTxnKind(fill);
        if (txn.kind === "exp" || txn.kind === "asgn") {
          const title =
            txn.kind === "exp"
              ? "Expired worthless — no buyback premium"
              : "Assigned at expiration — no buyback premium";
          return `<td class="num" data-col="premium" title="${esc(title)}">${fmtUsd(0)}</td>`;
        }
        const paid = -Math.abs(totalPremium);
        const premiumTitle = "Total buy-to-close premium (paid)";
        const cls = totalPremium > 0 ? "roi-loss" : "";
        return `<td class="num ${cls}" data-col="premium" title="${esc(premiumTitle)}">${fmtSignedMoney(paid)}</td>`;
      }
      const premiumTitle = "Total premium received at open";
      const cls = totalPremium > 0 ? "roi-strong" : "";
      return `<td class="num ${cls}" data-col="premium" title="${esc(premiumTitle)}">${fmtUsd(totalPremium)}</td>`;
    }
    case "pnl": {
      const txn = paperFillTxnKind(fill);
      // Premium is booked on the STO open; expiry closes do not realize that credit again.
      if (txn.kind === "exp") {
        return `<td class="num pos-pl-cell" data-col="pnl" title="${esc("Expiry — option premium already booked on open; close P&L is $0")}"><span class="pl-amt">${fmtUsd(0)}</span></td>`;
      }
      if (txn.kind === "asgn") {
        const assignPnl = paperFillAssignPnl(fill);
        const side = String(fill.side || "").toLowerCase();
        const title = side.startsWith("c")
          ? "Call assignment P/L = (strike − share avg cost) × shares. Option premium already booked on open."
          : "Put assignment — shares added at strike; option premium already booked on open.";
        return `<td class="num pos-pl-cell ${plClass(assignPnl)}" data-col="pnl" title="${esc(title)}"><span class="pl-amt">${fmtSignedMoney(assignPnl)}</span></td>`;
      }
      return `<td class="num pos-pl-cell ${plClass(fill.pnl)}" data-col="pnl"><span class="pl-amt">${fmtSignedMoney(fill.pnl)}</span><span class="pl-pct">${fmtPctSigned(pnlPct)}</span></td>`;
    }
    case "_actions": {
      if (agentsAdviceActionsEnabled && isAdviceFill(fill)) {
        const status = String(fill.userStatus || "pending").toLowerCase();
        if (status === "accepted") {
          return `<td data-col="_actions"><span class="sfe-advice-status sfe-advice-status-accepted">Accepted</span></td>`;
        }
        if (status === "dismissed") {
          return `<td data-col="_actions"><span class="sfe-advice-status sfe-advice-status-dismissed muted">Dismissed</span></td>`;
        }
        return `<td data-col="_actions" class="sfe-advice-status-actions">
          <button type="button" class="small" data-paper-action="advice-accept" data-rec-id="${esc(fill.id)}">Accept</button>
          <button type="button" class="small ghost" data-paper-action="advice-dismiss" data-rec-id="${esc(fill.id)}">Dismiss</button>
        </td>`;
      }
      return `<td data-col="_actions">${renderAgentsRowMenuHtml("transactions", fill.underlying)}</td>`;
    }
    default:
      return `<td data-col="${esc(colId)}">—</td>`;
  }
}

function agentsTxnDisplayColumns(columns: ReturnType<typeof resolveWebTableColumns>): ReturnType<typeof resolveWebTableColumns> {
  const visible = displayTableColumns(columns, isWebCompactMode());
  const tickerIdx = visible.findIndex((column) => column.key === "ticker");
  if (tickerIdx <= 0) return visible;
  const ordered = [...visible];
  const [ticker] = ordered.splice(tickerIdx, 1);
  ordered.unshift(ticker);
  return ordered;
}

interface PaperTxnDayBucket {
  date: string;
  fills: PaperFill[];
  premiums: number;
  btcCost: number;
  assignIncome: number;
  expIncome: number;
}

function paperFillDayKey(fill: PaperFill): string {
  const d = Date.parse(fill.ts);
  if (!Number.isFinite(d)) return "unknown";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: PACIFIC_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(d));
}

function paperTxnDayTotal(bucket: PaperTxnDayBucket): number {
  return (
    bucket.premiums -
    bucket.btcCost +
    bucket.assignIncome +
    bucket.expIncome
  );
}

function groupPaperFillsByDay(
  fills: PaperFill[],
  assignById: Map<string, number> = paperTxnAssignIncome,
): PaperTxnDayBucket[] {
  const byDate = new Map<string, PaperFill[]>();
  for (const fill of fills) {
    const key = paperFillDayKey(fill);
    const list = byDate.get(key) ?? [];
    list.push(fill);
    byDate.set(key, list);
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, dayFills]) => {
      const sorted = [...dayFills].sort((a, b) => {
        const ta = Date.parse(a.ts);
        const tb = Date.parse(b.ts);
        return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
      });
      let premiums = 0;
      let btcCost = 0;
      let assignIncome = 0;
      let expIncome = 0;
      for (const fill of sorted) {
        const kind = paperFillTxnKind(fill);
        const total = paperFillPremiumTotal(fill);
        if (kind.kind === "sto") premiums += total;
        else if (kind.kind === "btc") btcCost += total;
        else if (kind.kind === "asgn") {
          assignIncome +=
            fill.assignIncome != null && Number.isFinite(fill.assignIncome)
              ? fill.assignIncome
              : assignById.get(fill.id) ?? 0;
        }
      }
      return { date, fills: sorted, premiums, btcCost, assignIncome, expIncome };
    });
}

function renderPaperTxnDaySummaryChips(bucket: PaperTxnDayBucket): string {
  return [
    bucket.premiums ? `Premiums ${fmtUsd(bucket.premiums)}` : null,
    bucket.btcCost ? `BTC −${fmtUsd(bucket.btcCost)}` : null,
    bucket.assignIncome ? `Call Assgn. ${fmtUsd(bucket.assignIncome)}` : null,
    bucket.expIncome ? `Expiry ${fmtUsd(bucket.expIncome)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function renderPaperTxnTableBody(
  txns: PaperFill[],
  registry: PaperWhyRegistry,
  columns: ReturnType<typeof agentsTxnDisplayColumns>,
): string {
  return txns
    .map((fill) => {
      const cells = columns
        .map((column) => renderPaperTransactionCell(column.key, fill, registry))
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");
}

function renderPaperTxnDayTable(
  txns: PaperFill[],
  registry: PaperWhyRegistry,
): string {
  const columns = agentsTxnDisplayColumns(resolveWebTableColumns(AGENTS_TXN_TABLE_ID));
  const rows = renderPaperTxnTableBody(txns, registry, columns);
  return `
    <div class="sfe-agents-table-wrap sfe-agents-txn-wrap">
      <table class="sfe-agents-table sfe-agents-txn-table sfe-income-txn-table recs">
        ${renderTableColgroup(columns)}
        <thead>
          <tr>${renderTableHeadRow(columns)}</tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

function transactionsTableHtml(
  txns: PaperFill[],
  registry: PaperWhyRegistry,
  tickerFilter = "",
  emptyLabel = "No paper transactions yet.",
  adviceActions = false,
  adviceAgentId = "",
  lotSourceFills?: PaperFill[],
): string {
  agentsAdviceActionsEnabled = adviceActions;
  agentsAdviceAgentId = adviceAgentId;
  paperTxnAssignIncome = paperAssignIncomeByFillId(lotSourceFills?.length ? lotSourceFills : txns);
  if (!txns.length) {
    const msg = tickerFilter.trim()
      ? "No transactions match this ticker filter."
      : emptyLabel;
    return `<p class="muted small">${msg}</p>`;
  }

  const days = groupPaperFillsByDay(txns, paperTxnAssignIncome);
  return `
    <div class="sfe-income-days sfe-agents-txn-days">
      ${days
        .map((day, index) => {
          const chips = renderPaperTxnDaySummaryChips(day);
          const dayTotal = paperTxnDayTotal(day);
          const dayId = `txn-day-${day.date}`;
          const defaultOpen = index === 0;
          const totalClass =
            dayTotal < 0 ? "sfe-neg" : dayTotal > 0 ? "roi-strong" : "";
          return `
        <details
          class="collapsible sfe-income-day sfe-agents-txn-day"
          data-sfe-collapsible="${esc(dayId)}"
          data-sfe-collapsible-default="${defaultOpen ? "open" : "closed"}"${collapsibleOpenAttr(AGENTS_COLLAPSIBLE_SCOPE, dayId, defaultOpen)}
        >
          <summary class="sfe-income-day-summary">
            <span class="sfe-income-summary-row">
              <span class="sfe-income-day-date">
                ${esc(day.date)}
                ${chips ? `<span class="sfe-income-day-chips muted small">${esc(chips)}</span>` : ""}
              </span>
              <span class="sfe-income-day-total ${totalClass}">${esc(fmtUsd(dayTotal))}</span>
            </span>
          </summary>
          <div class="sfe-income-txn-wrap">
            ${renderPaperTxnDayTable(day.fills, registry)}
          </div>
        </details>`;
        })
        .join("")}
    </div>`;
}

function runActivityHtml(entry: PaperRunLogEntry): string {
  if (entry.kind === "advice") {
    const bits = ["advice"];
    if (entry.bootstrapUsed === true) bits.push("bootstrap");
    const ok = entry.ok === false ? "fail" : entry.ok === true ? "ok" : "";
    if (ok) bits.push(ok);
    const title = entry.bootstrapUsed
      ? "Advisor run — Cursor cloud agent was bootstrapped (create + first slot). Order intents recorded, not executed."
      : "Advisor run — order intents recorded, not executed";
    return `<span class="sfe-agents-run-activity sfe-agents-run-advice" title="${esc(title)}">${esc(bits.join(" · "))}</span>`;
  }
  const isTest =
    entry.dryRun === true ||
    entry.kind === "advise-test" ||
    entry.kind === "recycle" ||
    entry.kind === "test" ||
    entry.kind === "dry-run" ||
    entry.slot === "advise-test" ||
    entry.slot === "recycle" ||
    entry.slot === "auto-recycle";
  if (isTest) {
    const badge =
      entry.kind === "recycle" || entry.slot === "recycle" || entry.slot === "auto-recycle"
        ? entry.slot === "auto-recycle"
          ? "auto-recycle"
          : "recycle"
        : entry.kind === "dry-run" || (entry.dryRun && entry.kind !== "advise-test")
          ? "dry-run"
          : "test";
    const ok = entry.ok === false ? "fail" : entry.ok === true ? "ok" : "";
    const label = ok ? `${badge} · ${ok}` : badge;
    const title =
      entry.kind === "recycle" || entry.slot === "recycle" || entry.slot === "auto-recycle"
        ? entry.slot === "auto-recycle"
          ? "Auto-recycle — cloud agent restarted by run-count or token threshold"
          : "Manual recycle — cloud agent restarted; paper ledger unchanged"
        : "Advise test / dry-run — no paper fills";
    return `<span class="sfe-agents-run-activity sfe-agents-run-test" title="${esc(title)}">${esc(label)}</span>`;
  }
  const activity = entry.activity;
  if (!activity) return `<span class="sfe-agents-run-activity muted">—</span>`;
  const parts: string[] = [];
  parts.push(activity.enabled ? "Enabled" : "Disabled");
  if (activity.traded) parts.push("Traded");
  if (activity.toggled) parts.push("Toggled");
  const cls = [
    "sfe-agents-run-activity",
    activity.enabled ? "is-enabled" : "is-disabled",
    activity.traded ? "is-traded" : "",
    activity.toggled ? "is-toggled" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const title = activity.day ? `${activity.day} · ${parts.join(" · ")}` : parts.join(" · ");
  return `<span class="${cls}" title="${esc(title)}">${esc(parts.join(" · "))}</span>`;
}

function normalizeWebQueries(raw: unknown): PaperWebQuery[] {
  if (!Array.isArray(raw)) return [];
  const out: PaperWebQuery[] = [];
  for (const row of raw) {
    if (typeof row === "string") {
      const query = row.trim();
      if (query) out.push({ query });
      continue;
    }
    if (!row || typeof row !== "object") continue;
    const rec = row as Record<string, unknown>;
    const query = String(rec.query ?? rec.q ?? rec.search ?? rec.url ?? "").trim();
    if (!query) continue;
    const purpose = String(rec.purpose ?? rec.why ?? rec.note ?? "").trim();
    out.push(purpose ? { query, purpose } : { query });
    if (out.length >= 20) break;
  }
  return out;
}

function webResearchFromEntry(entry: PaperRunLogEntry | Record<string, unknown>): {
  allowWeb: boolean;
  webUsed: boolean;
  webQueries: PaperWebQuery[];
} {
  const allowWeb = entry.allowWeb === true;
  let webQueries = normalizeWebQueries(entry.webQueries);
  let webUsed = entry.webUsed === true;
  if (!webQueries.length && entry.decision && typeof entry.decision === "object") {
    const d = entry.decision as Record<string, unknown>;
    if (!webQueries.length) webQueries = normalizeWebQueries(d.webQueries ?? d.web_queries);
    if (d.webUsed === true) webUsed = true;
    else if (d.webUsed === false) webUsed = false;
  }
  if (webQueries.length) webUsed = true;
  return { allowWeb, webUsed, webQueries };
}

function runBootstrapBadgeHtml(entry: PaperRunLogEntry): string {
  if (entry.bootstrapUsed !== true) return "";
  return `<span class="sfe-agents-run-boot" title="Cursor cloud agent was created this run (bootstrap prompt + first slot)">bootstrap</span>`;
}

function runWebBadgeHtml(entry: PaperRunLogEntry): string {
  const { allowWeb, webUsed, webQueries } = webResearchFromEntry(entry);
  if (!allowWeb && !webUsed) return "";
  if (webUsed && webQueries.length) {
    const title = webQueries
      .map((q) => (q.purpose ? `${q.query} — ${q.purpose}` : q.query))
      .join("\n");
    return `<span class="sfe-agents-run-web" title="${esc(title)}">web ×${webQueries.length}</span>`;
  }
  if (allowWeb && entry.webUsed === false) {
    return `<span class="sfe-agents-run-web muted" title="Allow Web on; agent reported no web research">web: none</span>`;
  }
  if (webUsed) {
    return `<span class="sfe-agents-run-web" title="Agent reported web research">web</span>`;
  }
  return "";
}

function webResearchDetailHtml(entry: PaperRunLogEntry): string {
  const { allowWeb, webUsed, webQueries } = webResearchFromEntry(entry);
  if (!allowWeb && !webUsed && !webQueries.length) return "";
  if (webUsed && webQueries.length) {
    const items = webQueries
      .map((q) => {
        const purpose = q.purpose ? `<span class="muted small"> — ${esc(q.purpose)}</span>` : "";
        return `<li><code>${esc(q.query)}</code>${purpose}</li>`;
      })
      .join("");
    return runDetailSectionHtml("Web research", `<ul>${items}</ul>`);
  }
  if (allowWeb) {
    return runDetailSectionHtml(
      "Web research",
      `<p class="muted small">${webUsed ? "Agent reported web use but listed no queries." : "Allow Web was on; agent reported no web research this run."}</p>`,
    );
  }
  return "";
}

function runLogPretty(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function isRecycleRunLogEntry(entry: PaperRunLogEntry | null | undefined): boolean {
  return Boolean(
    entry &&
      (entry.kind === "recycle" ||
        entry.slot === "recycle" ||
        entry.slot === "auto-recycle"),
  );
}

function recycleRunLogEntryFromResponse(
  result: PaperAgentStateResponse & { runLog?: PaperRunLogEntry | PaperRunLogEntry[] },
): PaperRunLogEntry | null {
  const top = result.runLog;
  if (top && typeof top === "object" && !Array.isArray(top) && typeof top.message === "string") {
    return top;
  }
  return (result.portfolio?.runLog || []).find(isRecycleRunLogEntry) ?? null;
}

function mergeRecycleRunLogEntry(
  state: PaperAgentStateResponse,
  recycleEntry: PaperRunLogEntry | null,
): PaperAgentStateResponse {
  if (!recycleEntry?.id || !state.portfolio) return state;
  const existing = state.portfolio.runLog || [];
  const without = existing.filter((entry) => entry.id !== recycleEntry.id);
  return {
    ...state,
    portfolio: {
      ...state.portfolio,
      runLog: [recycleEntry, ...without],
    },
  };
}

/** Newest-first run log rows for the Performance panel (matches API order, defensive sort). */
function runLogEntriesForDisplay(
  runLog: PaperRunLogEntry[] | undefined,
  limit = 40,
): PaperRunLogEntry[] {
  const list = Array.isArray(runLog) ? [...runLog] : [];
  list.sort((a, b) => String(b.ts || "").localeCompare(String(a.ts || "")));
  const seen = new Set<string>();
  const out: PaperRunLogEntry[] = [];
  for (const entry of list) {
    const key = entry.id || `${entry.ts}:${entry.message}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
    if (out.length >= limit) break;
  }
  return out;
}

function feedbackFromUnknown(raw: unknown): {
  questions: string[];
  promptIdeas: string[];
  notes: string;
} | null {
  if (typeof raw === "string" && raw.trim()) {
    return { questions: [], promptIdeas: [], notes: raw.trim() };
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  const asList = (value: unknown): string[] => {
    if (typeof value === "string" && value.trim()) return [value.trim()];
    if (!Array.isArray(value)) return [];
    return value.map((x) => String(x ?? "").trim()).filter(Boolean);
  };
  const questions = asList(obj.questions);
  const promptIdeas = asList(obj.promptIdeas ?? obj.prompt_ideas ?? obj.ideas);
  const notes =
    typeof obj.notes === "string"
      ? obj.notes.trim()
      : typeof obj.note === "string"
        ? obj.note.trim()
        : "";
  if (!questions.length && !promptIdeas.length && !notes) return null;
  return { questions, promptIdeas, notes };
}

function runDetailSectionHtml(title: string, body: string): string {
  return `<details class="sfe-agents-run-block">
    <summary class="sfe-agents-run-block-summary">${esc(title)}</summary>
    <div class="sfe-agents-run-block-body">${body}</div>
  </details>`;
}

function feedbackBlockHtml(feedback: {
  questions: string[];
  promptIdeas: string[];
  notes: string;
} | null): string {
  if (!feedback) return "";
  const q = feedback.questions.map((line) => `<li>${esc(line)}</li>`).join("");
  const ideas = feedback.promptIdeas.map((line) => `<li>${esc(line)}</li>`).join("");
  const body = `
    ${
      q
        ? `<p class="small muted">Questions</p><ul>${q}</ul>`
        : `<p class="muted small">No questions.</p>`
    }
    ${
      ideas
        ? `<p class="small muted">Prompt ideas</p><ul>${ideas}</ul>`
        : `<p class="muted small">No prompt ideas.</p>`
    }
    ${
      feedback.notes
        ? `<p class="small"><span class="muted">Notes:</span> ${esc(feedback.notes)}</p>`
        : ""
    }`;
  return runDetailSectionHtml("Feedback", body);
}

function recycleCursorTokenHtml(
  entry: PaperRunLogEntry | null | undefined,
  advise?: {
    ok?: boolean;
    error?: string;
    bootstrapUsed?: boolean;
    cursorAgentId?: string;
    tokensTotal?: number;
    tokensIn?: number;
    tokensOut?: number;
    bootstrapTokens?: number;
    bookSyncTokens?: number;
  },
): string {
  const total = Math.max(
    0,
    Math.round(
      Number(entry?.tokensTotal ?? entry?.claudeTokens ?? advise?.tokensTotal) || 0,
    ),
  );
  if (!total) return "";
  const bootstrapUsed = entry?.bootstrapUsed === true || advise?.bootstrapUsed === true;
  let bootstrap = Math.max(0, Math.round(Number(advise?.bootstrapTokens) || 0));
  let bookSync = Math.max(0, Math.round(Number(advise?.bookSyncTokens) || 0));
  if (!bootstrap && !bookSync) {
    bookSync = total;
  }
  const tokensIn = Math.max(0, Math.round(Number(entry?.tokensIn ?? advise?.tokensIn) || 0));
  const tokensOut = Math.max(0, Math.round(Number(entry?.tokensOut ?? advise?.tokensOut) || 0));
  const inOut =
    tokensIn || tokensOut
      ? `<span class="muted small"> (${fmtInt(tokensIn)} in · ${fmtInt(tokensOut)} out)</span>`
      : "";
  if (bootstrapUsed && bootstrap > 0 && bookSync > 0) {
    return `<p><strong>Cursor tokens:</strong> ${fmtInt(total)} total${inOut}<br><span class="muted small">Bootstrap ${fmtInt(bootstrap)} · book sync ${fmtInt(bookSync)}</span></p>`;
  }
  if (bootstrapUsed) {
    return `<p><strong>Cursor tokens:</strong> ${fmtInt(total)} total${inOut}<br><span class="muted small">Bootstrap + book sync</span></p>`;
  }
  return `<p><strong>Cursor tokens:</strong> ${fmtInt(total)} total${inOut}</p>`;
}

function recycleResultDialogHtml(
  entry: PaperRunLogEntry | null | undefined,
  advise?: {
    ok?: boolean;
    error?: string;
    bootstrapUsed?: boolean;
    cursorAgentId?: string;
    tokensTotal?: number;
    tokensIn?: number;
    tokensOut?: number;
    bootstrapTokens?: number;
    bookSyncTokens?: number;
  },
): string {
  const ok = advise?.ok !== false && entry?.ok !== false;
  const lines: string[] = [];
  if (entry?.message) lines.push(entry.message);
  else if (ok) lines.push("Cursor cloud agent recycled — paper ledger unchanged.");
  else lines.push(advise?.error || entry?.error || "Recycle failed — previous Cursor agent kept.");
  const tokenHtml = recycleCursorTokenHtml(entry, advise);
  const decisions = entry?.summary?.decisions || [];
  if (decisions.length) {
    return `<p>${esc(lines[0] || "")}</p>${tokenHtml}<ul>${decisions.map((line) => `<li>${esc(line)}</li>`).join("")}</ul><p class="muted small">A full entry was added to the Run log below.</p>`;
  }
  return `<p>${esc(lines[0] || "")}</p>${tokenHtml}<p class="muted small">A full entry was added to the Run log below.</p>`;
}

function runLogItemHtml(entry: PaperRunLogEntry, highlightId?: string | null): string {
  const highlightCls =
    highlightId && entry.id === highlightId ? " sfe-agents-run-highlight" : "";
  const runLogIdAttr = entry.id ? ` data-run-log-id="${esc(entry.id)}"` : "";
  const mdTok = Math.max(0, Math.round(Number(entry.creditsUsed) || 0));
  const claudeTok = Math.max(0, Math.round(Number(entry.claudeTokens) || 0));
  const tokensCls = mdTok + claudeTok > 0 ? "sfe-agents-run-tokens" : "sfe-agents-run-tokens muted";
  const tokensLabel =
    claudeTok > 0 ? `${fmtInt(mdTok)} MarketData · ${fmtInt(claudeTok)} Cursor` : `${fmtInt(mdTok)} MarketData`;
  const tokens = `<span class="${tokensCls}">${esc(tokensLabel)}</span>`;
  const activity = runActivityHtml(entry);
  const summary = entry.summary;
  const isTest =
    entry.dryRun === true ||
    entry.kind === "advise-test" ||
    entry.kind === "recycle" ||
    entry.kind === "test" ||
    entry.kind === "dry-run" ||
    entry.slot === "advise-test" ||
    entry.slot === "recycle" ||
    entry.slot === "auto-recycle";
  if (!summary) {
    return `
      <div class="sfe-agents-run-plain sfe-agents-run-row${isTest ? " sfe-agents-run-is-test" : ""}${highlightCls}"${runLogIdAttr}>
        <span class="muted sfe-agents-run-when">${esc(fmtTs(entry.ts))}</span>
        ${activity}
        <span class="sfe-agents-run-msg">${esc(entry.message)}</span>
        ${runBootstrapBadgeHtml(entry)}
        ${runWebBadgeHtml(entry)}
        ${tokens}
      </div>`;
  }
  const ideas = (summary.ideas || [])
    .map((line) => `<li>${esc(line)}</li>`)
    .join("");
  const decisions = (summary.decisions || [])
    .map((line) => `<li>${esc(line)}</li>`)
    .join("");
  const runFeedback =
    feedbackFromUnknown(summary.feedback) ||
    feedbackFromUnknown(
      entry.decision && typeof entry.decision === "object"
        ? (entry.decision as Record<string, unknown>).feedback
        : null,
    );
  const tokenDetail =
    claudeTok > 0
      ? `MarketData: <strong>${esc(fmtInt(mdTok))}</strong> · Cursor: <strong>${esc(fmtInt(claudeTok))}</strong>`
      : `MarketData tokens this run: <strong>${esc(fmtInt(mdTok))}</strong>`;
  const hasCursorPrompt =
    (typeof entry.system === "string" && entry.system.trim()) ||
    (typeof entry.user === "string" && entry.user.trim()) ||
    (typeof entry.responseText === "string" && entry.responseText.trim());
  const showCursorDetail = isTest || Boolean(hasCursorPrompt) || Boolean(entry.decision) || Boolean(entry.applied);
  const cursorDetail = showCursorDetail
    ? `${runDetailSectionHtml(
        isTest ? "Test / Cursor meta" : "Cursor meta",
        `<p class="small">
          Slot: <code>${esc(String(entry.slot || (isTest ? "advise-test" : "—")))}</code>
          · Model: <code>${esc(entry.model || "—")}</code>
          · Runtime: <code>${esc(entry.runtime || "—")}</code>
          · Allow Web: <code>${esc(entry.allowWeb === true ? "on" : entry.allowWeb === false ? "off" : "—")}</code>
          · Duration: ${esc(fmtInt(Number(entry.durationMs) || 0))} ms
        </p>
        <p class="small muted">
          Requested: ${esc(fmtTs(entry.requestedAt || entry.ts))}
          · Responded: ${esc(fmtTs(entry.respondedAt || entry.ts))}
        </p>
        <p class="small">
          Cursor agent: <code>${esc(entry.cursorAgentId || "—")}</code>
          · Run: <code>${esc(entry.cursorRunId || "—")}</code>
          · Log: <code>${esc(entry.logId || "—")}</code>
          ${
            entry.bootstrapLogId
              ? ` · Bootstrap log: <code>${esc(entry.bootstrapLogId)}</code>`
              : ""
          }
        </p>
        <p class="small muted">
          Tokens in/out/total: ${esc(fmtInt(Number(entry.tokensIn) || 0))}/${esc(fmtInt(Number(entry.tokensOut) || 0))}/${esc(fmtInt(Number(entry.tokensTotal) || claudeTok))}
          · cache r/w: ${esc(fmtInt(Number(entry.cacheReadTokens) || 0))}/${esc(fmtInt(Number(entry.cacheWriteTokens) || 0))}
          ${Number(entry.reasoningTokens) ? ` · reasoning: ${esc(fmtInt(Number(entry.reasoningTokens)))}` : ""}
          ${entry.bootstrapUsed === true ? " · bootstrap+slot" : ""}
        </p>
        ${entry.error ? `<p class="sfe-neg small">${esc(entry.error)}</p>` : ""}`,
      )}
      ${
        entry.bootstrapUsed === true && entry.bootstrapUser
          ? runDetailSectionHtml(
              "Bootstrap prompt",
              `<pre class="sfe-advise-log-pre">${esc(runLogPretty(entry.bootstrapUser || ""))}</pre>`,
            )
          : ""
      }
      ${
        entry.bootstrapUsed === true && entry.bootstrapResponseText
          ? runDetailSectionHtml(
              "Bootstrap reply",
              `<pre class="sfe-advise-log-pre">${esc(runLogPretty(entry.bootstrapResponseText || ""))}</pre>`,
            )
          : ""
      }
      ${runDetailSectionHtml(
        "System prompt",
        `<p class="sfe-advise-log-when">Request: ${esc(fmtTs(entry.requestedAt || entry.ts))}</p>
        <pre class="sfe-advise-log-pre">${esc(runLogPretty(entry.system || ""))}</pre>`,
      )}
      ${runDetailSectionHtml(
        entry.bootstrapUsed === true ? "User prompt (book sync)" : "User prompt",
        `<pre class="sfe-advise-log-pre">${esc(runLogPretty(entry.user || ""))}</pre>`,
      )}
      ${runDetailSectionHtml(
        entry.bootstrapUsed === true ? "Book sync reply" : "LLM reply",
        `<p class="sfe-advise-log-when">Response: ${esc(fmtTs(entry.respondedAt || entry.ts))}</p>
        <pre class="sfe-advise-log-pre">${esc(runLogPretty(entry.responseText || ""))}</pre>`,
      )}
      ${runDetailSectionHtml(
        "Decision payload",
        `<pre class="sfe-advise-log-pre">${esc(runLogPretty(entry.decision ?? null))}</pre>`,
      )}
      ${runDetailSectionHtml(
        "Applied",
        `<pre class="sfe-advise-log-pre">${esc(
          runLogPretty(
            entry.applied ??
              (isTest
                ? entry.kind === "recycle" ||
                    entry.slot === "recycle" ||
                    entry.slot === "auto-recycle"
                  ? {
                      mode: entry.slot === "auto-recycle" ? "auto-recycle" : "recycle",
                      notes: [
                        entry.slot === "auto-recycle"
                          ? "Auto-recycle — triggered by run-count or per-run token threshold."
                          : "Manual recycle — cloud conversation reset only; paper ledger untouched.",
                      ],
                    }
                  : { mode: "dry_run", notes: ["No paper fills"] }
                : null),
          ),
        )}</pre>`,
      )}`
    : "";
  return `
    <details class="sfe-agents-run-item${isTest ? " sfe-agents-run-is-test" : ""}${highlightCls}"${runLogIdAttr}${highlightCls ? " open" : ""}>
      <summary class="sfe-agents-run-row">
        <span class="muted sfe-agents-run-when">${esc(fmtTs(entry.ts))}</span>
        ${activity}
        <span class="sfe-agents-run-msg">${esc(entry.message)}</span>
        ${runBootstrapBadgeHtml(entry)}
        ${runWebBadgeHtml(entry)}
        ${tokens}
      </summary>
      <div class="sfe-agents-run-detail">
        ${runDetailSectionHtml("Analysis", `<p>${esc(summary.analysis || "—")}</p>`)}
        ${runDetailSectionHtml(
          "Ideas",
          ideas ? `<ul>${ideas}</ul>` : `<p class="muted small">No candidates considered.</p>`,
        )}
        ${runDetailSectionHtml(
          "Decisions",
          decisions ? `<ul>${decisions}</ul>` : `<p class="muted small">No open/close decisions.</p>`,
        )}
        ${feedbackBlockHtml(runFeedback)}
        ${webResearchDetailHtml(entry)}
        ${cursorDetail}
        <p class="muted small sfe-agents-run-token-line">${tokenDetail}</p>
      </div>
    </details>`;
}

function performanceHtml(
  state: Pick<PaperAgentStateResponse, "settings" | "portfolio" | "stats" | "transactions" | "kind" | "runInFlight" | "runInFlightStartedAt" | "runInFlightMode" | "agentId" | "activeId">,
  title = "Performance",
  accentHex?: string,
  registry: PaperWhyRegistry = new Map(),
  transactionsTickerFilter = "",
  transactionsFilterPanelOpen = false,
  highlightRunLogId?: string | null,
): string {
  const { settings, portfolio } = state;
  const advisor = isAdvisorAgent(state);
  const adviceAgentId = state.agentId || state.activeId || "";
  const basis = settings.initialCapital > 0 ? settings.initialCapital : 1;
  const simRoc = ((portfolio.equity - settings.initialCapital) / basis) * 100;
  const log = runLogEntriesForDisplay(portfolio.runLog)
    .map((entry) => runLogItemHtml(entry, highlightRunLogId))
    .join("");
  const allTxns =
    state.transactions?.length
      ? state.transactions
      : portfolio.fills || [];
  const txns = allTxns.filter((fill) =>
    matchesAgentsTickerFilter(fill.underlying, transactionsTickerFilter),
  );
  const txnSummary =
    transactionsTickerFilter.trim() && txns.length !== allTxns.length
      ? `${fmtInt(txns.length)} of ${fmtInt(allTxns.length)} ${advisor ? "intents" : "in private ledger"}`
      : advisor
        ? `${fmtInt(txns.length)} recorded intent${txns.length === 1 ? "" : "s"}`
        : `${fmtInt(txns.length)} in private ledger`;
  const showTxnFilter = showAgentsTickerFilterPanel(
    transactionsTickerFilter,
    transactionsFilterPanelOpen,
  );

  const statsBlock = advisor
    ? ""
    : "stats" in state && state.stats
      ? paperAgentStatsHtml(state.stats, {
          marketdata: state.marketdata,
          cursor: state.cursor || state.claude,
        })
      : `<div class="sfe-agents-kpi-row">
        <div><span class="muted small">Agent equity</span><strong>${fmtUsd(portfolio.equity)}</strong></div>
        <div><span class="muted small">Agent ROC vs start</span><strong class="${simRoc >= 0 ? "sfe-pos" : "sfe-neg"}">${fmtPct(simRoc)}</strong></div>
        <div><span class="muted small">Expected monthly ROC</span><strong>${fmtPct(settings.expectedMonthlyRocPct)}${settings.tryOverperform ? " · outperform" : ""}</strong></div>
        <div><span class="muted small">Realized P&amp;L</span><strong class="${portfolio.realizedPnl >= 0 ? "sfe-pos" : "sfe-neg"}">${fmtUsd(portfolio.realizedPnl)}</strong></div>
        <div><span class="muted small">Unrealized</span><strong class="${portfolio.unrealized >= 0 ? "sfe-pos" : "sfe-neg"}">${fmtUsd(portfolio.unrealized)}</strong></div>
      </div>`;

  return `
    <section class="sfe-agents-card sfe-agents-tinted" style="${agentAccentStyle(accentHex)}">
      <h3 class="insight-h">${esc(title)}</h3>
      ${statsBlock}
      <p class="muted small">${advisor ? "Advisor never fills the paper book. The list below is order intents (same JSON a future live executor can send) — not executed trades." : "Paper book carries across months — never touches real transactions. The ledger below is private to this agent."}</p>
      <h4 class="sfe-agents-subh">${advisor ? "Advice history" : "Transactions"} <span class="muted small">— ${esc(txnSummary)}</span></h4>
      ${agentsTickerFilterPanelHtml("transactions", transactionsTickerFilter, showTxnFilter)}
      ${transactionsTableHtml(
        txns,
        registry,
        transactionsTickerFilter,
        advisor ? "No advice recorded yet." : "No paper transactions yet.",
        advisor,
        adviceAgentId,
        allTxns,
      )}
      <h4 class="sfe-agents-subh">Run log <span class="muted small">— click a run for analysis</span> <span class="muted small sfe-agents-run-log-status" data-paper-run-log-status hidden></span></h4>
      ${runInFlightBannerHtml(state)}
      ${
        log
          ? `<div class="sfe-agents-run-log">
              <div class="sfe-agents-run-row sfe-agents-run-head muted small">
                <span>When</span><span>Activity</span><span>Run</span><span>MarketData</span>
              </div>
              ${log}
            </div>`
          : `<p class="muted small">${state.runInFlight ? "Waiting for this run to finish…" : "No runs yet."}</p>`
      }
    </section>
  `;
}

type LiveSectionOpen = {
  root: boolean;
  overview: boolean;
  options: boolean;
  stocks: boolean;
};

const DEFAULT_LIVE_SECTION_OPEN: LiveSectionOpen = {
  root: true,
  overview: true,
  options: true,
  stocks: false,
};

function loadLiveSectionOpen(): LiveSectionOpen {
  return {
    root: isCollapsibleOpen(AGENTS_COLLAPSIBLE_SCOPE, "live-root", DEFAULT_LIVE_SECTION_OPEN.root),
    overview: isCollapsibleOpen(
      AGENTS_COLLAPSIBLE_SCOPE,
      "live-overview",
      DEFAULT_LIVE_SECTION_OPEN.overview,
    ),
    options: isCollapsibleOpen(
      AGENTS_COLLAPSIBLE_SCOPE,
      "live-options",
      DEFAULT_LIVE_SECTION_OPEN.options,
    ),
    stocks: isCollapsibleOpen(
      AGENTS_COLLAPSIBLE_SCOPE,
      "live-stocks",
      DEFAULT_LIVE_SECTION_OPEN.stocks,
    ),
  };
}

function liveSectionAttrs(
  id: keyof LiveSectionOpen,
  open: boolean,
  persist: boolean,
): string {
  const collapsible = persist ? ` data-sfe-collapsible="live-${id}"` : "";
  const defaultClosed =
    persist && !DEFAULT_LIVE_SECTION_OPEN[id] ? ` data-sfe-collapsible-default="closed"` : "";
  return `data-paper-live-section="${id}"${collapsible}${defaultClosed}${open ? " open" : ""}`;
}

function runInFlightBannerHtml(
  state: Pick<PaperAgentStateResponse, "kind" | "runInFlight" | "runInFlightStartedAt" | "runInFlightMode">,
): string {
  if (!state.runInFlight) return "";
  const advisor = isAdvisorAgent(state);
  const started = state.runInFlightStartedAt ? fmtTs(state.runInFlightStartedAt) : "just now";
  const label = advisor ? "Advice run in progress" : "Agent run in progress";
  return `<p class="sfe-agents-inflight small">${esc(label)} (started ${esc(started)}). Cursor can take several minutes — especially with Allow Web — and waits if another agent is already running. The log and recommendations appear when this run finishes.</p>`;
}

function liveHtml(
  state: Pick<PaperAgentStateResponse, "portfolio" | "runtime" | "nextSlot" | "settings" | "name" | "kind" | "advice" | "runInFlight" | "runInFlightStartedAt" | "runInFlightMode" | "agentId" | "activeId">,
  title = "Live agent",
  open: LiveSectionOpen = DEFAULT_LIVE_SECTION_OPEN,
  accentHex?: string,
  persist = true,
  registry: PaperWhyRegistry = new Map(),
  optionsTickerFilter = "",
  optionsFilterPanelOpen = false,
  readOnly = false,
): string {
  const { portfolio, runtime, nextSlot, settings, name } = state;
  const advisor = isAdvisorAgent(state);
  const adviceAgentId = state.agentId || state.activeId || "";
  const recs = advisor
    ? (portfolio.fills || []).filter((fill) => String(fill.userStatus || "pending") !== "dismissed")
    : [];
  const recsHtml =
    advisor
      ? `<div class="sfe-agents-advice-recs">
          <h4 class="sfe-agents-subh">Latest recommendations</h4>
          ${
            recs.length
              ? transactionsTableHtml(
                  recs,
                  registry,
                  "",
                  "No recommendations yet. Run advice to record order intents from your live book.",
                  true,
                  adviceAgentId,
                )
              : `<p class="muted small">No recommendations yet. Run advice to record order intents from your live book.</p>`
          }
        </div>`
      : "";
  const scheduleHtml = readOnly
    ? `
          <div class="sfe-agents-next-run sfe-agents-next-run--header">
            <span class="muted small">Schedule</span>
            <strong class="sfe-agents-next-run-val">${esc(nextSlot?.label ?? "—")}</strong>
          </div>`
    : "";
  return `
    <details class="collapsible sfe-agents-card sfe-agents-live sfe-agents-tinted${advisor ? " sfe-agents-live--advisor" : ""}" ${liveSectionAttrs("root", open.root, persist)} style="${agentAccentStyle(accentHex)}">
      <summary class="sfe-agents-section-summary sfe-agents-live-summary">
        <span class="sfe-agents-live-summary-main">
          <span class="sfe-agents-live-title-row">
            <span class="insight-h">${esc(title)}${name ? ` · ${esc(name)}` : ""}</span>
            <span class="sfe-agents-badge${settings.enabled ? " sfe-agents-badge-on" : ""}">${settings.enabled ? "On" : "Off"}</span>
            ${advisor ? `<span class="sfe-agents-tab-badge">Advisor</span>` : ""}
          </span>
          ${scheduleHtml}
        </span>
      </summary>
      <div class="sfe-agents-live-body">
        ${runInFlightBannerHtml(state)}
        <details class="collapsible sfe-agents-live-sub" ${liveSectionAttrs("overview", open.overview, persist)}>
          <summary class="sfe-agents-section-summary">${advisor ? "Live portfolio" : "Overview"}</summary>
          <div class="sfe-agents-book-body">
            <div class="sfe-agents-kpi-row">
              <div><span class="muted small">Cash</span><strong>${fmtUsd(portfolio.cash)}</strong></div>
              ${advisor ? `<div><span class="muted small">Buying power</span><strong>${fmtUsd(portfolio.buyingPower ?? portfolio.cash)}</strong></div>` : ""}
              <div><span class="muted small">Equity</span><strong>${fmtUsd(portfolio.equity)}</strong></div>
              <div><span class="muted small">Reserved</span><strong>${fmtUsd(portfolio.reserved)}</strong></div>
              ${advisor ? "" : `<div><span class="muted small">Unrealized</span><strong class="${portfolio.unrealized >= 0 ? "sfe-pos" : "sfe-neg"}">${fmtUsd(portfolio.unrealized)}</strong></div>`}
            </div>
            <p class="muted small">
              ${advisor ? "Source: Robinhood snapshot + own ledger (strike avg cost). Advise-only — no orders sent." : `Last manage: ${esc(fmtTs(runtime.lastManageAt))}`}
              ${runtime.lastError ? ` · <span class="sfe-neg">${esc(runtime.lastError)}</span>` : ""}
            </p>
          </div>
        </details>
        ${recsHtml}
        ${positionsBookHtml(portfolio.positions, portfolio.shares || [], { options: open.options, stocks: open.stocks }, persist, registry, optionsTickerFilter, optionsFilterPanelOpen)}
      </div>
    </details>
  `;
}

const ADVISE_LOG_TEXT_CAP = 4000;

function clipAdviseText(value: unknown, cap = ADVISE_LOG_TEXT_CAP): string {
  const text = String(value ?? "");
  if (text.length <= cap) return text;
  return `${text.slice(0, cap)}\n\n… truncated (${text.length.toLocaleString("en-US")} chars total)`;
}

function adviseLogPretty(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string") return clipAdviseText(value);
  try {
    return clipAdviseText(JSON.stringify(value, null, 2));
  } catch {
    return clipAdviseText(String(value));
  }
}

/** Resolve request/response timestamps for a log entry (legacy rows may only have `ts`). */
function adviseLogTiming(entry: Record<string, unknown>): {
  requestedAt: string | null;
  respondedAt: string | null;
} {
  const requestedAt =
    typeof entry.requestedAt === "string" && entry.requestedAt
      ? entry.requestedAt
      : typeof entry.ts === "string" && entry.ts
        ? entry.ts
        : null;
  if (typeof entry.respondedAt === "string" && entry.respondedAt) {
    return { requestedAt, respondedAt: entry.respondedAt };
  }
  const duration = Number(entry.durationMs) || 0;
  if (requestedAt && duration > 0) {
    const startMs = Date.parse(requestedAt);
    if (Number.isFinite(startMs)) {
      return { requestedAt, respondedAt: new Date(startMs + duration).toISOString() };
    }
  }
  return { requestedAt, respondedAt: null };
}

function adviseLogEntryHtml(entry: Record<string, unknown>, index: number): string {
  const ok = entry.ok === true;
  const dry = entry.dryRun === true;
  const allowWeb = entry.allowWeb === true;
  const status = ok ? "ok" : "fail";
  const statusCls = ok ? "sfe-pos" : "sfe-neg";
  const slot = entry.slot ? String(entry.slot) : "—";
  const model = entry.model ? String(entry.model) : "—";
  const tokens = Number(entry.tokensTotal) || 0;
  const duration = Number(entry.durationMs) || 0;
  const agentId = entry.cursorAgentId ? String(entry.cursorAgentId) : "—";
  const runId = entry.cursorRunId ? String(entry.cursorRunId) : "—";
  const resumed = entry.resumed === true ? "yes" : "no";
  const phase =
    entry.phase === "bootstrap" || entry.phase === "slot"
      ? String(entry.phase)
      : "slot";
  const error = entry.error ? String(entry.error) : "";
  const { requestedAt, respondedAt } = adviseLogTiming(entry);
  const parsed = entry.parsed;
  let decisionLabel = "—";
  let actionsPreview = "";
  let adviseFeedback: ReturnType<typeof feedbackFromUnknown> = null;
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    const p = parsed as {
      pass?: unknown;
      actions?: unknown;
      picks?: unknown;
      summary?: unknown;
      analysis?: unknown;
      feedback?: unknown;
    };
    const actions = Array.isArray(p.actions) ? p.actions : [];
    const picks = Array.isArray(p.picks) ? p.picks : [];
    adviseFeedback = feedbackFromUnknown(p.feedback);
    if (p.pass === true && !actions.length && !picks.length) {
      decisionLabel = "pass";
    } else if (actions.length) {
      decisionLabel = `${actions.length} action${actions.length === 1 ? "" : "s"}`;
      actionsPreview = actions
        .slice(0, 8)
        .map((a) => {
          if (!a || typeof a !== "object") return "";
          const row = a as Record<string, unknown>;
          const type = String(row.type || "open");
          const sym = String(row.symbol || row.underlying || row.optionSymbol || "?");
          const qty = Number(row.qty ?? row.quantity) || 1;
          const side = row.side ? String(row.side) : "";
          return `${type} ${qty}× ${sym}${side ? ` ${side}` : ""}`.trim();
        })
        .filter(Boolean)
        .join(" · ");
    } else if (picks.length) {
      decisionLabel = `${picks.length} pick${picks.length === 1 ? "" : "s"}`;
    } else {
      decisionLabel = "picks";
    }
    if (adviseFeedback) {
      const n =
        adviseFeedback.questions.length +
        adviseFeedback.promptIdeas.length +
        (adviseFeedback.notes ? 1 : 0);
      decisionLabel = `${decisionLabel} · feedback×${n}`;
    }
  }
  const webInfo = webResearchFromEntry({
    allowWeb,
    webUsed: entry.webUsed,
    webQueries: entry.webQueries,
    decision: parsed && typeof parsed === "object" ? parsed : null,
  });
  const webSummary =
    webInfo.webUsed && webInfo.webQueries.length
      ? `web ×${webInfo.webQueries.length}`
      : allowWeb && (entry.webUsed === false || (parsed && typeof parsed === "object" && (parsed as { webUsed?: unknown }).webUsed === false))
        ? "web: none"
        : "";

  return `
    <details class="sfe-advise-log-entry"${index === 0 ? " open" : ""}>
      <summary>
        <span>${esc(fmtTs(requestedAt))}</span>
        <span class="${statusCls}">${esc(status)}</span>
        <span class="muted">${esc(phase)}</span>
        ${dry ? `<span class="muted">dry-run</span>` : ""}
        <span class="muted">${allowWeb ? "allowWeb" : "no-web"}</span>
        ${webSummary ? `<span class="sfe-agents-run-web">${esc(webSummary)}</span>` : ""}
        <span class="muted">slot ${esc(slot)}</span>
        <span class="muted">${esc(model)}</span>
        <span class="muted">${esc(fmtInt(tokens))} tok · ${esc(fmtInt(duration))} ms</span>
        <span class="muted">${esc(decisionLabel)}</span>
      </summary>
      <div class="sfe-advise-log-meta">
        <span>Phase: <code>${esc(phase)}</code></span>
        <span>Cursor agent: <code>${esc(agentId)}</code></span>
        <span>Run: <code>${esc(runId)}</code></span>
        <span>Resumed: ${esc(resumed)}</span>
        <span>Allow Web: <code>${esc(allowWeb ? "on" : "off")}</code></span>
        <span>In/out: ${esc(fmtInt(Number(entry.tokensIn) || 0))}/${esc(fmtInt(Number(entry.tokensOut) || 0))}</span>
      </div>
      ${
        error
          ? `<div class="sfe-advise-log-block"><h5>Error</h5><p class="sfe-neg">${esc(error)}</p></div>`
          : ""
      }
      <div class="sfe-advise-log-block">
        <h5>System prompt <span class="muted">(paper → cloud)</span></h5>
        <p class="sfe-advise-log-when">Request: ${esc(fmtTs(requestedAt))}</p>
        <pre class="sfe-advise-log-pre">${esc(adviseLogPretty(entry.system))}</pre>
      </div>
      <div class="sfe-advise-log-block">
        <h5>User prompt <span class="muted">(packed context)</span></h5>
        <pre class="sfe-advise-log-pre">${esc(adviseLogPretty(entry.user))}</pre>
      </div>
      <div class="sfe-advise-log-block">
        <h5>LLM reply</h5>
        <p class="sfe-advise-log-when">Response: ${esc(fmtTs(respondedAt))}</p>
        <pre class="sfe-advise-log-pre">${esc(adviseLogPretty(entry.responseText))}</pre>
      </div>
      ${
        actionsPreview
          ? `<div class="sfe-advise-log-block"><h5>Actions</h5><p class="small">${esc(actionsPreview)}</p></div>`
          : ""
      }
      ${feedbackBlockHtml(adviseFeedback)}
      ${
        webInfo.allowWeb || webInfo.webUsed
          ? webResearchDetailHtml({
              allowWeb: webInfo.allowWeb,
              webUsed: webInfo.webUsed,
              webQueries: webInfo.webQueries,
              message: "",
              ts: requestedAt || "",
            })
          : ""
      }
      <div class="sfe-advise-log-block">
        <h5>Parsed decision</h5>
        <pre class="sfe-advise-log-pre">${esc(adviseLogPretty(entry.parsed))}</pre>
      </div>
      <div class="sfe-advise-log-block">
        <h5>Applied</h5>
        <pre class="sfe-advise-log-pre">${esc(adviseLogPretty(entry.applied))}</pre>
      </div>
    </details>`;
}

function adviseLogBodyHtml(payload: {
  cursorConfigured: boolean;
  resumeAgentId: string | null;
  updatedAt: string | null;
  entries: Array<Record<string, unknown>>;
}): string {
  const entries = Array.isArray(payload.entries) ? payload.entries : [];
  const head = `
    <p class="muted small">
      Cursor configured: ${payload.cursorConfigured ? "yes" : "no"}
      · Resume agent id: <code>${esc(payload.resumeAgentId || "—")}</code>
      · Log updated: ${esc(fmtTs(payload.updatedAt))}
      · Showing ${esc(fmtInt(entries.length))} newest
    </p>`;
  if (!entries.length) {
    return `${head}<p class="muted sfe-advise-log-empty">No Cursor advise interactions yet for this agent. They appear after a scheduled run or an advise-test call.</p>`;
  }
  return `${head}<div class="sfe-advise-log">${entries.map((e, i) => adviseLogEntryHtml(e, i)).join("")}</div>`;
}

function previousSnapshotHtml(
  snapshot: PaperAgentSnapshot | null | undefined,
  open: boolean,
  accentHex?: string,
  registry: PaperWhyRegistry = new Map(),
): string {
  if (!snapshot) {
    return `
      <details class="collapsible sfe-agents-compare sfe-agents-tinted" data-sfe-collapsible="compare" data-sfe-collapsible-default="closed"${open ? " open" : ""} style="${agentAccentStyle(accentHex)}">
        <summary class="sfe-agents-section-summary">Previous · compare</summary>
        <p class="muted small sfe-agents-compare-empty">No previous book yet. Resetting this agent saves a full snapshot here for comparison.</p>
      </details>`;
  }
  const fakeState = {
    name: snapshot.name,
    settings: snapshot.settings,
    portfolio: snapshot.portfolio,
    runtime: snapshot.runtime,
    nextSlot: null,
  };
  return `
    <details class="collapsible sfe-agents-compare sfe-agents-tinted" data-sfe-collapsible="compare" data-sfe-collapsible-default="closed"${open ? " open" : ""} style="${agentAccentStyle(accentHex)}">
      <summary class="sfe-agents-section-summary">
        Previous · ${esc(snapshot.name)}
        <span class="muted small">saved ${esc(fmtTs(snapshot.capturedAt))} (${esc(snapshot.reason)})</span>
      </summary>
      <div class="sfe-agents-compare-body">
        ${performanceHtml(fakeState, "Previous performance", accentHex, registry)}
        ${liveHtml(fakeState, "Previous live", { ...DEFAULT_LIVE_SECTION_OPEN, stocks: false }, accentHex, false, registry)}
      </div>
    </details>`;
}

export class AgentsController {
  private host: HTMLElement | null = null;
  private state: PaperAgentStateResponse | null = null;
  private busy = false;
  private statusMessage = "";
  private statusIsError = false;
  private boundHost: HTMLElement | null = null;
  private showCompare = isCollapsibleOpen(AGENTS_COLLAPSIBLE_SCOPE, "compare", false);
  private agentId: string | null = null;
  private selectedMonth: string | null = null;
  private viewMode: "owner" | "viewer" = "owner";
  private sharedOwnerUserId: string | null = null;
  private incomingShares: PaperAgentIncomingShare[] = [];
  private ownerUserId: string | null = null;
  private liveSectionOpen: LiveSectionOpen = loadLiveSectionOpen();
  private whyRegistry: PaperWhyRegistry = new Map();
  private markPollTimer: ReturnType<typeof setInterval> | null = null;
  private quietNumbersSignature = "";
  private quietStructureSignature = "";
  private markPollBusy = false;
  private compactModeCleanup: (() => void) | null = null;
  private marksCleanup: (() => void) | null = null;
  private optionsTickerFilter = "";
  private transactionsTickerFilter = "";
  private optionsFilterPanelOpen = false;
  private transactionsFilterPanelOpen = false;
  private agentsRowMenuBound = false;
  private standaloneViewer = false;
  private highlightRunLogId: string | null = null;
  private recycleInFlight = false;
  private recycleAgentId: string | null = null;
  private runInFlight = false;
  private pendingActiveAgentId: string | null = null;
  private remoteInflightBound = false;
  private stateCache = new Map<string, PaperAgentStateResponse>();
  private selectGeneration = 0;

  /** Standalone observer page — read-only shared agent, no main app chrome. */
  mountSharedViewer(panel: HTMLElement, ownerUserId: string, agentId: string): void {
    this.standaloneViewer = true;
    this.mount(panel, { sharedView: { ownerUserId, agentId } });
  }

  mount(panel: HTMLElement, opts?: { sharedView?: { ownerUserId: string; agentId: string } }): void {
    this.host = panel;
    this.compactModeCleanup?.();
    this.compactModeCleanup = subscribeWebCompactMode(() => {
      void this.refresh();
    });
    this.marksCleanup?.();
    this.marksCleanup = subscribeLedgerMarks(() => {
      if (this.host) this.render();
    });
    void ensureLedgerMarkIndex().catch(() => null);
    if (!opts?.sharedView) this.startMarkPoll();
    if (!this.remoteInflightBound) {
      this.remoteInflightBound = true;
      window.addEventListener("sfe-paper-agent-inflight", this.onRemoteInflight);
    }
    // Mobile re-renders replace the panel element. Bind delegated handlers to
    // each new host so agent tabs remain interactive after a mobile render.
    if (this.boundHost !== panel) {
      this.boundHost = panel;
      panel.addEventListener("click", (e) => {
        const liveActions = (e.target as HTMLElement | null)?.closest<HTMLElement>(
          ".sfe-agents-live-actions",
        );
        if (liveActions && this.host?.contains(liveActions)) {
          e.preventDefault();
          e.stopPropagation();
        }
        const remove = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-paper-action='remove-slot']");
        if (remove && this.host?.contains(remove)) {
          e.preventDefault();
          e.stopPropagation();
          this.onRemoveSlot(remove.dataset.slot || "");
          return;
        }
        const chip = (e.target as HTMLElement | null)?.closest<HTMLElement>(".year-chip[data-paper-chip]");
        if (chip && this.host?.contains(chip)) {
          this.onChipClick(chip);
          return;
        }
        const t = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-paper-action]");
        if (!t || !this.host?.contains(t)) return;
        const action = t.dataset.paperAction;
        if (action === "save") void this.onSave();
        if (action === "defaults") void this.onDefaults();
        if (action === "refresh") void this.refresh();
        if (action === "add-slot") this.onAddSlot();
        if (action === "add-agent") void this.onAddAgent();
        if (action === "add-advisor") void this.onAddAdvisor();
        if (action === "clone-agent") void this.onCloneAgent();
        if (action === "rename-agent") void this.onRenameAgent();
        if (action === "edit-instructions") void this.onEditInstructions();
        if (action === "view-advise-log") void this.onViewAdviseLog();
        if (action === "show-why") {
          e.preventDefault();
          e.stopPropagation();
          this.onShowWhy(t.dataset.whyId || "");
        }
        if (action === "delete-agent") void this.onDeleteAgent();
        if (action === "select-agent") void this.onSelectAgent(t.dataset.agentId || "");
        if (action === "pick-color") void this.onPickColor(t.dataset.paperColor || "");
        if (action === "select-month") void this.onSelectMonth(t.dataset.month || "");
        if (action === "reset-options-ticker-filter") this.onResetOptionsTickerFilter();
        if (action === "reset-txn-ticker-filter") this.onResetTxnTickerFilter();
        if (action === "run-now") {
          e.preventDefault();
          e.stopPropagation();
          void this.onRunNow();
        }
        if (action === "advice-accept") {
          e.preventDefault();
          e.stopPropagation();
          void this.onAdviceStatus("accepted", t.dataset.recId || "");
        }
        if (action === "advice-dismiss") {
          e.preventDefault();
          e.stopPropagation();
          void this.onAdviceStatus("dismissed", t.dataset.recId || "");
        }
        if (action === "share-agent") void this.onShareAgent();
        if (action === "recycle-agent") void this.onRecycleAgent();
        if (action === "copy-observer-link") void this.onCopyObserverLink();
        if (action === "revoke-share") void this.onRevokeShare();
        if (action === "open-shared-agent") {
          void this.onOpenSharedAgent(t.dataset.ownerId || "", t.dataset.agentId || "");
        }
        if (action === "exit-viewer") void this.onExitViewer();
      });
      panel.addEventListener("input", (e) => {
        const target = e.target;
        if (!(target instanceof HTMLInputElement) || !this.host?.contains(target)) return;
        const agentId = this.currentAgentId();
        if (target.id === "sfeAgentsOptionsTicker") {
          this.optionsTickerFilter = sanitizeTickerFilterValue(target.value).toUpperCase();
          saveAgentsTickerFilter(agentId, "options", this.optionsTickerFilter);
          withPreservedInputFocus(() => this.render());
          return;
        }
        if (target.id === "sfeAgentsTxnTicker") {
          this.transactionsTickerFilter = sanitizeTickerFilterValue(target.value).toUpperCase();
          saveAgentsTickerFilter(agentId, "transactions", this.transactionsTickerFilter);
          withPreservedInputFocus(() => this.render());
        }
      });
      this.bindAgentsRowMenuHandlers();
    }
    if (opts?.sharedView) {
      this.clearMarkPoll();
      void this.openSharedView(opts.sharedView.ownerUserId, opts.sharedView.agentId);
    } else {
      void this.bootstrap();
    }
  }

  /** Load owner id, incoming shares, and optional observer deep link. */
  private async bootstrap(): Promise<void> {
    if (!this.host) return;
    try {
      const me = await fetchAuthMe();
      this.ownerUserId = me.user?.id || null;
    } catch {
      this.ownerUserId = null;
    }
    if (this.viewMode === "owner") {
      try {
        const payload = await fetchSharedPaperAgents();
        this.incomingShares = payload.grants || [];
      } catch {
        this.incomingShares = [];
      }
    }
    await this.refresh();
  }

  private isViewerMode(): boolean {
    return this.viewMode === "viewer";
  }

  private async openSharedView(ownerUserId: string, agentId: string): Promise<void> {
    if (!this.host || !ownerUserId || !agentId) return;
    this.viewMode = "viewer";
    this.sharedOwnerUserId = ownerUserId;
    this.agentId = agentId;
    this.selectedMonth = null;
    this.clearMarkPoll();
    await this.refresh();
  }

  private async onOpenSharedAgent(ownerUserId: string, agentId: string): Promise<void> {
    if (!ownerUserId || !agentId || this.busy) return;
    const path = paperAgentObserverLink(ownerUserId, agentId);
    window.location.href = new URL(path, window.location.origin).href;
  }

  private async onExitViewer(): Promise<void> {
    if (!this.host || this.busy) return;
    this.viewMode = "owner";
    this.sharedOwnerUserId = null;
    this.agentId = null;
    this.selectedMonth = null;
    this.startMarkPoll();
    try {
      const payload = await fetchSharedPaperAgents();
      this.incomingShares = payload.grants || [];
    } catch {
      this.incomingShares = [];
    }
    await this.refresh();
  }

  private async onShareAgent(): Promise<void> {
    if (this.isViewerMode() || this.busy) return;
    const agentId = this.currentAgentId();
    if (!agentId) return;
    const email = await promptDialog({
      title: "Share view-only access",
      message:
        "Grant read-only access to another StockForge+ user by email. They can observe this agent but cannot run it or change settings.",
      confirmLabel: "Share",
      placeholder: "observer@example.com",
    });
    if (!email) return;
    this.busy = true;
    this.setStatus("Sharing…");
    try {
      await grantPaperAgentView(agentId, email.trim());
      this.setStatus(`View-only access granted to ${email.trim()}.`);
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Share failed", true);
    } finally {
      this.busy = false;
    }
  }

  private async onRecycleAgent(): Promise<void> {
    if (!this.host || !this.state || this.busy || this.isViewerMode()) return;
    const agentId = this.currentAgentId();
    if (!agentId) return;
    if (isAdvisorAgent(this.state) && !this.state.settings.enabled) {
      this.setStatus("Turn the advisor On before recycling its cloud agent.", true);
      return;
    }
    const ok = await confirmDialog({
      title: "Recycle Cursor agent",
      message: `Recycle “${this.state.name}”? This archives the current Cursor cloud conversation, bootstraps a fresh agent, and syncs it to your current paper book. Your ledger, positions, fills, and performance history stay exactly as they are — the next scheduled run continues on the same book.`,
      confirmLabel: "Recycle",
      danger: true,
    });
    if (!ok) return;
    this.busy = true;
    this.recycleInFlight = true;
    this.recycleAgentId = agentId;
    this.syncRecycleUi();
    try {
      const result = await recyclePaperAgent(agentId);
      const recycleEntry = recycleRunLogEntryFromResponse(result);
      let next: PaperAgentStateResponse = mergeRecycleRunLogEntry(result, recycleEntry);
      try {
        const reloaded = await fetchPaperAgentState(agentId, this.selectedMonth);
        next = mergeRecycleRunLogEntry(reloaded, recycleEntry);
      } catch {
        // keep recycle response if reload fails
      }
      this.state = next;
      this.highlightRunLogId = recycleEntry?.id || next.portfolio?.runLog?.[0]?.id || null;
      this.render();
      this.focusHighlightedRunLog();
      const failed = result.advise?.ok === false || recycleEntry?.ok === false;
      const runMsg =
        recycleEntry?.message ||
        result.advise?.error ||
        (failed ? "Recycle failed — previous Cursor agent kept." : "Recycled — fresh Cursor agent bootstrapped.");
      await contentDialog({
        title: failed ? "Recycle failed" : "Agent recycled",
        bodyHtml: recycleResultDialogHtml(recycleEntry, result.advise),
        closeLabel: "OK",
        modalClass: failed ? "sfe-agents-recycle-modal sfe-agents-recycle-modal--fail" : "sfe-agents-recycle-modal",
      });
      if (this.currentAgentId() === agentId) {
        if (failed) {
          this.setStatus(runMsg, true);
        } else {
          this.setStatus("");
        }
      }
    } catch (err) {
      if (this.currentAgentId() === agentId) {
        this.setStatus(err instanceof Error ? err.message : "Recycle failed", true);
      }
    } finally {
      this.recycleInFlight = false;
      this.recycleAgentId = null;
      this.syncRecycleUi();
      this.busy = false;
    }
  }

  private async onCopyObserverLink(): Promise<void> {
    if (this.isViewerMode() || this.busy) return;
    const agentId = this.currentAgentId();
    if (!agentId) return;
    let ownerId = this.ownerUserId;
    if (!ownerId) {
      try {
        const me = await fetchAuthMe();
        ownerId = me.user?.id || null;
        this.ownerUserId = ownerId;
      } catch {
        ownerId = null;
      }
    }
    if (!ownerId) {
      this.setStatus("Sign in required to copy an observer link.", true);
      return;
    }
    const link = paperAgentObserverLink(ownerId, agentId);
    try {
      await navigator.clipboard.writeText(new URL(link, window.location.origin).href);
      this.setStatus("Observer link copied.");
    } catch {
      this.setStatus(link, false);
    }
  }

  private async onRevokeShare(): Promise<void> {
    if (this.isViewerMode() || this.busy) return;
    const agentId = this.currentAgentId();
    if (!agentId) return;
    this.busy = true;
    this.setStatus("Loading observers…");
    let grants: Awaited<ReturnType<typeof fetchOutgoingPaperAgentShares>>["grants"] = [];
    try {
      ({ grants } = await fetchOutgoingPaperAgentShares(agentId));
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Could not load observers", true);
      this.busy = false;
      return;
    }
    this.busy = false;
    if (!grants.length) {
      this.setStatus("No observers have view access for this agent.", true);
      return;
    }
    const observerList = grants.map((g) => g.viewerEmail).join(", ");
    const email = await promptDialog({
      title: "Revoke view access",
      message: `Observers with access: ${observerList}. Enter an email to revoke one, or type ALL to revoke every observer.`,
      confirmLabel: "Revoke",
      placeholder: "observer@example.com or ALL",
    });
    if (!email) return;
    const normalized = email.trim().toLowerCase();
    const revokeAll = normalized === "all" || normalized === "*";
    if (revokeAll) {
      const ok = await confirmDialog({
        title: "Revoke all observers",
        message: `Remove view-only access for all ${grants.length} observer(s) on “${this.state?.name || "this agent"}”?`,
        confirmLabel: "Revoke all",
        danger: true,
      });
      if (!ok) return;
      this.busy = true;
      this.setStatus("Revoking all observers…");
      try {
        await Promise.all(grants.map((g) => revokePaperAgentView(agentId, g.viewerUserId)));
        this.setStatus(`Revoked view access for ${grants.length} observer(s).`);
      } catch (err) {
        this.setStatus(err instanceof Error ? err.message : "Revoke all failed", true);
      } finally {
        this.busy = false;
      }
      return;
    }
    this.busy = true;
    this.setStatus("Revoking…");
    try {
      const grant = grants.find((g) => g.viewerEmail.toLowerCase() === normalized);
      if (!grant) {
        this.setStatus("No view grant found for that email.", true);
        return;
      }
      await revokePaperAgentView(agentId, grant.viewerUserId);
      this.setStatus(`Revoked view access for ${grant.viewerEmail}.`);
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Revoke failed", true);
    } finally {
      this.busy = false;
    }
  }

  private bindAgentsRowMenuHandlers(): void {
    if (this.agentsRowMenuBound) return;
    this.agentsRowMenuBound = true;
    document.addEventListener(
      "click",
      (e) => {
        const target = e.target as HTMLElement;
        const menuPortal = document.getElementById(AGENTS_ROW_MENU_ID);

        const filterItem = target.closest<HTMLElement>('[data-paper-action="agents-row-filter"]');
        if (filterItem && menuPortal?.contains(filterItem)) {
          e.preventDefault();
          e.stopPropagation();
          const scope = filterItem.dataset.agentsTableScope as AgentsTableScope | undefined;
          const ticker = filterItem.dataset.agentsTicker;
          if (scope && ticker) {
            closeAgentsRowMenu();
            this.applyAgentsRowTickerFilter(scope, ticker);
          }
          return;
        }

        const menuBtn = target.closest<HTMLElement>('[data-paper-action="agents-row-menu"]');
        if (menuBtn && this.host?.contains(menuBtn)) {
          e.preventDefault();
          e.stopPropagation();
          const host = menuBtn.closest<HTMLElement>(".sfe-agents-row-menu");
          const scope = host?.dataset.agentsTableScope as AgentsTableScope | undefined;
          const ticker = host?.dataset.agentsTicker;
          if (!scope || !ticker) return;
          if (menuPortal) closeAgentsRowMenu();
          else {
            const activeFilter =
              scope === "options" ? this.optionsTickerFilter : this.transactionsTickerFilter;
            openAgentsRowMenu(menuBtn, scope, ticker, activeFilter);
          }
          return;
        }

        if (Date.now() < agentsRowMenuSuppressCloseUntil) return;
        if (target.closest('[data-paper-action="agents-row-menu"]')) return;
        if (menuPortal?.contains(target)) return;
        closeAgentsRowMenu();
      },
      true,
    );
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeAgentsRowMenu();
    });
    window.addEventListener("scroll", () => closeAgentsRowMenu(), true);
    window.addEventListener("resize", () => closeAgentsRowMenu());
  }

  private applyAgentsRowTickerFilter(scope: AgentsTableScope, ticker: string): void {
    const normalized = sanitizeTickerFilterValue(ticker).toUpperCase();
    if (!normalized) return;
    const current =
      scope === "options" ? this.optionsTickerFilter : this.transactionsTickerFilter;
    if (sanitizeTickerFilterValue(current).toUpperCase() === normalized) {
      if (scope === "options") this.onResetOptionsTickerFilter();
      else this.onResetTxnTickerFilter();
      return;
    }
    const agentId = this.currentAgentId();
    if (scope === "options") {
      this.optionsTickerFilter = normalized;
      this.optionsFilterPanelOpen = true;
      saveAgentsTickerFilter(agentId, "options", normalized);
    } else {
      this.transactionsTickerFilter = normalized;
      this.transactionsFilterPanelOpen = true;
      saveAgentsTickerFilter(agentId, "transactions", normalized);
    }
    this.render();
    requestAnimationFrame(() => {
      this.host?.querySelector<HTMLInputElement>(`#${agentsTableScopeInputId(scope)}`)?.focus();
    });
  }

  private clearMarkPoll(): void {
    if (this.markPollTimer) {
      clearInterval(this.markPollTimer);
      this.markPollTimer = null;
    }
  }

  private startMarkPoll(): void {
    this.clearMarkPoll();
    const tickMs = this.state?.runInFlight ? 4000 : 12000;
    this.markPollTimer = setInterval(() => {
      if (!this.host || document.hidden) return;
      void this.refreshMarksQuiet();
    }, tickMs);
  }

  private syncMarkPollInterval(): void {
    if (!this.markPollTimer) return;
    this.startMarkPoll();
  }

  private async refreshMarksQuiet(): Promise<void> {
    if (!this.host || this.isViewerMode() || this.markPollBusy) return;
    if (this.host.querySelector(".sfe-agents-switch-overlay")) return;
    this.markPollBusy = true;
    try {
      const prevStructure = this.quietStructureSignature;
      const agentId = this.currentAgentId();
      const next = await fetchPaperAgentState(agentId, this.selectedMonth, {
        refreshMarks: false,
      });
      if (!this.host || this.currentAgentId() !== agentId) return;
      this.cacheAgentState(next);
      const structureSig = agentsStructureSignature(next);
      const numbersSig = agentsLiveNumbersSignature(next);
      if (structureSig === prevStructure && numbersSig === this.quietNumbersSignature) {
        return;
      }

      const prevState = this.state;
      this.state = next;
      if (next.stats?.selectedMonth) {
        this.selectedMonth = next.stats.selectedMonth;
      }
      this.quietStructureSignature = structureSig;
      this.quietNumbersSignature = numbersSig;
      this.syncMarkPollInterval();

      const structureChanged = structureSig !== prevStructure;
      if (!structureChanged && prevState && patchAgentsLiveQuiet(this.host, next)) {
        this.syncRunButton(Boolean(next.runInFlight));
        return;
      }

      if (structureChanged && isUserActiveInPanel(this.host)) {
        if (prevState && patchAgentsLiveQuiet(this.host, next)) {
          this.syncRunButton(Boolean(next.runInFlight));
        }
        return;
      }

      this.render();
      this.syncRunButton(Boolean(next.runInFlight));
    } catch {
      /* background mark refresh is best-effort */
    } finally {
      this.markPollBusy = false;
    }
  }

  private currentAgentId(): string | null {
    return this.agentId || this.state?.agentId || this.state?.activeId || null;
  }

  private onChipClick(chip: HTMLElement): void {
    const group = chip.closest<HTMLElement>("[data-paper-chip-group]");
    if (!group || !this.host) return;
    const mode = group.dataset.paperChipMode || "single";
    if (mode === "multi") {
      chip.classList.toggle("active");
      chip.setAttribute("aria-pressed", chip.classList.contains("active") ? "true" : "false");
      return;
    }
    if (mode === "toggle") {
      chip.classList.toggle("active");
      chip.setAttribute("aria-pressed", chip.classList.contains("active") ? "true" : "false");
      const groupName = group.dataset.paperChipGroup;
      if (groupName === "top-n-discretion") this.syncTopNInput();
      if (groupName === "tp-discretion") this.syncTakeProfitInput();
      if (groupName === "risky") this.syncRiskyCallsInput();
      return;
    }
    // single-select
    for (const btn of group.querySelectorAll<HTMLElement>(".year-chip")) {
      const on = btn === chip;
      btn.classList.toggle("active", on);
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    }
    if (group.dataset.paperChipGroup === "schedule-mode") {
      this.syncSchedulePanels();
    }
    if (group.dataset.paperChipGroup === "stop-mode") {
      this.syncStopLossPct();
    }
    if (group.dataset.paperChipGroup === "enabled") {
      void this.onEnabledToggle();
    }
  }

  /** Persist On/Off from the agent header without a full Settings save. */
  private async onEnabledToggle(): Promise<void> {
    if (!this.host || !this.state || this.busy || this.isViewerMode()) return;
    const enabled = activeChipValues(this.host, "enabled").includes("on");
    if (enabled === this.state.settings.enabled) return;
    this.busy = true;
    this.setStatus(enabled ? "Turning on…" : "Turning off…");
    try {
      await savePaperAgentSettings({ enabled }, this.currentAgentId());
      const id = this.currentAgentId();
      this.state = {
        ...this.state,
        settings: { ...this.state.settings, enabled },
        agents: (this.state.agents || []).map((a) =>
          a.id === id ? { ...a, enabled } : a,
        ),
      };
      const activeTab =
        this.host.querySelector<HTMLElement>(
          `.sfe-agents-tab[data-agent-id="${CSS.escape(id || "")}"]`,
        ) || this.host.querySelector<HTMLElement>(".sfe-agents-tab.active");
      if (activeTab) {
        activeTab.classList.toggle("is-on", enabled);
        const name = this.state.name || "Agent";
        activeTab.title = `${name}${enabled ? " · on" : " · off"}`;
      }
      const badge = this.host.querySelector(".sfe-agents-badge");
      if (badge) {
        badge.textContent = enabled ? "On" : "Off";
        badge.classList.toggle("sfe-agents-badge-on", enabled);
      }
      const advisorOff = !enabled && isAdvisorAgent(this.state);
      if (advisorOff && this.state.runtime) {
        this.state = {
          ...this.state,
          runtime: { ...this.state.runtime, cursorSdkAgentId: null },
        };
      }
      this.syncRunButton(this.isCurrentAgentRunning());
      this.syncRecycleButton(this.isRecyclingCurrentAgent());
      this.setStatus(
        enabled
          ? isAdvisorAgent(this.state)
            ? "Advisor on."
            : "Agent on."
          : advisorOff
            ? "Advisor off. Cloud agent removed until you turn it back on."
            : "Agent off.",
      );
      notifyAgentsStatusChanged();
    } catch (err) {
      // Revert chips to last saved state.
      for (const btn of this.host.querySelectorAll<HTMLElement>(
        '[data-paper-chip-group="enabled"] .year-chip',
      )) {
        const on = (btn.dataset.paperChip === "on") === this.state.settings.enabled;
        btn.classList.toggle("active", on);
        btn.setAttribute("aria-pressed", on ? "true" : "false");
      }
      this.setStatus(err instanceof Error ? err.message : "Failed to update agent", true);
    } finally {
      this.busy = false;
    }
  }

  private syncSchedulePanels(): void {
    if (!this.host) return;
    const chip = activeChipValues(this.host, "schedule-mode")[0];
    const mode =
      chip === "interval" || chip === "manual" || chip === "slots" ? chip : "slots";
    for (const panel of this.host.querySelectorAll<HTMLElement>("[data-paper-schedule-panel]")) {
      panel.hidden = panel.dataset.paperSchedulePanel !== mode;
    }
  }

  private syncStopLossPct(): void {
    if (!this.host) return;
    const mode = activeChipValues(this.host, "stop-mode")[0] || "stop";
    const pct = this.host.querySelector<HTMLElement>("[data-paper-sl-pct]");
    if (pct) pct.hidden = mode === "keep";
  }

  private setFieldDisabled(input: HTMLInputElement | null, disabled: boolean): void {
    if (!input) return;
    input.disabled = disabled;
    input.setAttribute("aria-disabled", disabled ? "true" : "false");
    input.closest(".filter-affix, .sfe-agents-field-with-check")?.classList.toggle(
      "is-disabled",
      disabled,
    );
  }

  private syncTopNInput(): void {
    if (!this.host) return;
    const discretion = activeChipValues(this.host, "top-n-discretion").includes("on");
    this.setFieldDisabled(
      this.host.querySelector<HTMLInputElement>("[data-paper-top-n-input]"),
      discretion,
    );
  }

  private syncTakeProfitInput(): void {
    if (!this.host) return;
    const discretion = activeChipValues(this.host, "tp-discretion").includes("on");
    this.setFieldDisabled(
      this.host.querySelector<HTMLInputElement>("[data-paper-tp-input]"),
      discretion,
    );
  }

  private syncRiskyCallsInput(): void {
    if (!this.host) return;
    const allowed = activeChipValues(this.host, "risky").includes("on");
    this.setFieldDisabled(
      this.host.querySelector<HTMLInputElement>("[data-paper-risky-min-pop-input]"),
      !allowed,
    );
  }

  private syncDiscretionFields(): void {
    this.syncTopNInput();
    this.syncTakeProfitInput();
    this.syncRiskyCallsInput();
    this.syncStopLossPct();
  }

  private onAddSlot(): void {
    if (!this.host) return;
    const input = this.host.querySelector<HTMLInputElement>("[data-paper-slot-add]");
    const hostChips = this.host.querySelector<HTMLElement>("[data-paper-slots-host]");
    const time = normalizeTimeInput(input?.value ?? "");
    if (!time || !hostChips) {
      this.setStatus("Enter a valid HH:MM time to add.", true);
      return;
    }
    const existing = hostChips.querySelector<HTMLElement>(`[data-paper-chip="${time}"]`);
    if (existing) {
      existing.classList.add("active");
      existing.setAttribute("aria-pressed", "true");
      this.setStatus(`Timeslot ${time} already listed — turned on.`);
      return;
    }
    hostChips.insertAdjacentHTML("beforeend", slotChipBtn(time, true));
    this.setStatus(`Added timeslot ${time} PT.`);
  }

  private onRemoveSlot(slot: string): void {
    if (!this.host) return;
    const time = normalizeTimeInput(slot);
    if (!time) return;
    const hostChips = this.host.querySelector<HTMLElement>("[data-paper-slots-host]");
    const chip = hostChips?.querySelector<HTMLElement>(`[data-paper-chip="${time}"]`);
    if (!chip) return;
    const remaining = hostChips?.querySelectorAll(".year-chip[data-paper-chip]").length ?? 0;
    if (remaining <= 1) {
      this.setStatus("Keep at least one timeslot, or switch to interval mode.", true);
      return;
    }
    chip.remove();
    this.setStatus(`Removed timeslot ${time}.`);
  }

  private onResetOptionsTickerFilter(): void {
    this.optionsTickerFilter = "";
    this.optionsFilterPanelOpen = false;
    saveAgentsTickerFilter(this.currentAgentId(), "options", "");
    this.render();
  }

  private onResetTxnTickerFilter(): void {
    this.transactionsTickerFilter = "";
    this.transactionsFilterPanelOpen = false;
    saveAgentsTickerFilter(this.currentAgentId(), "transactions", "");
    this.render();
  }

  private syncTickerFiltersForAgent(): void {
    const agentId = this.currentAgentId();
    this.optionsTickerFilter = loadAgentsTickerFilter(agentId, "options");
    this.transactionsTickerFilter = loadAgentsTickerFilter(agentId, "transactions");
  }

  private async refresh(): Promise<void> {
    if (!this.host) return;
    if (!this.state) {
      this.host.innerHTML = `
        <div class="sfe-agents-panel panel">
          <div class="sfe-agents-header">
            <div>
              <h2 class="insight-h">Agentic</h2>
              <p class="muted small sfe-agents-lead">Loading paper agent…</p>
            </div>
            <div class="sfe-agents-header-actions"></div>
          </div>
        </div>`;
    }
    try {
      if (this.isViewerMode() && this.sharedOwnerUserId) {
        const agentId = this.agentId || this.currentAgentId();
        if (!agentId) throw new Error("Shared agent id missing");
        this.state = await fetchSharedPaperAgentState(
          this.sharedOwnerUserId,
          agentId,
          this.selectedMonth,
        );
      } else {
        this.state = await fetchPaperAgentState(this.currentAgentId(), this.selectedMonth);
      }
      this.agentId = this.state.agentId || this.state.activeId;
      if (this.state.stats?.selectedMonth) {
        this.selectedMonth = this.state.stats.selectedMonth;
      }
      this.syncTickerFiltersForAgent();
      this.cacheAgentState(this.state);
      this.quietStructureSignature = agentsStructureSignature(this.state);
      this.quietNumbersSignature = agentsLiveNumbersSignature(this.state);
      this.syncMarkPollInterval();
      this.render();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load paper agent";
      this.host.innerHTML = `
        <div class="sfe-agents-panel panel">
          <div class="sfe-agents-header">
            <div>
              <h2 class="insight-h">Agentic</h2>
              <p class="muted small sfe-neg">${esc(message)}</p>
            </div>
            <div class="sfe-agents-header-actions">
              <button type="button" class="small" data-paper-action="refresh">Retry</button>
            </div>
          </div>
        </div>`;
    }
  }

  private async onSelectMonth(month: string): Promise<void> {
    if (!month || !/^\d{4}-\d{2}$/.test(month) || month === this.selectedMonth) return;
    this.selectedMonth = month;
    await this.refresh();
  }

  private setStatus(message: string, isError = false): void {
    this.statusMessage = message;
    this.statusIsError = isError;
    this.applyStatus();
  }

  private applyStatus(): void {
    if (!this.host) return;
    const recyclingCurrent = this.isRecyclingCurrentAgent();
    for (const el of this.host.querySelectorAll<HTMLElement>("[data-paper-recycle-status]")) {
      el.textContent = recyclingCurrent ? "Recycling Cursor agent… (can take a few minutes)" : "";
      el.hidden = !recyclingCurrent;
    }
    for (const el of this.host.querySelectorAll<HTMLElement>(
      "[data-paper-status], [data-paper-run-log-status]",
    )) {
      el.textContent = this.statusMessage;
      el.classList.toggle("sfe-neg", this.statusIsError);
      if (el.hasAttribute("data-paper-run-log-status")) {
        el.hidden = !this.statusMessage;
      }
    }
  }

  private isRecyclingCurrentAgent(): boolean {
    return (
      this.recycleInFlight &&
      this.recycleAgentId != null &&
      this.currentAgentId() === this.recycleAgentId
    );
  }

  private syncRecycleUi(): void {
    this.syncRecycleButton(this.isRecyclingCurrentAgent());
    this.applyStatus();
  }

  private focusHighlightedRunLog(): void {
    if (!this.host || !this.highlightRunLogId) return;
    const el = this.host.querySelector<HTMLElement>(
      `[data-run-log-id="${CSS.escape(this.highlightRunLogId)}"]`,
    );
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    window.setTimeout(() => {
      this.highlightRunLogId = null;
    }, 8000);
  }

  private syncRecycleButton(recycling: boolean): void {
    const btn = this.host?.querySelector<HTMLButtonElement>("[data-paper-action='recycle-agent']");
    if (!btn) return;
    const advisor = isAdvisorAgent(this.state);
    const enabled = Boolean(this.state?.settings?.enabled);
    btn.disabled = recycling || this.isViewerMode() || (advisor && !enabled);
    btn.textContent = recycling ? "Recycling…" : "Recycle";
  }

  private isCurrentAgentRunning(): boolean {
    return this.runInFlight || Boolean(this.state?.runInFlight);
  }

  private syncRunButton(running = this.isCurrentAgentRunning()): void {
    const btn = this.host?.querySelector<HTMLButtonElement>("[data-paper-action='run-now']");
    if (!btn) return;
    const advisor = isAdvisorAgent(this.state);
    const enabled = Boolean(this.state?.settings?.enabled);
    btn.disabled = running || !enabled;
    btn.textContent = running
      ? advisor
        ? "Advising…"
        : "Running…"
      : advisor
        ? "Run advice"
        : "Run";
  }

  private onRemoteInflight = (event: Event): void => {
    const detail = (event as CustomEvent<{ agentId?: string | null; mode?: string | null }>).detail;
    const agentId = String(detail?.agentId || "");
    const current = this.currentAgentId();
    if (agentId && agentId === current) {
      this.syncRunButton(true);
      if (isAdvisorAgent(this.state)) {
        this.setStatus("Running advice… (also shown on Puts/Calls/Options)");
      }
      return;
    }
    if (!agentId) void this.refreshMarksQuiet();
  };

  private async flushPendingActiveAgent(): Promise<void> {
    const id = this.pendingActiveAgentId;
    if (!id || this.runInFlight) return;
    this.pendingActiveAgentId = null;
    try {
      await setActivePaperAgent(id);
    } catch {
      this.pendingActiveAgentId = id;
    }
  }

  private cacheAgentState(state: PaperAgentStateResponse | null | undefined): void {
    const id = state?.agentId || state?.activeId;
    if (!id || !state) return;
    this.stateCache.set(id, state);
  }

  private applySelectedAgentState(
    state: PaperAgentStateResponse,
    agentId: string,
    opts?: { resetMonth?: boolean },
  ): void {
    this.state = state;
    this.agentId = state.agentId || agentId;
    this.selectedMonth = opts?.resetMonth ? null : (state.stats?.selectedMonth ?? this.selectedMonth);
    this.syncTickerFiltersForAgent();
    this.cacheAgentState(state);
    this.quietStructureSignature = agentsStructureSignature(state);
    this.quietNumbersSignature = agentsLiveNumbersSignature(state);
  }

  private markAgentTabSelected(agentId: string): void {
    if (!this.host) return;
    const tabs = this.host.querySelectorAll<HTMLElement>(".sfe-agents-tab[data-agent-id]");
    let selected: HTMLElement | null = null;
    for (const tab of tabs) {
      const on = tab.dataset.agentId === agentId;
      tab.classList.toggle("active", on);
      if (on) selected = tab;
    }
    const block = this.host.querySelector<HTMLElement>(".sfe-agents-tabs-block");
    const style = selected?.getAttribute("style");
    if (block && style) block.setAttribute("style", style);
  }

  private setAgentSwitchLoading(loading: boolean): void {
    const wrap = this.host?.querySelector<HTMLElement>(".sfe-agents-active-wrap");
    if (!wrap) return;
    wrap.classList.toggle("is-switching", loading);
    wrap.setAttribute("aria-busy", loading ? "true" : "false");
    let overlay = wrap.querySelector<HTMLElement>(".sfe-agents-switch-overlay");
    if (loading) {
      if (overlay) return;
      overlay = document.createElement("div");
      overlay.className = "sfe-agents-switch-overlay";
      overlay.setAttribute("role", "status");
      overlay.innerHTML =
        '<div class="sfe-agents-switch-spinner" aria-hidden="true"></div><span>Loading agent…</span>';
      wrap.appendChild(overlay);
      return;
    }
    overlay?.remove();
  }

  private persistActiveAgent(agentId: string): void {
    if (this.runInFlight) {
      this.pendingActiveAgentId = agentId;
      return;
    }
    this.pendingActiveAgentId = null;
    void setActivePaperAgent(agentId).catch(() => {
      this.pendingActiveAgentId = agentId;
    });
  }

  private async refreshSelectedAgent(generation: number, agentId: string): Promise<void> {
    try {
      const next = await fetchPaperAgentState(agentId, null, { refreshMarks: false });
      if (generation !== this.selectGeneration || this.currentAgentId() !== agentId) return;
      this.applySelectedAgentState(next, agentId);
      this.render();
    } catch {
      /* cached tab already painted — background refresh is best-effort */
    }
  }

  private async onSelectAgent(agentId: string): Promise<void> {
    if (!agentId || this.isViewerMode() || agentId === this.currentAgentId()) return;
    const previousId = this.currentAgentId();
    const generation = ++this.selectGeneration;
    this.cacheAgentState(this.state);
    this.markAgentTabSelected(agentId);

    const cached = this.stateCache.get(agentId);
    if (cached) {
      this.setAgentSwitchLoading(false);
      this.applySelectedAgentState(cached, agentId, { resetMonth: true });
      this.render();
      this.setStatus("");
      this.persistActiveAgent(agentId);
      void this.refreshSelectedAgent(generation, agentId);
      return;
    }

    this.setAgentSwitchLoading(true);
    this.setStatus("Switching…");
    try {
      // Marks stay current via the 12s poll — skip the sequential quote refresh
      // that made tab switches wait on MarketData.
      const next = await fetchPaperAgentState(agentId, null, { refreshMarks: false });
      if (generation !== this.selectGeneration) return;
      this.applySelectedAgentState(next, agentId);
      this.render();
      this.setStatus("");
      this.persistActiveAgent(agentId);
    } catch (err) {
      if (generation !== this.selectGeneration) return;
      if (previousId) this.markAgentTabSelected(previousId);
      this.setStatus(err instanceof Error ? err.message : "Switch failed", true);
    } finally {
      if (generation === this.selectGeneration) this.setAgentSwitchLoading(false);
    }
  }

  private async onAddAgent(): Promise<void> {
    if (!this.host || this.busy || this.isViewerMode()) return;
    const suggested = `Agent ${(this.state?.agents?.length ?? 0) + 1}`;
    const name = await promptDialog({
      title: "New agent",
      message: "Name for the new agent. Starts with default settings, a fresh book, and Off.",
      defaultValue: suggested,
      confirmLabel: "Create",
      placeholder: "Agent name",
    });
    if (!name) return;
    this.busy = true;
    this.setStatus("Creating…");
    try {
      this.state = await createPaperAgent({ name });
      this.agentId = this.state.agentId;
      this.selectedMonth = null;
      this.render();
      this.setStatus(`Created “${this.state.name}” (defaults, fresh book, Off).`);
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Create failed", true);
    } finally {
      this.busy = false;
    }
  }

  private async onAddAdvisor(): Promise<void> {
    if (!this.host || this.busy || this.isViewerMode()) return;
    const suggested = `Advisor ${(this.state?.agents?.filter((a) => a.kind === "advisor").length ?? 0) + 1}`;
    const name = await promptDialog({
      title: "New advisor",
      message: "Name for the advisor. Same settings as paper agents, but it reads your live portfolio and only records recommendations — it does not trade.",
      defaultValue: suggested,
      confirmLabel: "Create",
      placeholder: "Advisor name",
    });
    if (!name) return;
    this.busy = true;
    this.setStatus("Creating advisor…");
    try {
      this.state = await createPaperAgent({ name, kind: "advisor" });
      this.agentId = this.state.agentId;
      this.selectedMonth = null;
      this.render();
      this.setStatus(`Created “${this.state.name}” (advisor, live portfolio, Off).`);
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Create failed", true);
    } finally {
      this.busy = false;
    }
  }

  private async onCloneAgent(): Promise<void> {
    if (!this.host || this.busy || this.isViewerMode()) return;
    const sourceId = this.currentAgentId();
    if (!sourceId) return;
    const sourceName = this.state?.name || "Agent";
    const suggested = `${sourceName} copy`;
    const name = await promptDialog({
      title: "Clone agent",
      message: isAdvisorAgent(this.state)
        ? `Copy all settings and instructions from “${sourceName}”. The advisor still reads your live portfolio and starts Off.`
        : `Copy all settings and instructions from “${sourceName}”. The book starts fresh and Off.`,
      defaultValue: suggested,
      confirmLabel: "Clone",
      placeholder: "Agent name",
    });
    if (!name) return;
    const advisor = isAdvisorAgent(this.state);
    let initialCapital: number | undefined;
    if (!advisor) {
      const capitalRaw = await promptDialog({
        title: "Initial capital",
        message: `Starting cash for “${name}”. Strategy settings are copied; the paper book starts fresh at this balance.`,
        defaultValue: fmtMoneyInput(10_000, 2),
        confirmLabel: "Clone",
        placeholder: "10,000.00",
      });
      if (capitalRaw == null) return;
      initialCapital = parseMoneyInput(capitalRaw, 10_000);
    }
    this.busy = true;
    this.setStatus("Cloning…");
    try {
      this.state = await createPaperAgent({
        name,
        clone: true,
        cloneFromId: sourceId,
        settings: advisor ? undefined : { initialCapital },
      });
      this.agentId = this.state.agentId;
      this.selectedMonth = null;
      this.render();
      this.setStatus(
        advisor
          ? `Cloned “${sourceName}” → “${this.state.name}” (advisor settings + instructions, Off).`
          : `Cloned “${sourceName}” → “${this.state.name}” (settings + instructions, fresh book, Off).`,
      );
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Clone failed", true);
    } finally {
      this.busy = false;
    }
  }

  private async onEditInstructions(): Promise<void> {
    if (!this.host || !this.state || this.busy || this.isViewerMode()) return;
    const id = this.currentAgentId();
    if (!id) return;
    const name = this.state.name || "agent";
    const allowWeb = this.state.settings.allowWeb === true;
    this.busy = true;
    this.setStatus("Loading prompt templates…");
    let templates: PromptTemplate[] = [];
    let legend = getPlaceholderLegend();
    try {
      const res = await fetchPromptTemplates();
      templates = res.templates || [];
      if (res.placeholderLegend) legend = res.placeholderLegend;
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Failed to load templates", true);
      this.busy = false;
      return;
    }
    this.busy = false;
    this.setStatus("");

    const selectedTemplateId =
      this.state.settings.promptTemplateId || BUILTIN_PROMPT_TEMPLATE_ID;
    const seedBody = getDefaultSlotPromptTemplateBody();
    const seedBootstrapBody = getDefaultBootstrapPromptBody();
    const result = await instructionsDialog({
      title: `Instructions · ${name}`,
      message:
        "Edit the Bootstrap prompt (standing rules, sent once on create) and the Slot template (live book/candidates every advise). Additional free-text goes into <USER_INSTRUCTIONS>. Allow Web swaps <WEB_RESEARCH_RULES> / <FINAL_REPLY_RULE> at runtime.",
      defaultValue: this.state.settings.instructions || "",
      confirmLabel: "Save",
      cancelLabel: "Cancel",
      maxLength: 8000,
      rows: 8,
      fieldLabel: "Additional instructions",
      placeholder:
        "e.g. Prefer high-PoP puts on focus names; avoid chasing weekly calls; size down when monthly ROC is ahead of target…",
      templates,
      selectedTemplateId,
      seedBody,
      seedBootstrapBody,
      placeholderLegend: legend,
      templateLabel: "Current template",
      templateHint: `Bootstrap once on create · short slot every advise. Allow Web is currently ${allowWeb ? "ON" : "OFF"}.`,
      bootstrapLabel: "Bootstrap prompt",
      bootstrapHint:
        "Standing Wheel playbook + JSON schema. Sent once when a Cursor agent is created (skipped when Keep agent resumes). Reset built-in to pick up the latest default.",
      slotLabel: "Slot template",
      slotHint:
        "Short timeslot pack with live placeholders (<BOOK_*>, <CANDIDATES>, <WANT>, <FEEDBACK>, …). Sent after bootstrap on create, or alone on resume.",
      instructionsHint: "Per-agent notes injected into <USER_INSTRUCTIONS> (saved with this agent).",
    });
    if (result == null) return;

    const prevInstructions = (this.state.settings.instructions || "").trim();
    const prevTemplateId =
      this.state.settings.promptTemplateId || BUILTIN_PROMPT_TEMPLATE_ID;
    const origById = new Map(templates.map((t) => [t.id, t]));

    this.busy = true;
    this.setStatus("Saving templates & instructions…");
    try {
      for (const delId of result.deletedIds) {
        await deletePromptTemplate(delId);
      }

      let selectedId = result.promptTemplateId;
      for (const tmpl of result.templates) {
        if (tmpl.id.startsWith("local-")) {
          const created = await createPromptTemplate({
            name: tmpl.name,
            body: tmpl.body,
            bootstrapBody: tmpl.bootstrapBody,
          });
          if (selectedId === tmpl.id) selectedId = created.template.id;
          continue;
        }
        if (
          tmpl.id === BUILTIN_PROMPT_TEMPLATE_ID &&
          result.resetBuiltin
        ) {
          await resetPromptTemplate(BUILTIN_PROMPT_TEMPLATE_ID);
          if (
            tmpl.name !== "Wheel (default)" ||
            tmpl.body !== seedBody ||
            tmpl.bootstrapBody !== seedBootstrapBody
          ) {
            await updatePromptTemplate(tmpl.id, {
              name: tmpl.name,
              body: tmpl.body,
              bootstrapBody: tmpl.bootstrapBody,
            });
          }
          continue;
        }
        const prev = origById.get(tmpl.id);
        if (
          !prev ||
          prev.name !== tmpl.name ||
          prev.body !== tmpl.body ||
          prev.bootstrapBody !== tmpl.bootstrapBody
        ) {
          await updatePromptTemplate(tmpl.id, {
            name: tmpl.name,
            body: tmpl.body,
            bootstrapBody: tmpl.bootstrapBody,
          });
        }
      }

      await savePaperAgentSettings(
        {
          instructions: result.instructions,
          promptTemplateId: selectedId,
        },
        id,
      );

      this.state = await fetchPaperAgentState(id, this.selectedMonth);
      this.agentId = this.state.agentId || id;
      this.render();
      const unchanged =
        result.instructions === prevInstructions && selectedId === prevTemplateId;
      this.setStatus(
        unchanged
          ? "Prompt templates saved."
          : "Instructions & prompt template saved.",
      );
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Save failed", true);
    } finally {
      this.busy = false;
    }
  }

  private onShowWhy(id: string): void {
    const entry = this.whyRegistry.get(id);
    if (!entry) return;
    if (entry.adviceRec) {
      const status = String(entry.adviceRec.userStatus || "pending").toLowerCase();
      if (status === "pending") {
        openAdviceDetailModal([entry.adviceRec], {
          onUpdated: () => {
            void this.refresh();
          },
        });
        return;
      }
    }
    void contentDialog({
      title: entry.title,
      bodyHtml: formatAgentJustificationHtml(entry.body),
      closeLabel: "Close",
      modalClass: "sfe-agents-why-modal",
    });
  }

  private async onViewAdviseLog(): Promise<void> {
    if (!this.host || !this.state || this.busy || this.isViewerMode()) return;
    const id = this.currentAgentId();
    if (!id) return;
    const name = this.state.name || "agent";
    this.busy = true;
    this.setStatus("Loading log…");
    try {
      const log = await fetchPaperAdviseLog(id, 40);
      await contentDialog({
        title: `Log · ${name}`,
        message:
          "Linode ↔ Cursor cloud advise interactions for this agent (newest first). Expand a row for system/user prompts (above), LLM reply, parsed JSON, and applied notes.",
        bodyHtml: adviseLogBodyHtml(log),
        closeLabel: "Close",
        modalClass: "sfe-advise-log-modal",
      });
      this.setStatus(
        log.entries?.length
          ? `Log: ${log.entries.length} interaction(s).`
          : "Log is empty for this agent.",
      );
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Failed to load log", true);
    } finally {
      this.busy = false;
    }
  }

  private async onRenameAgent(): Promise<void> {
    if (!this.host || !this.state || this.busy || this.isViewerMode()) return;
    const id = this.currentAgentId();
    if (!id) return;
    const next = await promptDialog({
      title: "Rename agent",
      defaultValue: this.state.name,
      confirmLabel: "Rename",
      placeholder: "Agent name",
    });
    if (!next || next === this.state.name) return;
    this.busy = true;
    this.setStatus("Renaming…");
    try {
      this.state = await renamePaperAgent(id, next);
      this.agentId = this.state.agentId || id;
      this.render();
      this.setStatus("Renamed.");
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Rename failed", true);
    } finally {
      this.busy = false;
    }
  }

  private async onDeleteAgent(): Promise<void> {
    if (!this.host || !this.state || this.busy || this.isViewerMode()) return;
    const id = this.currentAgentId();
    if (!id) return;
    if ((this.state.agents?.length ?? 0) <= 1) {
      this.setStatus("Keep at least one agent.", true);
      return;
    }
    const ok = await confirmDialog({
      title: "Delete agent",
      message: `Delete “${this.state.name}”? Its book and settings will be removed. Other agents stay.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    this.busy = true;
    this.setStatus("Deleting…");
    try {
      this.state = await deletePaperAgent(id);
      this.stateCache.delete(id);
      this.cacheAgentState(this.state);
      this.agentId = this.state.agentId || this.state.activeId;
      this.render();
      this.setStatus(`Deleted. Now viewing ${this.state.name}.`);
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Delete failed", true);
    } finally {
      this.busy = false;
    }
  }

  private async onPickColor(color: string): Promise<void> {
    if (!this.host || !this.state || !color || this.busy) return;
    if (!PAPER_AGENT_COLOR_OPTIONS.some((c) => c.id === color)) return;
    if (color === this.state.color) return;
    const prevColor = String(this.state.color || "slate");
    const hex = PAPER_AGENT_COLOR_OPTIONS.find((c) => c.id === color)?.hex;
    if (!hex) return;

    for (const btn of this.host.querySelectorAll<HTMLElement>(
      "[data-paper-color-group] .sfe-agents-color-swatch",
    )) {
      const on = btn.dataset.paperColor === color;
      btn.classList.toggle("active", on);
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    }
    this.state = { ...this.state, color: color as PaperAgentColor, colorHex: hex };
    for (const el of this.host.querySelectorAll<HTMLElement>(
      ".sfe-agents-tinted, .sfe-agents-tab.active",
    )) {
      el.style.setProperty("--sfe-agent-accent", hex);
    }

    this.busy = true;
    try {
      await savePaperAgentSettings({ color }, this.currentAgentId());
      const id = this.currentAgentId();
      this.state = {
        ...this.state,
        agents: (this.state.agents || []).map((a) =>
          a.id === id ? { ...a, color, colorHex: hex } : a,
        ),
      };
      this.setStatus(`Color → ${color}.`);
    } catch (err) {
      const prevHex = PAPER_AGENT_COLOR_OPTIONS.find((c) => c.id === prevColor)?.hex || "#64748b";
      for (const btn of this.host.querySelectorAll<HTMLElement>(
        "[data-paper-color-group] .sfe-agents-color-swatch",
      )) {
        const on = btn.dataset.paperColor === prevColor;
        btn.classList.toggle("active", on);
        btn.setAttribute("aria-pressed", on ? "true" : "false");
      }
      this.state = {
        ...this.state,
        color: prevColor as PaperAgentColor,
        colorHex: prevHex,
      };
      for (const el of this.host.querySelectorAll<HTMLElement>(
        ".sfe-agents-tinted, .sfe-agents-tab.active",
      )) {
        el.style.setProperty("--sfe-agent-accent", prevHex);
      }
      this.setStatus(err instanceof Error ? err.message : "Failed to update color", true);
    } finally {
      this.busy = false;
    }
  }

  private async onRunNow(): Promise<void> {
    if (!this.host || this.isCurrentAgentRunning() || this.recycleInFlight || this.isViewerMode()) return;
    const advisor = isAdvisorAgent(this.state);
    if (!this.state?.settings?.enabled) {
      this.setStatus(
        advisor ? "Turn the advisor On before running advice." : "Turn the agent On before running.",
        true,
      );
      return;
    }
    const runAgentId = this.currentAgentId();
    if (!runAgentId) return;
    this.runInFlight = true;
    this.syncRunButton(true);
    this.setStatus(
      advisor
        ? "Running advice… (Cursor advise can take a few minutes; no orders sent)"
        : "Running… (Cursor advise can take a few minutes)",
    );
    try {
      const body = (await (advisor
        ? runAgentAdvice(runAgentId)
        : runPaperAgentNow({ forceOpen: true }, runAgentId))) as PaperAgentStateResponse & {
        result?: {
          skipped?: boolean;
          reason?: string;
          actions?: { opened?: number; closed?: number; marked?: number; held?: number };
        };
      };
      if (this.currentAgentId() === runAgentId) {
        this.state = body;
        this.agentId = body.agentId || body.activeId || this.agentId;
        this.render();
        const result = body.result;
        if (result?.skipped) {
          const reason = result.reason || "skipped";
          const label =
            reason === "disabled"
              ? "Agent is Off."
              : reason === "market_closed"
                ? "Market closed."
                : reason === "nothing_to_do"
                  ? "Nothing to do."
                  : reason === "already_running"
                    ? "Already running."
                    : `Skipped (${reason}).`;
          this.setStatus(label, reason === "disabled");
        } else {
          const a = result?.actions;
          const parts = [
            a?.opened ? `${a.opened} open${a.opened === 1 ? "" : "s"}` : null,
            a?.closed ? `${a.closed} close${a.closed === 1 ? "" : "s"}` : null,
            a?.held ? `${a.held} held` : null,
          ].filter(Boolean);
          this.setStatus(
            parts.length
              ? `${advisor ? "Advice recorded" : "Manual run finished"} — ${parts.join(", ")}.`
              : advisor
                ? "Advice run finished — no intents."
                : "Manual run finished.",
          );
        }
      } else if (this.state && body.agents) {
        this.state.agents = body.agents;
        this.render();
        this.setStatus(`Run finished for ${body.name || "agent"}.`);
      }
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Run failed", true);
      this.syncRunButton(false);
      if (this.currentAgentId() === runAgentId) {
        try {
          // Pick up a failure/skip row the server may have written before the error response.
          this.state = await fetchPaperAgentState(runAgentId, this.selectedMonth);
          this.render();
          this.applyStatus();
        } catch {
          /* keep error status */
        }
      }
    } finally {
      this.runInFlight = false;
      this.syncRunButton();
      await this.flushPendingActiveAgent();
    }
  }

  private async onAdviceStatus(
    status: "accepted" | "dismissed",
    recId: string,
  ): Promise<void> {
    if (!this.host || !this.state || this.busy || this.isViewerMode() || !recId) return;
    if (!isAdvisorAgent(this.state)) return;
    const agentId = this.currentAgentId();
    if (!agentId) return;
    const accepting = status === "accepted";
    const note = await textAreaDialog({
      title: accepting ? "Accept recommendation" : "Dismiss recommendation",
      message: "The advisor sees this comment with your decision on its next cycle.",
      fieldLabel: "Comment (optional)",
      placeholder: accepting ? "Why this one works" : "Why you passed on this one",
      rows: 3,
      maxLength: 500,
      allowEmpty: true,
      confirmLabel: accepting ? "Accept" : "Dismiss",
    });
    if (note === null) return;
    this.busy = true;
    this.setStatus(accepting ? "Accepting recommendation…" : "Dismissing recommendation…");
    try {
      this.state = await updateAdviceStatus(status, [recId], agentId, note.trim() || undefined);
      this.render();
      this.setStatus(status === "accepted" ? "Recommendation accepted." : "Recommendation dismissed.");
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Update failed", true);
    } finally {
      this.busy = false;
    }
  }

  private async onSave(): Promise<void> {
    if (!this.host || !this.state || this.busy || this.isViewerMode()) return;
    this.busy = true;
    this.setStatus("Saving…");
    try {
      const settings = readSettingsFromForm(this.host, this.state.settings);
      const color = settings.color || this.state.color || "slate";
      await savePaperAgentSettings(
        { ...settings, name: this.state.name, color },
        this.currentAgentId(),
      );
      this.state = await fetchPaperAgentState(this.currentAgentId(), this.selectedMonth);
      this.agentId = this.state.agentId;
      if (this.state.stats?.selectedMonth) this.selectedMonth = this.state.stats.selectedMonth;
      this.render();
      this.setStatus("Settings saved.");
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Save failed", true);
    } finally {
      this.busy = false;
    }
  }

  private async onDefaults(): Promise<void> {
    if (!this.host || !this.state || this.busy || this.isViewerMode()) return;
    this.busy = true;
    this.setStatus("Resetting form…");
    try {
      const defaults = await fetchPaperAgentDefaults();
      this.state = {
        ...this.state,
        settings: {
          ...defaults,
          enabled: this.state.settings.enabled,
          focusTickers: loadSavedTickers(),
          instructions: this.state.settings.instructions || "",
          strategy: this.state.settings.strategy || "discretion",
          claudeModel: this.state.settings.claudeModel || "auto",
        },
      };
      this.render();
      this.setStatus("Defaults loaded into the form — click Save to persist.");
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : "Reset failed", true);
    } finally {
      this.busy = false;
    }
  }

  private captureLiveSectionOpen(): void {
    if (!this.host) return;
    const read = (key: keyof LiveSectionOpen, fallback: boolean): boolean => {
      const el = this.host?.querySelector<details>(
        `.sfe-agents-active-wrap [data-sfe-collapsible="live-${key}"]`,
      );
      return el ? el.open : fallback;
    };
    this.liveSectionOpen = {
      root: read("root", this.liveSectionOpen.root),
      overview: read("overview", this.liveSectionOpen.overview),
      options: read("options", this.liveSectionOpen.options),
      stocks: read("stocks", this.liveSectionOpen.stocks),
    };
    setCollapsibleOpen(AGENTS_COLLAPSIBLE_SCOPE, "live-root", this.liveSectionOpen.root);
    setCollapsibleOpen(AGENTS_COLLAPSIBLE_SCOPE, "live-overview", this.liveSectionOpen.overview);
    setCollapsibleOpen(AGENTS_COLLAPSIBLE_SCOPE, "live-options", this.liveSectionOpen.options);
    setCollapsibleOpen(AGENTS_COLLAPSIBLE_SCOPE, "live-stocks", this.liveSectionOpen.stocks);

    const settings = this.host.querySelector<details>(".sfe-agents-settings[data-sfe-collapsible='settings']");
    if (settings) setCollapsibleOpen(AGENTS_COLLAPSIBLE_SCOPE, "settings", settings.open);

    const performance = this.host.querySelector<details>(
      ".sfe-agents-stats details.sfe-total-performance[data-sfe-collapsible='performance']",
    );
    if (performance) setCollapsibleOpen(AGENTS_COLLAPSIBLE_SCOPE, "performance", performance.open);

    const compare = this.host.querySelector<details>(".sfe-agents-compare[data-sfe-collapsible='compare']");
    if (compare) {
      this.showCompare = compare.open;
      setCollapsibleOpen(AGENTS_COLLAPSIBLE_SCOPE, "compare", compare.open);
    }
  }

  private render(): void {
    if (!this.host || !this.state) return;
    withPreservedPanelView(this.host, () => this.paintPanel());
  }

  private paintPanel(): void {
    if (!this.host || !this.state) return;
    this.captureLiveSectionOpen();
    this.liveSectionOpen = loadLiveSectionOpen();
    this.showCompare = isCollapsibleOpen(AGENTS_COLLAPSIBLE_SCOPE, "compare", false);
    const state = this.state;
    agentsPaperMark = isAdvisorAgent(state) ? null : paperAgentLedgerMark(state.color);
    const readOnly = this.isViewerMode();
    const activeId = state.agentId || state.activeId;
    const whyRegistry: PaperWhyRegistry = new Map();

    if (readOnly) {
      this.host.innerHTML = `
      <div class="sfe-agents-panel panel sfe-agents-panel-viewer">
        <div class="sfe-agents-sticky-chrome">
          ${viewerBannerHtml(state, this.standaloneViewer)}
        </div>
        <div class="sfe-agents-tinted sfe-agents-active-wrap" style="${agentAccentStyle(state.colorHex)}">
          ${settingsFormHtml(state.settings, true, isAdvisorAgent(state))}
          ${liveHtml(state, isAdvisorAgent(state) ? "Live portfolio" : "Live agent", this.liveSectionOpen, state.colorHex, true, whyRegistry, this.optionsTickerFilter, this.optionsFilterPanelOpen, true)}
          ${performanceHtml(state, "Performance", state.colorHex, whyRegistry, this.transactionsTickerFilter, this.transactionsFilterPanelOpen)}
        </div>
      </div>
    `;
      this.whyRegistry = whyRegistry;
      bindDetailsCollapsiblePersistence(this.host, AGENTS_COLLAPSIBLE_SCOPE);
      syncAgentsColumnPickers(this.host, () => this.render());
      guardTickerFilterInputs(this.host);
      this.applyStatus();
      return;
    }

    this.host.innerHTML = `
      <div class="sfe-agents-panel panel">
        <div class="sfe-agents-sticky-chrome">
          <div class="sfe-agents-header">
            <div>
              <h2 class="insight-h">Agentic</h2>
              <p class="muted small sfe-agents-lead">
                Screener-driven paper trading and live-portfolio advisors. Paper agents keep a private book; advisors recommend against your real positions without placing orders.
              </p>
            </div>
            <div class="sfe-agents-header-actions">
              <div class="sfe-agents-column-picker-host" data-agents-column-picker></div>
              <button type="button" class="small ghost" data-paper-action="clone-agent" title="Clone the selected agent — copies settings and instructions; fresh book, Off">Clone</button>
              <button type="button" class="small" data-paper-action="add-agent" title="Add a new paper agent with default settings and a fresh book">Add Agent</button>
              <button type="button" class="small sfe-agents-add-advisor" data-paper-action="add-advisor" title="Add an advisor that recommends against your live portfolio without trading">Add Advisor</button>
              <button type="button" class="small ghost" data-paper-action="delete-agent" title="Delete the selected agent">Delete</button>
            </div>
          </div>
          ${sharedWithMeHtml(this.incomingShares, null, null)}
          ${tokenCardsHtml(state)}
          ${agentTabsHtml(
            state.agents || [],
            activeId,
            state.name,
            state.settings.enabled,
            String(state.color || "slate"),
            Boolean(state.settings.instructions?.trim()),
            state.colorHex,
            state.nextSlot,
          )}
        </div>
        <div class="sfe-agents-tinted sfe-agents-active-wrap" style="${agentAccentStyle(state.colorHex)}">
          ${settingsFormHtml(state.settings, false, isAdvisorAgent(state))}
          ${liveHtml(state, isAdvisorAgent(state) ? "Live portfolio" : "Live agent", this.liveSectionOpen, state.colorHex, true, whyRegistry, this.optionsTickerFilter, this.optionsFilterPanelOpen, false)}
          ${performanceHtml(state, "Performance", state.colorHex, whyRegistry, this.transactionsTickerFilter, this.transactionsFilterPanelOpen, this.highlightRunLogId)}
          ${isAdvisorAgent(state) ? "" : previousSnapshotHtml(state.lastSnapshot, this.showCompare, state.colorHex, whyRegistry)}
        </div>
      </div>
    `;
    this.whyRegistry = whyRegistry;
    this.syncDiscretionFields();
    bindDetailsCollapsiblePersistence(this.host, AGENTS_COLLAPSIBLE_SCOPE);
    syncAgentsColumnPickers(this.host, () => this.render());
    guardTickerFilterInputs(this.host);
    this.applyStatus();
    this.syncRunButton();
    this.syncRecycleUi();
  }
}
